import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux"; 
import { SITENAMEALIAS, WEBSERVICE, isTestMode } from "../utils/init";
import { showToast } from "../utils/library";
import { IpGeo, getAuthToken, getAuthUuid } from "../utils/service";
import { BrowserProvider, Contract, parseEther, parseUnits } from "ethers";
import PaybitoCaptcha from "../components/PaybitoCaptcha";
import PaybitoCaptchaExchange from "../components/PaybitoCaptchaExchange";
import PabtekPaymentPopup from "../components/PabtekPaymentPopup";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { QRCodeSVG } from "qrcode.react"; 
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import axios from "axios";
import { getCountries } from "../utils/kycService";
import { Country, State, City } from "country-state-city";
import PaybitoTitan from "../assets/images/PaybitoTitan.png";


// ─── Constants ────────────────────────────────────────────────────────────────
const WEBSERVICE_LOCAL = WEBSERVICE;
const PAYBITO_ACCOUNTS = "https://accounts.paybito.com/api";
const paynow_payment = WEBSERVICE_LOCAL + "/payment/paynow_payment";
const hosted_catalog_payment =
  WEBSERVICE_LOCAL + "/payment/hosted_catalog_payment";
const email_bill_payment = WEBSERVICE_LOCAL + "/payment/email_bill_payment";

// ─── KurvPay ──────────────────────────────────────────────────────────────────
const KURV_COLLECT_JS_URL =
  "https://emscorporate.transactiongateway.com/token/Collect.js";

// ─── HMS ──────────────────────────────────────────────────────────────────────
const HMS_COLLECT_JS_URL =
  "https://hms.transactiongateway.com/token/Collect.js";
const HMS_TOKENIZATION_KEY = "NY2qtU-dCygr9-a3BnPT-qq9Gy7";


// ─── NMI ──────────────────────────────────────────────────────────────────────
const NMI_COLLECT_JS_URL =
  "https://secure.nmi.com/token/Collect.js";

const MOONPAY_SUPPORTED_CURRENCIES = [
  "UNI",
  "SOL",
  "XLM",
  "USDC",
  "GRT",
  "TRX",
  "BAT",
  "LTC",
  "LINK",
  "XRP",
  "ETH",
  "DAI",
  "TON",
];
const MOONPAY_BUY_CURRENCIES = ["BTC", "ETH", "XRP", "USDC", "USDT"];
const CONVERT_PAGE_CURRENCIES = ["BCH", "LTC", "HCX"];

const NETWORK_CONFIG = {
  TESTNET: {
    chainId: "0xaa36a7",
    name: "Sepolia Testnet",
    rpc: "https://sepolia.infura.io/v3/97fdd02f0ce84fe1945f812f88c14a9b",
    explorer: "https://sepolia.etherscan.io/tx/",
  },
  MAINNET: {
    chainId: "0x1",
    name: "Ethereum Mainnet",
    rpc: "https://rpc.ankr.com/eth",
    explorer: "https://etherscan.io/tx/",
  },
};

const ERC20_ABI = [
  "function transfer(address to, uint256 value) returns (bool)",
];

// ─── Testnet addresses (Sepolia) ──────────────────────────────────────────────
const TESTNET_ETH_MERCHANT_ADDRESS =
  "0xc29c1E36be93bA472AfdAfDEEB2f4Dc306577528";
const TESTNET_ERC20_USDT_ADDRESS = "0x6C19381C4b968e7960067F846d9aC35b85B7ce71";
const TESTNET_ERC20_USDC_ADDRESS = "0x1c7d4b196cb0c7b01d743fbc6116a902379c7238";
const TESTNET_TRON_MERCHANT_ADDRESS = "TWanaFPf5PwnpvEVn29BPtPk14kjNBS7q4";
const TESTNET_TRC20_USDT_ADDRESS = "TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf";

// ─── Mainnet addresses ────────────────────────────────────────────────────────
const MAINNET_ETH_MERCHANT_ADDRESS =
  "0xc29c1E36be93bA472AfdAfDEEB2f4Dc306577528";
const MAINNET_ERC20_USDT_ADDRESS = "0xdAC17F958D2ee523a2206206994597C13D831ec7"; // USDT ERC20
const MAINNET_ERC20_USDC_ADDRESS = "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48"; // USDC ERC20
const MAINNET_TRON_MERCHANT_ADDRESS = "TWanaFPf5PwnpvEVn29BPtPk14kjNBS7q4";
const MAINNET_TRC20_USDT_ADDRESS = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t"; // USDT TRC20

// ─── Token decimals ───────────────────────────────────────────────────────────
const ERC20_DECIMALS = {
  USDT: 6,
  USDC: 6,
};

// ─── Crypto master list ───────────────────────────────────────────────────────
const CRYPTO_MASTER_LIST = [
  {
    currency_code: "BTC",
    currency_name: "Bitcoin",
    currency_id: 2,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/BTC.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "ETH",
    currency_name: "Ethereum",
    currency_id: 3,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/ETH.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "BCH",
    currency_name: "Bitcoin Cash",
    currency_id: 4,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/BCH.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "LTC",
    currency_name: "Litecoin",
    currency_id: 7,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/LTC.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "HCX",
    currency_name: "Hashcash Coin",
    currency_id: 8,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/HCX.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "XRP",
    currency_name: "Ripple",
    currency_id: 14,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/XRP.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "USDT",
    currency_name: "Tether",
    currency_id: 16,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/USDT.png",
    network: ["ERC", "TRC"],
  },
  {
    currency_code: "DOGE",
    currency_name: "Dogecoin",
    currency_id: 124,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/DOGE.png",
    network: ["NATIVE"],
  },
  {
    currency_code: "USDC",
    currency_name: "USDC",
    currency_id: 148,
    logo: "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/USDC.png",
    network: ["ERC"],
  },
];

let stripePromise = null;

const getStripePromise = (publishableKey) => {
  if (!publishableKey) return null;
  if (!stripePromise) {
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};
// Stripe Payment Form Component
const StripePaymentForm = ({
  clientSecret,
  redirectSuccessUrl,
  redirectFailureUrl,
  paymentIntentId,
  loginEmail,
  orderGrandTotal,
  grandTotal,
  detectedCurrency,
  buildRedirectUrl,
  redirectAdvanceOptions,
  onError,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isElementReady, setIsElementReady] = useState(false);
  const [isPayButtonReady, setIsPayButtonReady] = useState(false);
  const [expressAvailable, setExpressAvailable] = useState(false);

  // Check if Stripe is ready
  useEffect(() => {
    if (stripe && elements) {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, 250); // small delay to ensure the embedded element is fully initialized

      setTimeout(() => {
        setIsElementReady(true);
      }, 20000);

      return () => clearTimeout(timer);
    }
  }, [stripe, elements]);

  const handleExpressConfirm = async () => {
    if (!stripe || !elements) return;
    setProcessing(true);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url:
            redirectSuccessUrl ||
            `${window.location.origin}/merchant/checkout/success`,
          payment_method_data: {
            billing_details: {
              email: loginEmail || "",
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        let errorMessage = error.message;
        if (error.type === "card_error") {
          errorMessage = `Card error: ${error.message}`;
        } else if (error.type === "validation_error") {
          errorMessage = `Validation error: ${error.message}`;
        }
        onError?.(errorMessage);
        return;
      }

      const redirectUrl = buildRedirectUrl(
        redirectSuccessUrl ||
          `${window.location.origin}/merchant/checkout/success`,
        paymentIntent.id || paymentIntentId,
        "success",
        loginEmail || "",
        String(orderGrandTotal || grandTotal || "0"),
        detectedCurrency.code || "USD",
        redirectAdvanceOptions
      );
      window.location.href = redirectUrl;
    } catch (err) {
      console.error("[Stripe] Express checkout error:", err);
      onError?.(err.message || "Something went wrong");
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url:
            redirectSuccessUrl ||
            `${window.location.origin}/merchant/checkout/success`,
          payment_method_data: {
            billing_details: {
              email: loginEmail || "",
            },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        console.error("[Stripe] Payment error:", error);

        let errorMessage = error.message;
        if (error.type === "card_error") {
          errorMessage = `Card error: ${error.message}`;
        } else if (error.type === "validation_error") {
          errorMessage = `Validation error: ${error.message}`;
        }

        onError?.(errorMessage);
        return;
      }

      // Payment successful - redirect
      const redirectUrl = buildRedirectUrl(
        redirectSuccessUrl ||
          `${window.location.origin}/merchant/checkout/success`,
        paymentIntent.id || paymentIntentId,
        "success",
        loginEmail || "",
        String(orderGrandTotal || grandTotal || "0"),
        detectedCurrency.code || "USD",
        redirectAdvanceOptions
      );

      window.location.href = redirectUrl;
    } catch (err) {
      console.error("[Stripe] Unexpected error:", err);
      onError?.(err.message || "Something went wrong");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="co-stripe-sub-info">
        <p>
          <i
            className="fa fa-info-circle"
            style={{ color: "#635bff", marginRight: 6 }}
          />
          Your payment details are handled directly by PayBito Apex — we never store
          them.
        </p>
      </div>

      {/* ── Express Checkout: Apple Pay / Google Pay ── */}
      <ExpressCheckoutElement
        onConfirm={handleExpressConfirm}
        onReady={({ availablePaymentMethods }) => {
          console.log("[Stripe] Express checkout available methods:", availablePaymentMethods);
          setExpressAvailable(
            !!(availablePaymentMethods?.applePay || availablePaymentMethods?.googlePay)
          );
        }}
        options={{
          buttonHeight: 44,
          buttonTheme: { applePay: "black", googlePay: "black" },
          paymentMethods: { applePay: "auto", googlePay: "auto" },
        }}
      />

      {expressAvailable && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            margin: "12px 0",
          }}
        >
          <hr
            style={{
              flex: 1,
              border: "none",
              borderTop: "1px solid var(--co-gray-200)",
            }}
          />
          <span
            style={{
              padding: "0 10px",
              fontSize: 12,
              color: "var(--co-gray-400)",
              whiteSpace: "nowrap",
            }}
          >
            or pay with card
          </span>
          <hr
            style={{
              flex: 1,
              border: "none",
              borderTop: "1px solid var(--co-gray-200)",
            }}
          />
        </div>
      )}

      {!isReady || !isElementReady ? (
        // Loading state
        <div
          style={{
            border: "1.5px solid var(--co-gray-200)",
            borderRadius: 10,
            padding: "32px 16px",
            marginBottom: 16,
            background: "#f9fafb",
            textAlign: "center",
          }}
        >
          <span
            className="co-spinner"
            style={{ borderTopColor: "#635bff", width: 24, height: 24 }}
          />
          <p
            style={{ marginTop: 12, fontSize: 13, color: "var(--co-gray-500)" }}
          >
            Loading secure payment form...
          </p>
        </div>
      ) : (
        // Payment form
        <>
          <div
            style={{
              border: "1.5px solid var(--co-gray-200)",
              borderRadius: 10,
              padding: "16px",
              marginBottom: 16,
              background: "#fff",
              position: "relative",
            }}
          >
            {!isElementReady && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f9fafb",
                  zIndex: 1,
                }}
              >
                <span className="co-spinner" />
              </div>
            )}

            <PaymentElement
              options={{
                wallets: {
                  applePay: "auto",
                  googlePay: "auto",
                },
              }}
              onReady={() => {
                console.log("Stripe element ready");
                setIsElementReady(true);
                setIsPayButtonReady(true);
              }}
            />
          </div>

          {isElementReady && (
            <button
              type="submit"
              className="co-btn co-btn-stripe"
              disabled={processing || !isPayButtonReady}
              style={{ width: "100%" }}
            >
              {processing ? (
                <>
                  <span className="co-spinner" />
                  &nbsp;Processing Payment...
                </>
              ) : (
                <>
                  <i className="fa fa-lock" />
                  &nbsp;Pay {detectedCurrency.symbol}
                  {(orderGrandTotal || grandTotal || 0).toFixed(2)} with Card
                </>
              )}
            </button>
          )}
        </>
      )}
    </form>
  );
};

// Stripe Wrapper Component
const StripePaymentWrapper = (props) => {
  const { stripePublishableKey, stripePaymentIntentSecret } = props;

  if (!stripePublishableKey || !stripePaymentIntentSecret) {
    return (
      <div style={{ textAlign: "center", padding: "16px 0" }}>
        <span className="co-spinner" style={{ borderTopColor: "#635bff" }} />
        <span
          style={{ marginLeft: 8, fontSize: 13, color: "var(--co-gray-500)" }}
        >
          Loading Stripe...
        </span>
      </div>
    );
  }

  const stripePromise = getStripePromise(stripePublishableKey);

  const options = {
    clientSecret: stripePaymentIntentSecret,
    appearance: {
      theme: "stripe",
      variables: {
        colorPrimary: "#635bff",
        colorBackground: "#ffffff",
        colorText: "#374151",
        colorDanger: "#ef4444",
        fontFamily: "'Roboto', system-ui, sans-serif",
        borderRadius: "8px",
      },
    },
  };

  return (
    <Elements
      key={stripePaymentIntentSecret}
      stripe={stripePromise}
      options={options}
    >
      <StripePaymentForm {...props} />
    </Elements>
  );
};

// Patch customElements.define once so that CollectJS sub-SDKs (e.g. Apple Pay)
// can never throw "already been used with this registry" when configure() is
// called more than once during a session.
if (typeof window !== "undefined" && !window.__customElementsPatchedForKurv) {
  const _origDefine = customElements.define.bind(customElements);
  customElements.define = function (name, constructor, options) {
    if (customElements.get(name)) return; // already registered — skip silently
    _origDefine(name, constructor, options);
  };
  window.__customElementsPatchedForKurv = true;
}

// Inject a persistent style that prevents body-level CollectJS iframes from
// ever blocking pointer events on page elements (they communicate via postMessage
// so pointer-events:none does not break tokenization).
if (typeof window !== "undefined" && !window.__nmiPtrFixApplied) {
  const s = document.createElement("style");
  s.setAttribute("data-nmi", "ptr-fix");
  s.textContent = `
    body > iframe[src*="nmi.com"],
    body > iframe[src*="secure.nmi"],
    body > [id*="CollectJS"],
    body > [class*="CollectJS"] {
      pointer-events: none !important;
    }
  `;
  document.head.appendChild(s);
  window.__nmiPtrFixApplied = true;
}

// Module-level flags so CollectJS script is injected only ONCE per page load.
let _collectJsLoaded = false;
let _collectJsLoading = false;
const _collectJsCallbacks = [];
let _kurvCollectJsRef = null; // KurvPay's own CollectJS instance reference

const _loadCollectJs = (effectiveKey, onReady, onFail) => {
  // Use gateway-specific flag + ref so HMS/NMI loading their own CollectJS
  // (which overwrites window.CollectJS) does not fool this loader into thinking
  // KurvPay's script is already ready.
  if (_collectJsLoaded && _kurvCollectJsRef) {
    window.CollectJS = _kurvCollectJsRef; // restore KurvPay's instance
    onReady();
    return;
  }
  _collectJsCallbacks.push({ onReady, onFail });
  if (_collectJsLoading) return; // script already injected, wait for onload
  _collectJsLoading = true;

  const script = document.createElement("script");
  script.src = KURV_COLLECT_JS_URL;
  script.setAttribute("data-tokenization-key", effectiveKey);
  script.onload = () => {
    _kurvCollectJsRef = window.CollectJS; // save before another gateway can overwrite
    _collectJsLoading = false;
    _collectJsLoaded = true;
    _collectJsCallbacks.forEach((cb) => cb.onReady());
    _collectJsCallbacks.length = 0;
  };
  script.onerror = () => {
    _collectJsLoading = false;
    script.remove();
    _collectJsCallbacks.forEach((cb) => cb.onFail());
    _collectJsCallbacks.length = 0;
  };
  document.body.appendChild(script);
};

// Module-level flags so HMS CollectJS script is injected only ONCE per page load.
let _hmsCollectJsLoaded = false;
let _hmsCollectJsLoading = false;
const _hmsCollectJsCallbacks = [];
let _hmsCollectJsRef = null; // HMS's own CollectJS instance reference

const _loadHmsCollectJs = (effectiveKey, onReady, onFail) => {
  // Use gateway-specific flag + ref so KurvPay/NMI loading their own CollectJS
  // does not cause HMS to reuse the wrong gateway's instance.
  if (_hmsCollectJsLoaded && _hmsCollectJsRef) {
    window.CollectJS = _hmsCollectJsRef; // restore HMS's instance
    onReady();
    return;
  }
  _hmsCollectJsCallbacks.push({ onReady, onFail });
  if (_hmsCollectJsLoading) return;
  _hmsCollectJsLoading = true;

  const script = document.createElement("script");
  script.src = HMS_COLLECT_JS_URL;
  script.setAttribute("data-tokenization-key", effectiveKey);
  script.onload = () => {
    _hmsCollectJsRef = window.CollectJS; // save before another gateway can overwrite
    _hmsCollectJsLoading = false;
    _hmsCollectJsLoaded = true;
    _hmsCollectJsCallbacks.forEach((cb) => cb.onReady());
    _hmsCollectJsCallbacks.length = 0;
  };
  script.onerror = () => {
    _hmsCollectJsLoading = false;
    script.remove();
    _hmsCollectJsCallbacks.forEach((cb) => cb.onFail());
    _hmsCollectJsCallbacks.length = 0;
  };
  document.body.appendChild(script);
};

// Module-level state for the NMI CollectJS script.
// We keep a reference to the <script> tag so we can fully tear it down when
// NMIPaymentForm unmounts — this forces a clean re-execution on the next open,
// which is the only reliable way to reset CollectJS's internal postMessage
// channel that gets orphaned after configure() is called on stale DOM nodes.
let _nmiCollectJsLoaded = false;
let _nmiCollectJsLoading = false;
const _nmiCollectJsCallbacks = [];
let _nmiCollectJsScript = null;
let _nmiCollectJsRef = null; // NMI's own CollectJS instance reference

const _destroyNmiCollectJs = () => {
  if (_nmiCollectJsScript) {
    _nmiCollectJsScript.remove();
    _nmiCollectJsScript = null;
  }
  _nmiCollectJsRef = null;
  _nmiCollectJsLoaded = false;
  _nmiCollectJsLoading = false;
  _nmiCollectJsCallbacks.length = 0;
  try { delete window.CollectJS; } catch (_) { window.CollectJS = undefined; }
};

const _loadNmiCollectJs = (effectiveKey, onReady, onFail) => {
  // Use gateway-specific flag + ref so KurvPay/HMS loading their own CollectJS
  // does not cause NMI to reuse the wrong gateway's instance.
  if (_nmiCollectJsLoaded && _nmiCollectJsRef) {
    window.CollectJS = _nmiCollectJsRef; // restore NMI's instance
    onReady();
    return;
  }
  _nmiCollectJsCallbacks.push({ onReady, onFail });
  if (_nmiCollectJsLoading) return;
  _nmiCollectJsLoading = true;

  const script = document.createElement("script");
  _nmiCollectJsScript = script;
  script.src = NMI_COLLECT_JS_URL;
  script.setAttribute("data-tokenization-key", effectiveKey);
  script.onload = () => {
    _nmiCollectJsRef = window.CollectJS; // save before another gateway can overwrite
    _nmiCollectJsLoading = false;
    _nmiCollectJsLoaded = true;
    _nmiCollectJsCallbacks.forEach((cb) => cb.onReady());
    _nmiCollectJsCallbacks.length = 0;
  };
  script.onerror = () => {
    _nmiCollectJsLoading = false;
    _nmiCollectJsScript = null;
    script.remove();
    _nmiCollectJsCallbacks.forEach((cb) => cb.onFail());
    _nmiCollectJsCallbacks.length = 0;
  };
  document.body.appendChild(script);
};

// ─── KurvPay Payment Form Component ──────────────────────────────────────────
const KurvPaymentForm = ({
  tokenizationKey,
  paymentIntentId,
  loginEmail,
  orderGrandTotal,
  grandTotal,
  detectedCurrency,
  redirectSuccessUrl,
  redirectFailureUrl,
  buildRedirectUrl,
  onError,
  shippingProfile,
  orderProducts,
  discountAmount,
  orderFeeAmount,
  gatewayPlanId,
}) => {
  const [processing, setProcessing] = useState(false);
  const [collectJsReady, setCollectJsReady] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const tokenCallbackRef = useRef(null);
  const submittingRef = useRef(false);   // true while waiting for CollectJS callback
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;          // always points at the latest onError prop

  // Keep the token callback ref up to date so it always closes over latest state
  tokenCallbackRef.current = async (response) => {
    console.log("[KurvPay] Token callback fired:", response);
    submittingRef.current = false;
    setProcessing(false);
    // collectJs may call back with an error object (e.g. on 401) — handle gracefully
    if (!response) {
      onError?.("Tokenization failed: no response received.");
      return;
    }
    if (response.status === "error" || !response.token) {
      const msg = response.message || response.error || "Tokenization failed. Check the tokenization key and try again.";
      console.error("[KurvPay] Tokenization error:", response);
      onError?.(msg);
      return;
    }
    // Token received — call createCharge with payment_token as gatewayTransactionId
    const payment_token = response.token;
    const amount = String(orderGrandTotal || grandTotal || 0);
    const currency = detectedCurrency?.code || "USD";

    // Compute per-line-item breakdown amounts (mirrors createPaymentIntent logic)
    const _sp = shippingProfile || {};
    const _hPct = parseFloat(_sp.handlingFeeValue || 0);
    const _tPct = parseFloat(_sp.taxRate || 0);
    const _getBase = (p) => {
      const priceObj = p.prices?.[0];
      const ce =
        priceObj?.currencies?.find((c) => c.currency === detectedCurrency?.code) ||
        priceObj?.currencies?.find((c) => c.default === true) ||
        priceObj?.currencies?.[0];
      return parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0);
    };
    const _products = Array.isArray(orderProducts) ? orderProducts : [];
    const fiatSubtotal = parseFloat(
      _products.reduce((acc, p) => acc + _getBase(p), 0).toFixed(2)
    );
    const fiatShippingAmt = parseFloat(
      _products.reduce((acc, p) => acc + (_getBase(p) * _hPct) / 100, 0).toFixed(2)
    );
    const fiatTaxAmt = parseFloat(
      _products.reduce((acc, p) => acc + (_getBase(p) * _tPct) / 100, 0).toFixed(2)
    );
    let _discount = 0;
    try { _discount = discountAmount || 0; } catch (e) { _discount = 0; }
    const fiatDiscountAmt = parseFloat(_discount || 0);
    const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

    console.log("[KurvPay] payment_token:", payment_token);
    setProcessing(true);

    try {
      const chargePayload = {
        paymentIntentId,
        amount,
        currency,
        marketPrice: "0",
        blockchain: "",
        gatewayTransactionId: payment_token,
        walletProvider: "KurvPay",
        walletAddress: "",
        network: "",
        baseAmount: String(fiatSubtotal),
        taxAmount: String(fiatTaxAmt),
        discountAmount: String(fiatDiscountAmt),
        shippingAmount: String(fiatShippingAmt),
        processingFeeAmount: String(fiatProcessingFeeAmt),
        ...(gatewayPlanId ? { gatewayPlanId } : {}),
      };

      console.log("[KurvPay] createCharge payload:", chargePayload);

      const chargeResponse = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(chargePayload),
      });
      const chargeData = await chargeResponse.json();

      setProcessing(false);

      if (chargeData.status) {
        const transactionId = paymentIntentId;
        console.log("[KurvPay] Charge success, transactionId:", transactionId);
        window.location.href = buildRedirectUrl(
          redirectSuccessUrl ||
            `${window.location.origin}/merchant/checkout/success`,
          transactionId,
          "success",
          loginEmail,
          amount,
          currency
        );
      } else {
        console.error("[KurvPay] Charge failed:", chargeData.message || chargeData);
        onError?.(chargeData.message || "Payment failed. Please try again.");
        const failureTransactionId = paymentIntentId;
        window.location.href = buildRedirectUrl(
          redirectFailureUrl ||
            `${window.location.origin}/merchant/checkout/failure`,
          failureTransactionId,
          "failed",
          loginEmail,
          amount,
          currency
        );
      }
    } catch (err) {
      setProcessing(false);
      console.error("[KurvPay] Charge error:", err);
      onError?.("Network error processing payment. Please try again.");
    }
  };

  // Inject scoped CSS to strip every possible browser/global border & outline
  // from the cardholder name input regardless of !important in global styles.
  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-kurv", "cardholder");
    style.textContent = `
      #kurv-cardholder-name,
      #kurv-cardholder-name:focus,
      #kurv-cardholder-name:active,
      #kurv-cardholder-name:hover {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
        color: #111111 !important;
        -webkit-text-fill-color: #111111 !important;
      }
      #kurv-ccnumber iframe,
      #kurv-ccexp iframe,
      #kurv-cvv iframe {
        display: block !important;
        width: 100% !important;
        border: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  // Load CollectJS once per page (never removed — prevents Apple Pay SDK
  // custom-element re-registration error). On subsequent mounts just re-configure.
  useEffect(() => {
    const effectiveKey = tokenizationKey;
    if (!effectiveKey) return;
    let cancelled = false;

    setCollectJsReady(false);
    _loadCollectJs(
      effectiveKey,
      () => { if (!cancelled) setCollectJsReady(true); },
      () => { if (!cancelled) onError?.("Unable to load KurvPay payment library."); }
    );

    return () => {
      cancelled = true;
    };
  }, [tokenizationKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Configure CollectJS fields only after they are rendered into the DOM
  useEffect(() => {
    if (!collectJsReady || !window.CollectJS) return;
    window.CollectJS.configure({
      variant: "inline",
      styleSniffer: true,
      placeholderCss: {
        "color": "#9ca3af",
        "font-size": "14px",
        "font-family": "inherit",
      },
      customCss: {
        "border": "none",
        "outline": "none",
        "box-shadow": "none",
        "background": "transparent",
        "color": "#111111",
        "font-size": "15px",
        "font-family": "inherit",
        "padding": "0",
        "width": "100%",
        "height": "100%",
      },
      invalidCss: {
        "border": "none",
        "color": "#111111",
      },
      callback: (response) => {
        tokenCallbackRef.current?.(response);
      },
      validationCallback: (field, status, message) => {
        if (!status && submittingRef.current && (field === "ccexp" || field === "cvv")) {
          submittingRef.current = false;
          setProcessing(false);
          onErrorRef.current?.(message || "Please check your card details and try again.");
        }
      },
      timeoutDuration: 15000,
      timeoutCallback: () => {
        if (!submittingRef.current) return;
        console.warn("[KurvPay] Tokenization timed out");
        submittingRef.current = false;
        setProcessing(false);
        onErrorRef.current?.("Payment request timed out. Please try again.");
      },
      fields: {
        ccnumber: {
          selector: "#kurv-ccnumber",
          title: "Card Number",
          placeholder: "0000 0000 0000 0000",
        },
        ccexp: {
          selector: "#kurv-ccexp",
          title: "Expiration Date",
          placeholder: "MM / YY",
        },
        cvv: {
          selector: "#kurv-cvv",
          title: "CVV",
          placeholder: "CVV",
        },
      },
    });
  }, [collectJsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!window.CollectJS || !collectJsReady) {
      onError?.("KurvPay is not ready. Please wait and try again.");
      return;
    }
    if (!cardholderName.trim()) {
      onError?.("Please enter the cardholder name.");
      return;
    }
    if (!/^[a-zA-Z ]+$/.test(cardholderName.trim())) {
      onError?.("Cardholder name must contain only letters and spaces.");
      return;
    }
    submittingRef.current = true;
    setProcessing(true);
    window.CollectJS.startPaymentRequest();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Info bar */}
      <div className="co-stripe-sub-info">
        <p>
          <i
            className="fa fa-info-circle"
            style={{ color: "#111", marginRight: 6 }}
          />
          Enter your card details to complete the payment.
        </p>
        <p>
          Your card information is tokenized securely by PayBito Nexus and never
          stored on our servers.
        </p>
      </div>

      {!collectJsReady && (
        <div
          style={{
            border: "1.5px solid var(--co-gray-200)",
            borderRadius: 10,
            padding: "32px 16px",
            marginBottom: 16,
            background: "#f9fafb",
            textAlign: "center",
          }}
        >
          <span
            className="co-spinner"
            style={{ borderTopColor: "#111", width: 24, height: 24 }}
          />
          <p
            style={{ marginTop: 12, fontSize: 13, color: "var(--co-gray-500)" }}
          >
            Loading secure payment form...
          </p>
        </div>
      )}
      <div style={{ display: collectJsReady ? undefined : "none" }}>
          {/* CollectJS hosted iframes + cardholder name */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 18 }}>

            {/* Cardholder name */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--co-gray-500,#6b7280)",
                  marginBottom: 6,
                  letterSpacing: "0.04em",
                }}
              >
                Cardholder Name
              </label>
              <div
                style={{
                  height: 48,
                  border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                  borderRadius: 10,
                  padding: "0 14px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <input
                  id="kurv-cardholder-name"
                  type="text"
                  autoComplete="off"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value.replace(/[^a-zA-Z ]/g, ""))}
                  disabled={processing}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                    background: "transparent",
                    backgroundColor: "transparent",
                    fontSize: 15,
                    color: "#111",
                    caretColor: "#111",
                    colorScheme: "light",
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#111",
                    appearance: "none",
                    WebkitAppearance: "none",
                  }}
                />
              </div>
            </div>

            {/* Card number */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--co-gray-500,#6b7280)",
                  marginBottom: 6,
                  letterSpacing: "0.04em",
                }}
              >
                Card Number
              </label>
              <div
                id="kurv-ccnumber"
                style={{
                  height: 48,
                  border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                  borderRadius: 10,
                  padding: "0 14px",
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              />
            </div>

            {/* Expiry + CVV row */}
            <div style={{ display: "flex", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--co-gray-500,#6b7280)",
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  Expiry Date
                </label>
                <div
                  id="kurv-ccexp"
                  style={{
                    height: 48,
                    border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                    borderRadius: 10,
                    padding: "0 14px",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--co-gray-500,#6b7280)",
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  CVV
                </label>
                <div
                  id="kurv-cvv"
                  style={{
                    height: 48,
                    border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                    borderRadius: 10,
                    padding: "0 14px",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                />
              </div>
            </div>
          </div>


          {/* Submit button */}
          <button
            type="submit"
            className="co-btn"
            disabled={processing || !collectJsReady}
            style={{
              width: "100%",
              background: processing ? "#555" : "#111",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "13px 20px",
              fontSize: 15,
              fontWeight: 700,
              cursor: processing || !collectJsReady ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background .2s",
            }}
          >
            {processing ? (
              <>
                <span className="co-spinner" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,0.3)" }} />
                &nbsp;Processing Payment...
              </>
            ) : (
              <>
                <i className="fa fa-lock" />
                &nbsp;Pay {detectedCurrency?.symbol}
                {(orderGrandTotal || grandTotal || 0).toFixed(2)} with PayBito Nexus
              </>
            )}
          </button>
      </div>

    </form>
  );
};

// ─── HMS Payment Form Component ───────────────────────────────────────────────
const HMSPaymentForm = ({
  tokenizationKey,
  paymentIntentId,
  loginEmail,
  orderGrandTotal,
  grandTotal,
  detectedCurrency,
  redirectSuccessUrl,
  redirectFailureUrl,
  buildRedirectUrl,
  onError,
  shippingProfile,
  orderProducts,
  discountAmount,
  orderFeeAmount,
  gatewayPlanId,
}) => {
  const [processing, setProcessing] = useState(false);
  const [collectJsReady, setCollectJsReady] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const tokenCallbackRef = useRef(null);
  const submittingRef = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  tokenCallbackRef.current = async (response) => {
    console.log("[HMS] Token callback fired:", response);
    submittingRef.current = false;
    setProcessing(false);
    if (!response) {
      onError?.("Tokenization failed: no response received.");
      return;
    }
    if (response.status === "error" || !response.token) {
      const msg = response.message || response.error || "Tokenization failed. Check the tokenization key and try again.";
      console.error("[HMS] Tokenization error:", response);
      onError?.(msg);
      return;
    }
    const payment_token = response.token;
    const amount = String(orderGrandTotal || grandTotal || 0);
    const currency = detectedCurrency?.code || "USD";

    const _sp = shippingProfile || {};
    const _hPct = parseFloat(_sp.handlingFeeValue || 0);
    const _tPct = parseFloat(_sp.taxRate || 0);
    const _getBase = (p) => {
      const priceObj = p.prices?.[0];
      const ce =
        priceObj?.currencies?.find((c) => c.currency === detectedCurrency?.code) ||
        priceObj?.currencies?.find((c) => c.default === true) ||
        priceObj?.currencies?.[0];
      return parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0);
    };
    const _products = Array.isArray(orderProducts) ? orderProducts : [];
    const fiatSubtotal = parseFloat(
      _products.reduce((acc, p) => acc + _getBase(p), 0).toFixed(2)
    );
    const fiatShippingAmt = parseFloat(
      _products.reduce((acc, p) => acc + (_getBase(p) * _hPct) / 100, 0).toFixed(2)
    );
    const fiatTaxAmt = parseFloat(
      _products.reduce((acc, p) => acc + (_getBase(p) * _tPct) / 100, 0).toFixed(2)
    );
    let _discount = 0;
    try { _discount = discountAmount || 0; } catch (e) { _discount = 0; }
    const fiatDiscountAmt = parseFloat(_discount || 0);
    const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

    console.log("[HMS] payment_token:", payment_token);
    setProcessing(true);

    try {
      const chargePayload = {
        paymentIntentId,
        amount,
        currency,
        marketPrice: "0",
        blockchain: "",
        gatewayTransactionId: payment_token,
        walletProvider: "HostMerchantServices",
        walletAddress: "",
        network: "",
        baseAmount: String(fiatSubtotal),
        taxAmount: String(fiatTaxAmt),
        discountAmount: String(fiatDiscountAmt),
        shippingAmount: String(fiatShippingAmt),
        processingFeeAmount: String(fiatProcessingFeeAmt),
        ...(gatewayPlanId ? { gatewayPlanId } : {}),
      };

      console.log("[HMS] createCharge payload:", chargePayload);

      const chargeResponse = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(chargePayload),
      });
      const chargeData = await chargeResponse.json();

      setProcessing(false);

      if (chargeData.status) {
        const transactionId = paymentIntentId;
        console.log("[HMS] Charge success, transactionId:", transactionId);
        window.location.href = buildRedirectUrl(
          redirectSuccessUrl ||
            `${window.location.origin}/merchant/checkout/success`,
          transactionId,
          "success",
          loginEmail,
          amount,
          currency
        );
      } else {
        console.error("[HMS] Charge failed:", chargeData.message || chargeData);
        onError?.(chargeData.message || "Payment failed. Please try again.");
        const failureTransactionId = paymentIntentId;
        window.location.href = buildRedirectUrl(
          redirectFailureUrl ||
            `${window.location.origin}/merchant/checkout/failure`,
          failureTransactionId,
          "failed",
          loginEmail,
          amount,
          currency
        );
      }
    } catch (err) {
      setProcessing(false);
      console.error("[HMS] Charge error:", err);
      onError?.("Network error processing payment. Please try again.");
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-hms", "cardholder");
    style.textContent = `
      #hms-cardholder-name,
      #hms-cardholder-name:focus,
      #hms-cardholder-name:active,
      #hms-cardholder-name:hover {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
        color: #111111 !important;
        -webkit-text-fill-color: #111111 !important;
      }
      #hms-ccnumber iframe,
      #hms-ccexp iframe,
      #hms-cvv iframe {
        display: block !important;
        width: 100% !important;
        border: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    const effectiveKey = tokenizationKey;
    if (!effectiveKey) return;
    let cancelled = false;

    setCollectJsReady(false);
    _loadHmsCollectJs(
      effectiveKey,
      () => { if (!cancelled) setCollectJsReady(true); },
      () => { if (!cancelled) onError?.("Unable to load HMS payment library."); }
    );

    return () => {
      cancelled = true;
    };
  }, [tokenizationKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Configure CollectJS fields only after they are rendered into the DOM
  useEffect(() => {
    if (!collectJsReady || !window.CollectJS) return;
    window.CollectJS.configure({
      variant: "inline",
      styleSniffer: true,
      placeholderCss: {
        "color": "#9ca3af",
        "font-size": "14px",
        "font-family": "inherit",
      },
      customCss: {
        "border": "none",
        "outline": "none",
        "box-shadow": "none",
        "background": "transparent",
        "color": "#111111",
        "font-size": "15px",
        "font-family": "inherit",
        "padding": "0",
        "width": "100%",
        "height": "100%",
      },
      invalidCss: {
        "border": "none",
        "color": "#111111",
      },
      callback: (response) => {
        tokenCallbackRef.current?.(response);
      },
      validationCallback: (field, status, message) => {
        if (!status && submittingRef.current && (field === "ccexp" || field === "cvv")) {
          submittingRef.current = false;
          setProcessing(false);
          onErrorRef.current?.(message || "Please check your card details and try again.");
        }
      },
      timeoutDuration: 15000,
      timeoutCallback: () => {
        if (!submittingRef.current) return;
        console.warn("[HMS] Tokenization timed out");
        submittingRef.current = false;
        setProcessing(false);
        onErrorRef.current?.("Payment request timed out. Please try again.");
      },
      fields: {
        ccnumber: {
          selector: "#hms-ccnumber",
          title: "Card Number",
          placeholder: "0000 0000 0000 0000",
        },
        ccexp: {
          selector: "#hms-ccexp",
          title: "Expiration Date",
          placeholder: "MM / YY",
        },
        cvv: {
          selector: "#hms-cvv",
          title: "CVV",
          placeholder: "CVV",
        },
      },
    });
  }, [collectJsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!window.CollectJS || !collectJsReady) {
      onError?.("HMS is not ready. Please wait and try again.");
      return;
    }
    if (!cardholderName.trim()) {
      onError?.("Please enter the cardholder name.");
      return;
    }
    if (!/^[a-zA-Z ]+$/.test(cardholderName.trim())) {
      onError?.("Cardholder name must contain only letters and spaces.");
      return;
    }
    submittingRef.current = true;
    setProcessing(true);
    window.CollectJS.startPaymentRequest();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Info bar */}
      <div className="co-stripe-sub-info">
        <p>
          <i
            className="fa fa-info-circle"
            style={{ color: "#111", marginRight: 6 }}
          />
          Enter your card details to complete the payment.
        </p>
        <p>
          Your card information is tokenized securely by PayBito Nova and never
          stored on our servers.
        </p>
      </div>

      {!collectJsReady && (
        <div
          style={{
            border: "1.5px solid var(--co-gray-200)",
            borderRadius: 10,
            padding: "32px 16px",
            marginBottom: 16,
            background: "#f9fafb",
            textAlign: "center",
          }}
        >
          <span
            className="co-spinner"
            style={{ borderTopColor: "#111", width: 24, height: 24 }}
          />
          <p
            style={{ marginTop: 12, fontSize: 13, color: "var(--co-gray-500)" }}
          >
            Loading secure payment form...
          </p>
        </div>
      )}
      <div style={{ display: collectJsReady ? undefined : "none" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 18 }}>

            {/* Cardholder name */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--co-gray-500,#6b7280)",
                  marginBottom: 6,
                  letterSpacing: "0.04em",
                }}
              >
                Cardholder Name
              </label>
              <div
                style={{
                  height: 48,
                  border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                  borderRadius: 10,
                  padding: "0 14px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <input
                  id="hms-cardholder-name"
                  type="text"
                  autoComplete="off"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value.replace(/[^a-zA-Z ]/g, ""))}
                  disabled={processing}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                    background: "transparent",
                    backgroundColor: "transparent",
                    fontSize: 15,
                    color: "#111",
                    caretColor: "#111",
                    colorScheme: "light",
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#111",
                    appearance: "none",
                    WebkitAppearance: "none",
                  }}
                />
              </div>
            </div>

            {/* Card number */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--co-gray-500,#6b7280)",
                  marginBottom: 6,
                  letterSpacing: "0.04em",
                }}
              >
                Card Number
              </label>
              <div
                id="hms-ccnumber"
                style={{
                  height: 48,
                  border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                  borderRadius: 10,
                  padding: "0 14px",
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              />
            </div>

            {/* Expiry + CVV row */}
            <div style={{ display: "flex", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--co-gray-500,#6b7280)",
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  Expiry Date
                </label>
                <div
                  id="hms-ccexp"
                  style={{
                    height: 48,
                    border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                    borderRadius: 10,
                    padding: "0 14px",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--co-gray-500,#6b7280)",
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  CVV
                </label>
                <div
                  id="hms-cvv"
                  style={{
                    height: 48,
                    border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                    borderRadius: 10,
                    padding: "0 14px",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className="co-btn"
            disabled={processing || !collectJsReady}
            style={{
              width: "100%",
              background: processing ? "#555" : "#111",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "13px 20px",
              fontSize: 15,
              fontWeight: 700,
              cursor: processing || !collectJsReady ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background .2s",
            }}
          >
            {processing ? (
              <>
                <span className="co-spinner" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,0.3)" }} />
                &nbsp;Processing Payment...
              </>
            ) : (
              <>
                <i className="fa fa-lock" />
                &nbsp;Pay {detectedCurrency?.symbol}
                {(orderGrandTotal || grandTotal || 0).toFixed(2)} with PayBito Nova
              </>
            )}
          </button>
      </div>

    </form>
  );
};

// ─── NMI Payment Form Component ───────────────────────────────────────────────
const NMIPaymentForm = ({
  tokenizationKey,
  paymentIntentId,
  loginEmail,
  orderGrandTotal,
  grandTotal,
  detectedCurrency,
  redirectSuccessUrl,
  redirectFailureUrl,
  buildRedirectUrl,
  onError,
  shippingProfile,
  orderProducts,
  discountAmount,
  orderFeeAmount,
  gatewayPlanId,
}) => {
  const [processing, setProcessing] = useState(false);
  const [collectJsReady, setCollectJsReady] = useState(false);
  const [nmiFieldsReady, setNmiFieldsReady] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  // Unique suffix for container IDs on every mount so CollectJS always sees
  // brand-new selectors and sets up a fresh postMessage channel. Reusing the
  // same IDs across mounts leaves the old (orphaned) channel active, making
  // the field iframes appear but refuse keyboard input.
  const mountIdRef = useRef(`m${Date.now()}`);
  const tokenCallbackRef = useRef(null);
  const submittingRef = useRef(false);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  tokenCallbackRef.current = async (response) => {
    console.log("[NMI] Token callback fired:", response);
    submittingRef.current = false;
    setProcessing(false);
    if (!response) {
      onError?.("Tokenization failed: no response received.");
      return;
    }
    if (response.status === "error" || !response.token) {
      const msg = response.message || response.error || "Tokenization failed. Check the tokenization key and try again.";
      console.error("[NMI] Tokenization error:", response);
      onError?.(msg);
      return;
    }
    const payment_token = response.token;
    const amount = String(orderGrandTotal || grandTotal || 0);
    const currency = detectedCurrency?.code || "USD";

    const _sp = shippingProfile || {};
    const _hPct = parseFloat(_sp.handlingFeeValue || 0);
    const _tPct = parseFloat(_sp.taxRate || 0);
    const _getBase = (p) => {
      const priceObj = p.prices?.[0];
      const ce =
        priceObj?.currencies?.find((c) => c.currency === detectedCurrency?.code) ||
        priceObj?.currencies?.find((c) => c.default === true) ||
        priceObj?.currencies?.[0];
      return parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0);
    };
    const _products = Array.isArray(orderProducts) ? orderProducts : [];
    const fiatSubtotal = parseFloat(
      _products.reduce((acc, p) => acc + _getBase(p), 0).toFixed(2)
    );
    const fiatShippingAmt = parseFloat(
      _products.reduce((acc, p) => acc + (_getBase(p) * _hPct) / 100, 0).toFixed(2)
    );
    const fiatTaxAmt = parseFloat(
      _products.reduce((acc, p) => acc + (_getBase(p) * _tPct) / 100, 0).toFixed(2)
    );
    let _discount = 0;
    try { _discount = discountAmount || 0; } catch (e) { _discount = 0; }
    const fiatDiscountAmt = parseFloat(_discount || 0);
    const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

    console.log("[NMI] payment_token:", payment_token);
    setProcessing(true);

    try {
      const chargePayload = {
        paymentIntentId,
        amount,
        currency,
        marketPrice: "0",
        blockchain: "",
        gatewayTransactionId: payment_token,
        walletProvider: "NMI",
        walletAddress: "",
        network: "",
        baseAmount: String(fiatSubtotal),
        taxAmount: String(fiatTaxAmt),
        discountAmount: String(fiatDiscountAmt),
        shippingAmount: String(fiatShippingAmt),
        processingFeeAmount: String(fiatProcessingFeeAmt),
        ...(gatewayPlanId ? { gatewayPlanId } : {}),
      };

      console.log("[NMI] createCharge payload:", chargePayload);

      const chargeResponse = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(chargePayload),
      });
      const chargeData = await chargeResponse.json();

      setProcessing(false);

      if (chargeData.status) {
        const transactionId = paymentIntentId;
        console.log("[NMI] Charge success, transactionId:", transactionId);
        window.location.href = buildRedirectUrl(
          redirectSuccessUrl ||
            `${window.location.origin}/merchant/checkout/success`,
          transactionId,
          "success",
          loginEmail,
          amount,
          currency
        );
      } else {
        console.error("[NMI] Charge failed:", chargeData.message || chargeData);
        onError?.(chargeData.message || "Payment failed. Please try again.");
        const failureTransactionId = paymentIntentId;
        window.location.href = buildRedirectUrl(
          redirectFailureUrl ||
            `${window.location.origin}/merchant/checkout/failure`,
          failureTransactionId,
          "failed",
          loginEmail,
          amount,
          currency
        );
      }
    } catch (err) {
      setProcessing(false);
      console.error("[NMI] Charge error:", err);
      onError?.("Network error processing payment. Please try again.");
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.setAttribute("data-nmi", "cardholder");
    style.textContent = `
      #nmi-cardholder-name,
      #nmi-cardholder-name:focus,
      #nmi-cardholder-name:active,
      #nmi-cardholder-name:hover {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
        color: #111111 !important;
        -webkit-text-fill-color: #111111 !important;
      }
      [id^="nmi-ccnumber"] iframe,
      [id^="nmi-ccexp"] iframe,
      [id^="nmi-cvv"] iframe {
        display: block !important;
        width: 100% !important;
        border: none !important;
        pointer-events: auto !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  useEffect(() => {
    const effectiveKey = tokenizationKey;
    if (!effectiveKey) return;
    let cancelled = false;

    setCollectJsReady(false);
    _loadNmiCollectJs(
      effectiveKey,
      () => { if (!cancelled) setCollectJsReady(true); },
      () => { if (!cancelled) onError?.("Unable to load NMI payment library."); }
    );

    return () => {
      cancelled = true;
    };
  }, [tokenizationKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setNmiFieldsReady(false);
    if (!collectJsReady || !window.CollectJS) return;
    const mid = mountIdRef.current;
    // Clear any stale iframes from the current mount's containers before
    // configure() re-injects fresh ones.
    [`#nmi-ccnumber-${mid}`, `#nmi-ccexp-${mid}`, `#nmi-cvv-${mid}`].forEach((sel) => {
      const el = document.querySelector(sel);
      if (el) el.innerHTML = "";
    });
    window.CollectJS.configure({
      variant: "inline",
      styleSniffer: true,
      placeholderCss: {
        "color": "#9ca3af",
        "font-size": "14px",
        "font-family": "inherit",
      },
      customCss: {
        "border": "none",
        "outline": "none",
        "box-shadow": "none",
        "background": "transparent",
        "color": "#111111",
        "font-size": "15px",
        "font-family": "inherit",
        "padding": "0",
        "width": "100%",
        "height": "100%",
      },
      invalidCss: {
        "border": "none",
        "color": "#111111",
      },
      callback: (response) => {
        tokenCallbackRef.current?.(response);
      },
      validationCallback: (field, status, message) => {
        if (!status && submittingRef.current && (field === "ccexp" || field === "cvv")) {
          submittingRef.current = false;
          setProcessing(false);
          onErrorRef.current?.(message || "Please check your card details and try again.");
        }
      },
      timeoutDuration: 15000,
      timeoutCallback: () => {
        if (!submittingRef.current) return;
        console.warn("[NMI] Tokenization timed out");
        submittingRef.current = false;
        setProcessing(false);
        onErrorRef.current?.("Payment request timed out. Please try again.");
      },
      fields: {
        ccnumber: {
          selector: `#nmi-ccnumber-${mid}`,
          title: "Card Number",
          placeholder: "0000 0000 0000 0000",
        },
        ccexp: {
          selector: `#nmi-ccexp-${mid}`,
          title: "Expiration Date",
          placeholder: "MM / YY",
        },
        cvv: {
          selector: `#nmi-cvv-${mid}`,
          title: "CVV",
          placeholder: "CVV",
        },
      },
      fieldsAvailableCallback: () => {
        setNmiFieldsReady(true);
      },
    });
  }, [collectJsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety-net: if fieldsAvailableCallback never fires on subsequent configure()
  // calls (a known CollectJS limitation), unlock the form after 5 s so the user
  // is never stuck waiting indefinitely.
  useEffect(() => {
    if (!collectJsReady) return;
    const timer = setTimeout(() => setNmiFieldsReady(true), 5000);
    return () => clearTimeout(timer);
  }, [collectJsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // On unmount remove only the body-level NMI iframes (the tokenization overlay
  // iframes CollectJS appends directly to <body>). Removing [id*="CollectJS"]
  // elements caused cross-origin "Script error." because orphaned CollectJS
  // timers/postMessage handlers crashed when window.CollectJS was deleted.
  useEffect(() => {
    return () => {
      document
        .querySelectorAll('iframe[src*="nmi.com"], iframe[src*="secure.nmi"]')
        .forEach((el) => { if (el.parentElement === document.body) el.remove(); });
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!window.CollectJS || !collectJsReady) {
      onError?.("NMI is not ready. Please wait and try again.");
      return;
    }
    if (!cardholderName.trim()) {
      onError?.("Please enter the cardholder name.");
      return;
    }
    if (!/^[a-zA-Z ]+$/.test(cardholderName.trim())) {
      onError?.("Cardholder name must contain only letters and spaces.");
      return;
    }
    submittingRef.current = true;
    setProcessing(true);
    window.CollectJS.startPaymentRequest();
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Info bar */}
      <div className="co-stripe-sub-info">
        <p>
          <i
            className="fa fa-info-circle"
            style={{ color: "#111", marginRight: 6 }}
          />
          Enter your card details to complete the payment.
        </p>
        <p>
          Your card information is tokenized securely by PayBito Zenith and never
          stored on our servers.
        </p>
      </div>

      {/* Script not yet loaded — shown OUTSIDE the display:none wrapper so it's
          visible while the CollectJS script is downloading on page load */}
      {!collectJsReady && (
        <div
          style={{
            border: "1.5px solid var(--co-gray-200)",
            borderRadius: 10,
            padding: "32px 16px",
            marginBottom: 16,
            background: "#f9fafb",
            textAlign: "center",
          }}
        >
          <span className="co-spinner" style={{ borderTopColor: "#111", width: 24, height: 24 }} />
          <p style={{ marginTop: 12, fontSize: 13, color: "var(--co-gray-500)" }}>
            Loading secure payment form...
          </p>
        </div>
      )}

      {/* Outer wrapper: keeps iframe containers in the DOM (not display:none) once the
          script is loaded so CollectJS can inject iframes into a laid-out context.
          An absolute spinner overlay covers them until fieldsAvailableCallback fires. */}
      <div style={{ display: collectJsReady ? undefined : "none", position: "relative" }}>
        {/* Iframes injecting — absolute overlay so containers stay laid-out below */}
        {collectJsReady && !nmiFieldsReady && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "#f9fafb",
              borderRadius: 10,
              border: "1.5px solid var(--co-gray-200)",
              minHeight: 200,
            }}
          >
            <span className="co-spinner" style={{ borderTopColor: "#111", width: 24, height: 24 }} />
            <p style={{ marginTop: 12, fontSize: 13, color: "var(--co-gray-500)" }}>
              Loading secure payment form...
            </p>
          </div>
        )}
        {/* Fields: opacity:0 + pointerEvents:none while waiting.
            Do NOT use visibility:hidden — cross-origin iframes initialized inside a
            visibility:hidden parent render visually but their internal input state
            is broken, so the user can focus the field but cannot type.
            opacity:0 keeps the element fully rendered so iframes initialize properly. */}
        <div style={{
          opacity: nmiFieldsReady ? 1 : 0,
          pointerEvents: nmiFieldsReady ? undefined : "none",
          transition: "opacity 0.15s",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 18 }}>

            {/* Cardholder name */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--co-gray-500,#6b7280)",
                  marginBottom: 6,
                  letterSpacing: "0.04em",
                }}
              >
                Cardholder Name
              </label>
              <div
                style={{
                  height: 48,
                  border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                  borderRadius: 10,
                  padding: "0 14px",
                  background: "#fff",
                  display: "flex",
                  alignItems: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                }}
              >
                <input
                  id="nmi-cardholder-name"
                  type="text"
                  autoComplete="off"
                  value={cardholderName}
                  onChange={(e) => setCardholderName(e.target.value.replace(/[^a-zA-Z ]/g, ""))}
                  disabled={processing}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    boxShadow: "none",
                    background: "transparent",
                    backgroundColor: "transparent",
                    fontSize: 15,
                    color: "#111",
                    caretColor: "#111",
                    colorScheme: "light",
                    WebkitBoxShadow: "0 0 0 1000px #fff inset",
                    WebkitTextFillColor: "#111",
                    appearance: "none",
                    WebkitAppearance: "none",
                  }}
                />
              </div>
            </div>

            {/* Card number */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "var(--co-gray-500,#6b7280)",
                  marginBottom: 6,
                  letterSpacing: "0.04em",
                }}
              >
                Card Number
              </label>
              <div
                id={`nmi-ccnumber-${mountIdRef.current}`}
                style={{
                  height: 48,
                  border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                  borderRadius: 10,
                  padding: "0 14px",
                  background: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  cursor: "text",
                }}
                onClick={(e) => { e.currentTarget.querySelector("iframe")?.focus(); }}
              />
            </div>

            {/* Expiry + CVV row */}
            <div style={{ display: "flex", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--co-gray-500,#6b7280)",
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  Expiry Date
                </label>
                <div
                  id={`nmi-ccexp-${mountIdRef.current}`}
                  style={{
                    height: 48,
                    border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                    borderRadius: 10,
                    padding: "0 14px",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                    cursor: "text",
                  }}
                  onClick={(e) => { e.currentTarget.querySelector("iframe")?.focus(); }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--co-gray-500,#6b7280)",
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  CVV
                </label>
                <div
                  id={`nmi-cvv-${mountIdRef.current}`}
                  style={{
                    height: 48,
                    border: "1.5px solid var(--co-gray-200,#e2e6ef)",
                    borderRadius: 10,
                    padding: "0 14px",
                    background: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    overflow: "hidden",
                    cursor: "text",
                  }}
                  onClick={(e) => { e.currentTarget.querySelector("iframe")?.focus(); }}
                />
              </div>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            className="co-btn"
            disabled={processing || !nmiFieldsReady}
            style={{
              width: "100%",
              background: processing ? "#555" : "#111",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "13px 20px",
              fontSize: 15,
              fontWeight: 700,
              cursor: processing || !nmiFieldsReady ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              transition: "background .2s",
            }}
          >
            {processing ? (
              <>
                <span className="co-spinner" style={{ borderTopColor: "#fff", borderColor: "rgba(255,255,255,0.3)" }} />
                &nbsp;Processing Payment...
              </>
            ) : (
              <>
                <i className="fa fa-lock" />
                &nbsp;Pay {detectedCurrency?.symbol}
                {(orderGrandTotal || grandTotal || 0).toFixed(2)} with PayBito Zenith
              </>
            )}
          </button>
        </div> {/* end visibility wrapper */}
      </div> {/* end outer position:relative wrapper */}

    </form>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getUrlParameter = (name) =>
  new URLSearchParams(window.location.search).get(name);
function isOn(val) {
  return val === 1 || val === "1" || val === true || val === "true";
}
const getBrokerIdFromUrl = () => {
  const fromParam = getUrlParameter("brokerId");
  if (fromParam) return fromParam;
  const dp = getUrlParameter("data");
  if (dp) {
    try {
      const decoded = JSON.parse(atob(dp.replace(/ /g, "+")));
      const obj = Array.isArray(decoded) ? decoded[0] : decoded;
      const bid = obj.brokerId || obj.broker_id || null;
      if (bid) return bid;
    } catch {}
  }
  return (
    localStorage.getItem("BROKERID") ||
    localStorage.getItem(SITENAMEALIAS + "_brokerId")
  );
};
const parseRedirectUrl = (result) => {
  const split = result.split("window.location=");
  return split[1]
    .replace(/;[\s\S]*$/, "")
    .replace(/^["']|["']$/g, "")
    .trim();
};

// Helper function to build redirect URL with parameters
const buildRedirectUrl = (
  baseUrl,
  paymentId,
  status,
  customerEmail,
  amount,
  currency = null,
  advanceOptions = []
) => {
  try {
    const url = new URL(baseUrl);

    // Helper to check if a parameter is enabled in advance options
    const isParamEnabled = (paramKey) => {
      if (advanceOptions.length === 0) return true; // If no options specified, include all
      const option = advanceOptions.find((opt) => opt.paramKey === paramKey);
      return option ? option.isEnabled === 1 : false;
    };

    // Only add parameters that are enabled in advance options
    if (isParamEnabled("payment_id")) {
      url.searchParams.append("payment_id", paymentId);
    }
    if (isParamEnabled("status")) {
      url.searchParams.append("status", status);
    }
    if (customerEmail && isParamEnabled("customer_email")) {
      url.searchParams.append(
        "customer_email",
        encodeURIComponent(customerEmail)
      );
    }
    if (amount && isParamEnabled("amount")) {
      url.searchParams.append("amount", amount);
    }
    if (currency) {
      url.searchParams.append("currency", currency);
    }
    return url.toString();
  } catch (e) {
    // If baseUrl is invalid, use default
    const defaultBase =
      status === "success"
        ? `${window.location.origin}/merchant/checkout/success`
        : `${window.location.origin}/merchant/checkout/failure`;
    const url = new URL(defaultBase);

    // Helper to check if a parameter is enabled in advance options
    const isParamEnabled = (paramKey) => {
      if (advanceOptions.length === 0) return true;
      const option = advanceOptions.find((opt) => opt.paramKey === paramKey);
      return option ? option.isEnabled === 1 : false;
    };

    if (isParamEnabled("payment_id")) {
      url.searchParams.append("payment_id", paymentId);
    }
    if (isParamEnabled("status")) {
      url.searchParams.append("status", status);
    }
    if (customerEmail && isParamEnabled("customer_email")) {
      url.searchParams.append(
        "customer_email",
        encodeURIComponent(customerEmail)
      );
    }
    if (amount && isParamEnabled("amount")) {
      url.searchParams.append("amount", amount);
    }
    if (currency) {
      url.searchParams.append("currency", currency);
    }
    return url.toString();
  }
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
  :root {
    --co-primary:#2563EB; --co-primary-dark:#1D4ED8; --co-primary-active:#1E40AF; --co-primary-disabled:#93C5FD;
    --co-success:#15803D; --co-success-hover:#0d6b3f; --co-success-disabled:#86EFAC;
    --co-danger:#ef4444; --co-gray-100:#f1f3f9; --co-gray-200:#e2e6ef;
    --co-gray-500:#6b7280; --co-gray-700:#374151; --co-gray-900:#111827;
    --co-radius:16px; --co-shadow-md:0 8px 30px rgba(0,0,0,0.09);
  }
  .co-wrap * { box-sizing:border-box; font-family:'Roboto',sans-serif!important; }
  .co-wrap { font-family:'Roboto',sans-serif!important; background:linear-gradient(135deg,#e8ecf8 0%,#f0f2fa 50%,#eef0f7 100%)!important; margin:0; padding:0; min-height:100vh; color:var(--co-gray-900); }
  .co-page-inner { padding:40px 20px 60px; }
  .co-header { text-align:center; margin-bottom:32px; }
  .co-header-icon { width:64px; height:64px; border-radius:50%; background:linear-gradient(135deg,var(--co-primary),var(--co-primary-dark)); display:inline-flex; align-items:center; justify-content:center; margin-bottom:16px; box-shadow:0 8px 24px rgba(37,99,235,0.3); }
  .co-header-icon i { font-size:24px; color:#fff; }
  .co-header h2 { font-size:15px; font-weight:400; color:var(--co-gray-500); margin:0 0 2px; }
  .co-header p  { font-size:13px; color:var(--co-gray-500); margin:0; }
  .co-card { max-width:1060px; margin:0 auto; background:linear-gradient(145deg,#ffffff,#f6f7fc); border-radius:var(--co-radius); box-shadow:var(--co-shadow-md); border:1px solid rgba(37,99,235,0.08); overflow:hidden; }
  .co-card-accent { height:4px; background:linear-gradient(90deg,var(--co-primary),var(--co-primary-dark),var(--co-success)); }
  .co-card-body { padding:36px; }
  .co-layout { display:flex; gap:40px; align-items:flex-start; }
  .co-left  { flex:0 0 360px; }
  .co-right { flex:1; min-width:0; overflow-x:hidden; }
  @media(max-width:820px){
    .co-layout{flex-direction:column;}
    .co-left{flex:none;width:100%;}
    .co-right{width:100%;overflow-x:hidden!important;}
    .co-card-body{padding:20px;}
    .co-tbl-wrap { width:100%!important; max-width:100%!important; overflow-x:auto!important; overflow-y:visible!important; -webkit-overflow-scrolling:touch!important; border:1px solid var(--co-gray-200)!important; border-radius:12px!important; direction:ltr!important; }
    .co-tbl { border:none!important; border-radius:0!important; overflow:visible!important; width:auto!important; min-width:100%!important; }
    .co-tbl thead th, .co-tbl tbody td, .co-tbl tfoot th { white-space:nowrap!important; }
  }
  @media(max-width:560px){
    .co-card-body{padding:16px;}
    .co-section-label{font-size:18px!important; padding-bottom:14px!important;}
    .co-btn{padding:12px 14px; font-size:13px; gap:6px; flex-wrap:wrap; line-height:1.25; word-break:break-word;}
    .co-btn i{font-size:14px;}
    .co-total-val{font-size:34px!important; white-space:normal!important; word-break:break-word!important;}
    .co-total-card{padding:18px;}
    .co-total-note{font-size:11.5px;}
    .co-option-header{padding:14px 14px; gap:10px;}
    .co-option-icon{width:40px; height:40px; font-size:16px;}
    .co-option-title{font-size:13px;}
    .co-option-desc{font-size:11px;}
    .co-badge{font-size:10px; padding:3px 8px;}
    .co-tbl { font-size:12px!important; }
    .co-tbl thead th, .co-tbl tbody td, .co-tbl tfoot th { padding:10px 12px!important; }
    .co-tbl thead th:last-child, .co-tbl tbody td:last-child, .co-tbl tfoot th:last-child { padding-right:20px!important; }
  }
  @media(max-width:380px){
    .co-card-body{padding:12px;}
    .co-total-val{font-size:28px!important;}
  }
  .co-section-label { display:flex!important; align-items:center!important; gap:12px!important; font-size:22px!important; font-weight:700!important; color:var(--co-gray-900)!important; margin-bottom:10px!important; padding-bottom:18px!important; border-bottom:2px solid var(--co-gray-200)!important; letter-spacing:-0.2px!important; line-height:1.3!important; }
  .co-section-label::before { content:''!important; width:5px!important; height:26px!important; flex-shrink:0!important; background:linear-gradient(135deg,var(--co-primary),var(--co-primary-dark))!important; border-radius:2px!important; }
  .co-section-sub { font-size:13px; color:var(--co-gray-500); margin:0 0 24px; line-height:1.55; }
  .co-options { display:flex; flex-direction:column; gap:12px; }
  .co-option { border:1.5px solid var(--co-gray-200); border-radius:12px; background:#fff; cursor:pointer; overflow:hidden; transition:border-color .2s,box-shadow .2s,transform .22s; }
  .co-option:hover { border-color:var(--co-primary); box-shadow:0 4px 20px rgba(37,99,235,0.12); transform:translateY(-2px); }
  .co-option.selected { border-color:var(--co-primary); box-shadow:0 0 0 3px rgba(37,99,235,0.11); transform:none; }
  .co-option.is-loading { pointer-events:none; opacity:0.7; }
  .co-option-header { display:flex; align-items:center; gap:14px; padding:16px 18px; }
  .co-option-icon { width:46px; height:46px; border-radius:11px; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:18px; transition:background .2s,color .2s; background:var(--co-primary); color:#fff; }
  .co-option-icon.blue   { background:var(--co-primary);  color:#fff; }
  .co-option-icon.green  { background:var(--co-success); color:#fff; }
  .co-option-icon.purple { background:#7c3aed; color:#fff; }
  .co-option-icon.stripe { background:#635bff;  color:#fff; }
  .co-option-icon.netbilling { background:#004a99; color:#fff; }
  .co-option-icon.cardflo { background:#0a85ea; color:#fff; }
  .co-option.selected .co-option-icon.blue,   .co-option:hover .co-option-icon.blue   { background:var(--co-primary); color:#fff; }
  .co-option.selected .co-option-icon.green,  .co-option:hover .co-option-icon.green  { background:var(--co-success); color:#fff; }
  .co-option.selected .co-option-icon.purple, .co-option:hover .co-option-icon.purple { background:#7c3aed; color:#fff; }
  .co-option.selected .co-option-icon.stripe, .co-option:hover .co-option-icon.stripe { background:#635bff; color:#fff; }
  .co-option.selected .co-option-icon.netbilling, .co-option:hover .co-option-icon.netbilling { background:#004a99; color:#fff; }
  .co-option.selected .co-option-icon.cardflo, .co-option:hover .co-option-icon.cardflo { background:#0a85ea; color:#fff; }
  .co-option-text  { flex:1; min-width:0; }
  .co-option-title { font-size:14px; font-weight:700; color:var(--co-gray-900); line-height:1.2; margin-bottom:3px; }
  .co-option-desc  { font-size:12px; color:var(--co-gray-500); line-height:1.4; }
  .co-badge { font-size:11px; font-weight:700; padding:4px 10px; border-radius:20px; white-space:nowrap; flex-shrink:0; }
  .co-badge.fast     { background:rgba(16,185,129,0.10);  color:var(--co-success); }
  .co-badge.secure   { background:rgba(37,99,235,0.09);   color:var(--co-primary); }
  .co-badge.no-login { background:rgba(124,58,237,0.09);  color:#7c3aed; }
  .co-badge.stripe   { background:rgba(99,91,255,0.09);   color:#635bff; }
  .co-badge.netbilling { background:rgba(0,74,153,0.09);   color:#004a99; }
  .co-badge.cardflo { background:rgba(10,133,234,0.09);   color:#0a85ea; }

  /* NetBilling Form Styles */
  .nb-form-section { margin-bottom: 24px; }
  .nb-form-section-title { font-size: 15px; font-weight: 700; color: var(--co-gray-900); margin-bottom: 12px; border-bottom: 1px solid var(--co-gray-100); padding-bottom: 6px; display: flex; align-items: center; }
  .nb-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .nb-form-group { display: flex; flex-direction: column; margin-bottom: 10px; }
  .nb-form-group.col-span-2 { grid-column: span 2; }
  .nb-form-group label { font-size: 12px; font-weight: 600; color: var(--co-gray-700); margin-bottom: 4px; }
  .nb-form-group label .required { color: #ef4444; margin-left: 2px; }
  .nb-form-group input, .nb-form-group select { 
    padding: 10px 12px; 
    border: 1px solid #cbd5e1 !important; 
    border-radius: 8px; 
    font-size: 14px; 
    background: #ffffff !important;
    color: #1f2937 !important;
    transition: border-color 0.2s, box-shadow 0.2s;
    width: 100%;
    box-sizing: border-box;
    outline: none !important;
  }
  .nb-form-group input::placeholder {
    color: #9ca3af !important;
  }
  .nb-form-group input:focus { 
    border-color: #004a99 !important; 
    box-shadow: 0 0 0 2px rgba(0, 74, 153, 0.1);
    background: #ffffff !important;
  }
  .nb-form-group input.is-invalid { border-color: #ef4444; }
  .nb-form-group .error-msg { color: #ef4444; font-size: 11px; margin-top: 4px; font-weight: 500; }
  .nb-payment-summary { 
    background: #f1f5f9; 
    border-radius: 10px; 
    padding: 14px; 
    margin-bottom: 20px; 
    border: 1px solid #e2e8f0;
  }
  .nb-summary-row { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 6px; color: #334155; }
  .nb-summary-row span { font-weight: 600; color: #475569; }
  .nb-summary-row strong { color: #0f172a; }
  .nb-summary-total { font-weight: 700; color: #004a99; font-size: 16px; border-top: 1px dashed #cbd5e1; margin-top: 10px; padding-top: 10px; }
  .nb-summary-total strong { color: #004a99; }
  .co-btn { width:100%; padding:14px 20px; border:none; border-radius:10px; color:#fff; font-size:14px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:8px; transition:all .22s ease; margin-top:4px; }
  .co-btn:disabled { cursor:not-allowed; transform:none!important; }
  .co-btn:hover:not(:disabled) { transform:translateY(-2px); }
  .co-btn-blue   { background:var(--co-primary); box-shadow:0 6px 20px rgba(37,99,235,0.30); }
  .co-btn-blue:hover:not(:disabled)   { background:var(--co-primary-dark); box-shadow:0 8px 28px rgba(37,99,235,0.42); }
  .co-btn-blue:active:not(:disabled)  { background:var(--co-primary-active); }
  .co-btn-blue:disabled { background:var(--co-primary-disabled); }
  .co-btn-blue i { color:#fff!important; }
  .co-btn-green  { background:#15803D!important; box-shadow:0 6px 20px rgba(21,128,61,0.28); }
  .co-btn-green:hover:not(:disabled)  { background:#15803D!important; box-shadow:0 8px 28px rgba(21,128,61,0.42); }
  .co-btn-green:active:not(:disabled) { background:#15803D!important; box-shadow:0 4px 16px rgba(21,128,61,0.35); }
  .co-btn-green:disabled { background:#86EFAC!important; }
  .co-btn-green i { color:#fff!important; }
  .co-btn-grey   { background:#9e9e9e!important; box-shadow:none; }
  .co-btn-grey:hover:not(:disabled)   { background:#8e8e8e!important; box-shadow:none; }
  .co-btn-grey:active:not(:disabled)  { background:#7e7e7e!important; }
  .co-btn-grey:disabled { background:#c9c9c9!important; }
  .co-btn-grey i { color:#fff!important; }
  .co-btn-purple { background:linear-gradient(135deg,#7c3aed,#5b21b6); box-shadow:0 6px 20px rgba(124,58,237,0.28); }
  .co-btn-tron   { background:linear-gradient(135deg,#e8474e,#c0392b); box-shadow:0 6px 20px rgba(232,71,78,0.28); }
  .co-btn-tron:hover:not(:disabled) { box-shadow:0 8px 28px rgba(232,71,78,0.40); }
  .co-btn-stripe { background:linear-gradient(135deg,#635bff,#4b45c6); box-shadow:0 6px 20px rgba(99,91,255,0.30); }
  .co-btn-stripe:hover:not(:disabled) { box-shadow:0 8px 28px rgba(99,91,255,0.42); }
  .co-paypal-result-icon,.co-result-icon { width:72px; height:72px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; margin-bottom:20px; font-size:32px; }
  .co-paypal-result-icon.success,.co-result-icon.success { background:rgba(16,185,129,0.10); color:var(--co-success); }
  .co-paypal-result-icon.error,.co-result-icon.error     { background:rgba(239,68,68,0.10);  color:var(--co-danger); }
  .co-paypal-result-icon.cancel  { background:rgba(245,158,11,0.10); color:#f59e0b; }
  .co-paypal-result-title { font-size:20px; font-weight:700; margin:0 0 8px; color:var(--co-gray-900); }
  .co-paypal-result-msg   { font-size:14px; color:var(--co-gray-500); line-height:1.6; margin:0 0 20px; }
  .co-paypal-detail-row   { display:flex; justify-content:space-between; align-items:center; padding:10px 16px; margin-bottom:6px; border-radius:8px; background:var(--co-gray-100); font-size:13px; }
  .co-paypal-detail-label { color:var(--co-gray-500); font-weight:500; }
  .co-paypal-detail-value { color:var(--co-gray-900); font-weight:700; }
  @keyframes co-spin { to { transform:rotate(360deg); } }
  .co-spinner { width:15px; height:15px; border-radius:50%; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; animation:co-spin .75s linear infinite; display:inline-block; }
  .co-crypto-amt-input::-webkit-inner-spin-button,
  .co-crypto-amt-input::-webkit-outer-spin-button { -webkit-appearance:none; margin:0; }
  .co-crypto-amt-input[type=number] { -moz-appearance:textfield; }
  .co-crypto-amt-input { background:transparent !important; color:#2563EB !important; border:none !important; outline:none !important; box-shadow:none !important; -webkit-text-fill-color:#2563EB !important; }
  .co-crypto-amt-input--error { color:#ef4444 !important; -webkit-text-fill-color:#ef4444 !important; }
  .co-divider { height:1px; background:var(--co-gray-200); margin:18px 0; }
  .co-tbl-wrap { border:1px solid var(--co-gray-200); border-radius:12px; overflow-x:auto; overflow-y:visible; -webkit-overflow-scrolling:touch; margin-bottom:16px; width:100%; max-width:100%; direction:ltr; }
  .co-tbl { width:100%; border-collapse:collapse; font-size:13px; background:#fff!important; }
  .co-tbl thead th { background:var(--co-gray-100)!important; color:var(--co-gray-700)!important; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; padding:12px 16px; border:none!important; text-align:left; }
  .co-tbl thead th:nth-child(2) { text-align:center; }
  .co-tbl thead th:last-child { text-align:right; padding-right:24px!important; }
  .co-tbl tbody tr { background:#fff!important; }
  .co-tbl tbody td { padding:12px 16px; border-top:1px solid var(--co-gray-200)!important; color:var(--co-gray-700)!important; background:#fff!important; }
  .co-tbl tbody td:last-child { text-align:right; padding-right:24px!important; }
  .co-tbl tbody td:nth-child(2) { text-align:center; }
  .co-tbl tbody td.co-fee-label { color:var(--co-gray-500)!important; font-size:12px; }
  .co-tbl tfoot th { background:#2563EB!important; color:#fff!important; font-size:15px!important; font-weight:800!important; padding:16px 20px!important; border:none!important; }
  .co-tbl tfoot th:last-child { text-align:right; padding-right:24px!important; }
  .co-total-card { background:linear-gradient(135deg,rgba(37,99,235,0.07),rgba(30,64,175,0.03)); border:1px solid rgba(37,99,235,0.14); border-radius:12px; padding:24px; }
  .co-total-card h4 { font-size:13px; font-weight:800; text-transform:uppercase; letter-spacing:1px; color:var(--co-gray-700); margin:0 0 12px; }
  .co-total-val { font-size:48px!important; font-weight:700!important; display:block!important; margin-bottom:10px; line-height:1.1!important; background:linear-gradient(135deg,var(--co-primary),var(--co-primary-dark)); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; white-space:nowrap!important; }
  .co-total-note { font-size:12.5px; color:var(--co-gray-500); line-height:1.55; margin:0; }
  .co-skel { border-radius:6px; margin-bottom:8px; background:linear-gradient(90deg,var(--co-gray-100) 25%,#eaeef6 50%,var(--co-gray-100) 75%); background-size:200% 100%; animation:co-skel 1.4s ease infinite; }
  @keyframes co-skel { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .co-tx-status { margin-top:12px; padding:10px 14px; border-radius:8px; font-size:12px; font-weight:600; line-height:1.5; background:rgba(37,99,235,0.07); color:var(--co-primary); border:1px solid rgba(37,99,235,0.15); word-break:break-all; }
  .co-tx-status.confirmed { background:rgba(16,185,129,0.08); color:var(--co-success); border-color:rgba(16,185,129,0.2); }
  .co-modal-overlay { position:fixed; top:0; left:0; right:0; bottom:0; z-index:1; background:rgba(0,0,0,0.6); display:flex; align-items:center; justify-content:center; }
  .co-modal-box { position:relative; width:90vw; max-width:480px; background:#fff; border-radius:16px; overflow:hidden; box-shadow:0 25px 60px rgba(0,0,0,0.3); }
  .co-modal-header { display:flex; align-items:center; justify-content:space-between; padding:16px 24px; border-bottom:1px solid #e5e7eb; background:#f9fafb; }
  .co-modal-header h3 { margin:0; font-size:16px; font-weight:700; color:#1f2937; }
  .co-modal-close { background:none; border:none; font-size:22px; cursor:pointer; color:#6b7280; padding:4px 8px; border-radius:6px; line-height:1; }
  .co-modal-body { padding:24px; text-align:center; }
  .co-modal-body .co-modal-subtitle { font-size:13px; color:var(--co-gray-500); margin:0 0 20px; }
  .co-modal-row { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; margin-bottom:8px; border-radius:10px; border:1px solid var(--co-gray-200); background:#fff; }
  .co-modal-row-label { font-size:14px; color:var(--co-gray-700); }
  .co-modal-row-value { font-size:14px; font-weight:700; color:var(--co-gray-900); }
  .co-modal-err { font-size:13px; color:var(--co-danger); font-weight:600; margin-bottom:16px; }
  .co-stripe-sub-info { background:rgba(99,91,255,0.06); border:1px solid rgba(99,91,255,0.2); border-radius:10px; padding:14px 16px; margin-bottom:16px; text-align:left; }
  .co-stripe-sub-info p { margin:0 0 6px; font-size:12.5px; color:var(--co-gray-500); line-height:1.6; }
  .co-stripe-sub-info p:last-child { margin:0; }
  .co-discount-banner { background:#f0fdf4; border:1px solid #86efac; border-radius:8px; padding:8px 12px; margin-bottom:12px; font-size:12px; color:#166534; }
  .co-discount-banner strong { font-weight:700; }
  .co-discount-text { color:#10b981; font-weight:600; }
  .co-wallet-banner { background:#e0f2fe; border:1px solid #7dd3fc; border-radius:8px; padding:8px 12px; margin-bottom:12px; font-size:12px; color:#0c4a6e; display:flex; align-items:flex-start; gap:7px; }
  .co-wallet-banner strong { font-weight:700; color:#0c4a6e !important; }
  .co-wallet-banner span { color:#0c4a6e !important; }
  .co-wallet-banner span * { color:#0c4a6e !important; }
  .co-wallet-label { color:#0369a1; }
  .co-original-price { text-decoration:line-through; color:var(--co-gray-500); font-size:12px; }
`;

// ─── PayPal SVG icon ──────────────────────────────────────────────────────────
const PayPalIcon = ({ size = 22 }) => (
  <img src={PaybitoTitan} alt="PayBito Titan" style={{ width: size, height: "auto" }} />
);

// ─── NetBilling SVG icon ──────────────────────────────────────────────────────
const NetBillingIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="2" y="5" width="20" height="14" rx="2" fill="#004a99" />
    <path d="M2 10H22" stroke="white" strokeWidth="2" />
    <circle cx="18" cy="15" r="2" fill="white" />
  </svg>
);

// ─── Card flo SVG icon ──────────────────────────────────────────────────────
const CardFloIcon = ({ size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="2" y="5" width="20" height="14" rx="2" fill="#0a85ea" />
    <path d="M2 10H22" stroke="white" strokeWidth="2" />
    <circle cx="18" cy="15" r="2" fill="white" />
  </svg>
);

// ─── Utility functions for Card Validation ───────────────────────────────────
const validateLuhn = (number) => {
  let sum = 0;
  let shouldDouble = false;
  for (let i = number.length - 1; i >= 0; i--) {
    let digit = parseInt(number.charAt(i));
    if (shouldDouble) {
      if ((digit *= 2) > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
};

const getCardType = (number) => {
  const re = {
    visa: /^4/,
    mastercard: /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/,
    amex: /^3[47]/,
    discover: /^6(?:011|5)/,
    diners: /^3(?:0[0-5]|[68])/,
    jcb: /^(?:2131|1800|35)/,
  };
  for (let key in re) {
    if (re[key].test(number)) return key;
  }
  return null;
};

// ─── NetBilling Card Form ──────────────────────────────────────────────────
const NetBillingCardForm = ({
  orderId,
  amount,
  currency,
  currencySymbol,
  isSubscription,
  description,
  onSubmit,
  loading,
  countries = [],
}) => {
  const [formData, setFormData] = useState({
    cardNumber: "",
    cardExpiry: "",
    cvv: "",
    firstName: "",
    lastName: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const [errors, setErrors] = useState({});

  // ── Dynamic Location State ──
  const [allCountries] = useState(Country.getAllCountries());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [selectedStateIso, setSelectedStateIso] = useState("");
  
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const handleCountrySelect = (countryIso, countryName) => {
    const countryStates = State.getStatesOfCountry(countryIso);
    setFormData(prev => ({
      ...prev,
      country: countryName, // Full name for submission
      state: "",
      city: ""
    }));
    // Store ISO code in a ref or local variable if we need it for subsequent state/city calls
    // but here handleStateSelect will need the ISO. Let's store ISO in the form and use name for display, 
    // OR store name in form and keep ISO in a separate state.
    // Given the requirement "send as full name", we will store the name in formData.
    setSelectedCountryIso(countryIso); 

    setCountrySearchTerm(countryName);
    setStates(countryStates);
    setCities([]);
    setStateSearchTerm("");
    setCitySearchTerm("");
    setShowCountryDropdown(false);
  };

  const handleStateSelect = (stateIso, stateName) => {
    const stateCities = City.getCitiesOfState(selectedCountryIso, stateIso);
    setFormData(prev => ({
      ...prev,
      state: stateName, // Full name for submission
      city: ""
    }));
    setSelectedStateIso(stateIso);
    setStateSearchTerm(stateName);
    setCities(stateCities);
    setCitySearchTerm("");
    setShowStateDropdown(false);
  };

  const handleCitySelect = (cityName) => {
    setFormData(prev => ({ ...prev, city: cityName }));
    setCitySearchTerm(cityName);
    setShowCityDropdown(false);
  };

  const validate = () => {
    const newErrors = {};
    const sanitizedCard = formData.cardNumber.replace(/\s/g, "");

    if (!/^\d{13,19}$/.test(sanitizedCard)) {
      newErrors.cardNumber = "Invalid card number (13-19 digits)";
    } else if (!validateLuhn(sanitizedCard)) {
      newErrors.cardNumber = "Invalid card number (Luhn check failed)";
    } else {
      const type = getCardType(sanitizedCard);
      if (!type) {
        newErrors.cardNumber = "Unsupported card type";
      }
    }

    const expiryDigits = formData.cardExpiry.replace("/", "");
    if (!/^\d{4}$/.test(expiryDigits)) {
      newErrors.cardExpiry = "Invalid expiry (MM/YY)";
    } else {
      const month = parseInt(expiryDigits.substring(0, 2));
      if (month < 1 || month > 12) {
        newErrors.cardExpiry = "Invalid month (01-12)";
      }
    }

    if (!/^\d{3,4}$/.test(formData.cvv)) newErrors.cvv = "Invalid CVV (3-4 digits)";
    
    // First Name: Required, 2–50 characters, letters, spaces, hyphens (-), and apostrophes (')
    if (!/^[a-zA-Z\s\-\']{2,50}$/.test(formData.firstName.trim())) {
      newErrors.firstName = "First name is required (2-50 letters)";
    }

    // Last Name: Required, 2–50 characters, letters, spaces, hyphens (-), and apostrophes (')
    if (!/^[a-zA-Z\s\-\']{2,50}$/.test(formData.lastName.trim())) {
      newErrors.lastName = "Last name is required (2-50 letters)";
    }

    // Street Address: Required, 5–255 characters, letters, numbers, spaces, commas (,), periods (.), hyphens (-), and slashes (/)
    if (!/^[a-zA-Z0-9\s\,\.\-\/]{5,255}$/.test(formData.address.trim())) {
      newErrors.address = "Street address is required (5-255 characters)";
    }

    // ZIP/Postal Code: Required, 3–15 digits only
    if (!/^\d{3,15}$/.test(formData.zip.trim())) {
      newErrors.zip = "ZIP/Postal code is required (digits only)";
    }

    // Email is optional but must be valid if provided
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = "Invalid email";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;
    if (name === "cardExpiry") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      sanitizedValue = digits.length > 2 ? digits.slice(0, 2) + "/" + digits.slice(2) : digits;
    } else if (["cardNumber", "cvv"].includes(name)) {
      sanitizedValue = value.replace(/\D/g, "");
    } else if (name === "zip") {
      sanitizedValue = value.replace(/\D/g, "");
    } else if (["firstName", "lastName"].includes(name)) {
      sanitizedValue = value.replace(/[^a-zA-Z\s\-\']/g, "");
    } else if (name === "email") {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors((prev) => ({ ...prev, email: "Invalid email address" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ ...formData, cardExpiry: formData.cardExpiry.replace("/", "") });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="nb-card-form">
      <div className="nb-payment-summary">
        <div className="nb-summary-row">
          <span>Order ID:</span>
          <strong>{orderId}</strong>
        </div>
        <div className="nb-summary-row nb-summary-total">
          <span>Total Amount:</span>
          <strong>
            {currency} {parseFloat(amount || 0).toFixed(2)}
          </strong>
        </div>
      </div>

      <div className="nb-form-section">
        <div className="nb-form-section-title">
          <i className="fa fa-credit-card" style={{ marginRight: 8 }} />
          Card Information
        </div>
        <div className="nb-form-grid">
          <div className="nb-form-group col-span-2">
            <label>
              Card Number <span className="required">*</span>
            </label>
            <input
              name="cardNumber"
              autoComplete="one-time-code"
              value={formData.cardNumber}
              onChange={handleChange}
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              className={errors.cardNumber ? "is-invalid" : ""}
            />
            {errors.cardNumber && (
              <span className="error-msg">{errors.cardNumber}</span>
            )}
          </div>
          <div className="nb-form-group">
            <label>
              Expiry (MM/YY) <span className="required">*</span>
            </label>
            <input
              name="cardExpiry"
              autoComplete="one-time-code"
              value={formData.cardExpiry}
              onChange={handleChange}
              placeholder="MM/YY"
              maxLength="5"
              className={errors.cardExpiry ? "is-invalid" : ""}
            />
            {errors.cardExpiry && (
              <span className="error-msg">{errors.cardExpiry}</span>
            )}
          </div>
          <div className="nb-form-group">
            <label>
              CVV <span className="required">*</span>
            </label>
            <input
              name="cvv"
              type="password"
              autoComplete="one-time-code"
              value={formData.cvv}
              onChange={handleChange}
              placeholder="•••"
              maxLength="4"
              className={errors.cvv ? "is-invalid" : ""}
            />
            {errors.cvv && <span className="error-msg">{errors.cvv}</span>}
          </div>
        </div>
      </div>

      <div className="nb-form-section">
        <div className="nb-form-section-title">
          <i className="fa fa-user" style={{ marginRight: 8 }} />
          Cardholder Information
        </div>
        <div className="nb-form-grid">
          <div className="nb-form-group">
            <label>First Name <span className="required">*</span></label>
            <input
              name="firstName"
              autoComplete="one-time-code"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              className={errors.firstName ? "is-invalid" : ""}
            />
            {errors.firstName && (
              <span className="error-msg">{errors.firstName}</span>
            )}
          </div>
          <div className="nb-form-group">
            <label>Last Name <span className="required">*</span></label>
            <input
              name="lastName"
              autoComplete="one-time-code"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              className={errors.lastName ? "is-invalid" : ""}
            />
            {errors.lastName && (
              <span className="error-msg">{errors.lastName}</span>
            )}
          </div>
          <div className="nb-form-group col-span-2">
            <label>Email</label>
            <input
              name="email"
              type="email"
              autoComplete="one-time-code"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              className={errors.email ? "is-invalid" : ""}
            />
            {errors.email && (
              <span className="error-msg">{errors.email}</span>
            )}
          </div>
        </div>
      </div>

      <div className="nb-form-section">
        <div className="nb-form-section-title">
          <i className="fa fa-home" style={{ marginRight: 8 }} />
          Billing Address
        </div>
        <div className="nb-form-grid">
          <div className="nb-form-group col-span-2">
            <label>Street Address <span className="required">*</span></label>
            <input
              name="address"
              autoComplete="one-time-code"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main Street"
              className={errors.address ? "is-invalid" : ""}
            />
            {errors.address && (
              <span className="error-msg">{errors.address}</span>
            )}
          </div>
          
          {/* Country Selection */}
          <div className="nb-form-group col-span-2">
            <label>Country <span className="required">*</span></label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                autoComplete="one-time-code"
                placeholder="Search country"
                value={countrySearchTerm}
                onChange={(e) => {
                  setCountrySearchTerm(e.target.value);
                  setShowCountryDropdown(true);
                }}
                onFocus={() => setShowCountryDropdown(true)}
                onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
              />
              {showCountryDropdown && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  maxHeight: "200px", overflowY: "auto", background: "#ffffff",
                  border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: "2px"
                }}>
                  {allCountries.filter(c => c.name.toLowerCase().includes(countrySearchTerm.toLowerCase())).map(c => (
                    <div
                      key={c.isoCode}
                      style={{ 
                        padding: "10px 12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #f5f5f5",
                        color: "#333333",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                      onMouseDown={() => handleCountrySelect(c.isoCode, c.name)}
                      onMouseOver={(e) => {
                        e.target.style.background = "#f0f7ff";
                        e.target.style.color = "#000000";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#333333";
                      }}
                    >
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* State Selection */}
          <div className="nb-form-group">
            <label>State</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                autoComplete="one-time-code"
                placeholder="Search state"
                value={stateSearchTerm}
                onChange={(e) => {
                  setStateSearchTerm(e.target.value);
                  setShowStateDropdown(true);
                }}
                onFocus={() => setShowStateDropdown(true)}
                onBlur={() => setTimeout(() => setShowStateDropdown(false), 200)}
                disabled={!formData.country}
              />
              {showStateDropdown && states.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  maxHeight: "200px", overflowY: "auto", background: "#ffffff",
                  border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: "2px"
                }}>
                  {states.filter(s => s.name.toLowerCase().includes(stateSearchTerm.toLowerCase())).map(s => (
                    <div
                      key={s.isoCode}
                      style={{ 
                        padding: "10px 12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #f5f5f5",
                        color: "#333333",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                      onMouseDown={() => handleStateSelect(s.isoCode, s.name)}
                      onMouseOver={(e) => {
                        e.target.style.background = "#f0f7ff";
                        e.target.style.color = "#000000";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#333333";
                      }}
                    >
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* City Selection */}
          <div className="nb-form-group">
            <label>City</label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                autoComplete="one-time-code"
                placeholder="Search city"
                value={citySearchTerm}
                onChange={(e) => {
                  setCitySearchTerm(e.target.value);
                  setFormData(prev => ({ ...prev, city: e.target.value }));
                  setShowCityDropdown(true);
                }}
                onFocus={() => setShowCityDropdown(true)}
                onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                disabled={!formData.state}
              />
              {showCityDropdown && cities.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  maxHeight: "200px", overflowY: "auto", background: "#ffffff",
                  border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: "2px"
                }}>
                  {cities.filter(c => c.name.toLowerCase().includes(citySearchTerm.toLowerCase())).map(c => (
                    <div
                      key={c.name}
                      style={{ 
                        padding: "10px 12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #f5f5f5",
                        color: "#333333",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                      onMouseDown={() => handleCitySelect(c.name)}
                      onMouseOver={(e) => {
                        e.target.style.background = "#f0f7ff";
                        e.target.style.color = "#000000";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#333333";
                      }}
                    >
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="nb-form-group col-span-2">
            <label>Zip Code <span className="required">*</span></label>
            <input
              name="zip"
              autoComplete="one-time-code"
              value={formData.zip}
              onChange={handleChange}
              placeholder="10001"
              className={errors.zip ? "is-invalid" : ""}
            />
            {errors.zip && (
              <span className="error-msg">{errors.zip}</span>
            )}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="co-btn primary"
        style={{ width: "100%", height: 48 }}
      >
        {loading ? (
          <i className="fa fa-spinner fa-spin" />
        ) : isSubscription ? (
          <>
            <i className="fa fa-lock" />
            &nbsp;Subscribe with PayBito Vertex
          </>
        ) : (
          <>
            <i className="fa fa-lock" />
            &nbsp;{`Pay ${currencySymbol || currency} ${parseFloat(amount).toFixed(2)} with Card`}
          </>
        )}
      </button>
    </form>
  );
};

// ─── Card flo Card Form ──────────────────────────────────────────────────────
const CardFloCardForm = ({
  orderId,
  amount,
  currency,
  currencySymbol,
  isSubscription,
  description,
  onSubmit,
  loading,
  countries = [],
}) => {
  const [formData, setFormData] = useState({
    cardNumber: "",
    cardExpiry: "",
    cvv: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  });

  const [errors, setErrors] = useState({});

  // ── Dynamic Location State ──
  const [allCountries] = useState(Country.getAllCountries());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [selectedStateIso, setSelectedStateIso] = useState("");
  
  const [countrySearchTerm, setCountrySearchTerm] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const handleCountrySelect = (countryIso, countryName) => {
    const countryStates = State.getStatesOfCountry(countryIso);
    setFormData(prev => ({
      ...prev,
      country: countryIso, // ISO code for submission (e.g. "US")
      state: "",
      city: ""
    }));
    setSelectedCountryIso(countryIso); 

    setCountrySearchTerm(countryName);
    setStates(countryStates);
    setCities([]);
    setStateSearchTerm("");
    setCitySearchTerm("");
    setShowCountryDropdown(false);
  };

  const handleStateSelect = (stateIso, stateName) => {
    const stateCities = City.getCitiesOfState(selectedCountryIso, stateIso);
    setFormData(prev => ({
      ...prev,
      state: stateIso, // ISO code for submission (e.g. "WB")
      city: ""
    }));
    setSelectedStateIso(stateIso);
    setStateSearchTerm(stateName);
    setCities(stateCities);
    setCitySearchTerm("");
    setShowStateDropdown(false);
  };

  const handleCitySelect = (cityName) => {
    setFormData(prev => ({ ...prev, city: cityName }));
    setCitySearchTerm(cityName);
    setShowCityDropdown(false);
  };

  const validate = () => {
    const newErrors = {};
    const sanitizedCard = formData.cardNumber.replace(/\s/g, "");

    if (!/^\d{13,19}$/.test(sanitizedCard)) {
      newErrors.cardNumber = "Invalid card number (13-19 digits)";
    } else if (!validateLuhn(sanitizedCard)) {
      newErrors.cardNumber = "Invalid card number (Luhn check failed)";
    } else {
      const type = getCardType(sanitizedCard);
      if (!type) {
        newErrors.cardNumber = "Unsupported card type";
      }
    }

    const expiryDigits = formData.cardExpiry.replace("/", "");
    if (!/^\d{4}$/.test(expiryDigits)) {
      newErrors.cardExpiry = "Invalid expiry (MM/YY)";
    } else {
      const month = parseInt(expiryDigits.substring(0, 2));
      if (month < 1 || month > 12) {
        newErrors.cardExpiry = "Invalid month (01-12)";
      }
    }

    if (!/^\d{3,4}$/.test(formData.cvv)) newErrors.cvv = "Invalid CVV (3-4 digits)";
    
    // First Name: Required, 2–50 characters, letters, spaces, hyphens (-), and apostrophes (')
    if (!/^[a-zA-Z\s\-\']{2,50}$/.test(formData.firstName.trim())) {
      newErrors.firstName = "First name is required (2-50 letters)";
    }

    // Last Name: Required, 2–50 characters, letters, spaces, hyphens (-), and apostrophes (')
    if (!/^[a-zA-Z\s\-\']{2,50}$/.test(formData.lastName.trim())) {
      newErrors.lastName = "Last name is required (2-50 letters)";
    }

    // Street Address: Required, 5–255 characters, letters, numbers, spaces, commas (,), periods (.), hyphens (-), and slashes (/)
    if (!/^[a-zA-Z0-9\s\,\.\-\/]{5,255}$/.test(formData.address.trim())) {
      newErrors.address = "Street address is required (5-255 characters)";
    }

    // ZIP/Postal Code: Required, 3–15 digits only
    if (!/^\d{3,15}$/.test(formData.zip.trim())) {
      newErrors.zip = "ZIP/Postal code is required (digits only)";
    }

    // Email is required and must be valid
    if (!formData.email || !formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email is required";
    }

    // Phone is required
    if (!formData.phone || !/^\+?[0-9\s\-]{7,15}$/.test(formData.phone.trim())) {
      newErrors.phone = "Phone number is required (7-15 digits)";
    }

    // Country, State, City are all mandatory in Card flo
    if (!formData.country) {
      newErrors.country = "Country is required";
    }
    if (!formData.state) {
      newErrors.state = "State is required";
    }
    if (!formData.city) {
      newErrors.city = "City is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let sanitizedValue = value;
    if (name === "cardExpiry") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      sanitizedValue = digits.length > 2 ? digits.slice(0, 2) + "/" + digits.slice(2) : digits;
    } else if (["cardNumber", "cvv"].includes(name)) {
      sanitizedValue = value.replace(/\D/g, "");
    } else if (name === "zip") {
      sanitizedValue = value.replace(/\D/g, "");
    } else if (["firstName", "lastName"].includes(name)) {
      sanitizedValue = value.replace(/[^a-zA-Z\s\-\']/g, "");
    } else if (name === "phone") {
      sanitizedValue = value.replace(/[^0-9\+\-\s]/g, "");
    } else if (name === "email") {
      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setErrors((prev) => ({ ...prev, email: "Invalid email address" }));
      } else {
        setErrors((prev) => ({ ...prev, email: "" }));
      }
    }
    setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ ...formData, cardExpiry: formData.cardExpiry.replace("/", "") });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="nb-card-form">
      <div className="nb-payment-summary">
        <div className="nb-summary-row">
          <span>Order ID:</span>
          <strong>{orderId}</strong>
        </div>
        <div className="nb-summary-row nb-summary-total">
          <span>Total Amount:</span>
          <strong>
            {currency} {parseFloat(amount || 0).toFixed(2)}
          </strong>
        </div>
      </div>

      <div className="nb-form-section">
        <div className="nb-form-section-title">
          <i className="fa fa-credit-card" style={{ marginRight: 8 }} />
          Card Information
        </div>
        <div className="nb-form-grid">
          <div className="nb-form-group col-span-2">
            <label>
              Card Number <span className="required">*</span>
            </label>
            <input
              name="cardNumber"
              autoComplete="one-time-code"
              value={formData.cardNumber}
              onChange={handleChange}
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              className={errors.cardNumber ? "is-invalid" : ""}
            />
            {errors.cardNumber && (
              <span className="error-msg">{errors.cardNumber}</span>
            )}
          </div>
          <div className="nb-form-group">
            <label>
              Expiry (MM/YY) <span className="required">*</span>
            </label>
            <input
              name="cardExpiry"
              autoComplete="one-time-code"
              value={formData.cardExpiry}
              onChange={handleChange}
              placeholder="MM/YY"
              maxLength="5"
              className={errors.cardExpiry ? "is-invalid" : ""}
            />
            {errors.cardExpiry && (
              <span className="error-msg">{errors.cardExpiry}</span>
            )}
          </div>
          <div className="nb-form-group">
            <label>
              CVV <span className="required">*</span>
            </label>
            <input
              name="cvv"
              type="password"
              autoComplete="one-time-code"
              value={formData.cvv}
              onChange={handleChange}
              placeholder="•••"
              maxLength="4"
              className={errors.cvv ? "is-invalid" : ""}
            />
            {errors.cvv && <span className="error-msg">{errors.cvv}</span>}
          </div>
        </div>
      </div>

      <div className="nb-form-section">
        <div className="nb-form-section-title">
          <i className="fa fa-user" style={{ marginRight: 8 }} />
          Cardholder Information
        </div>
        <div className="nb-form-grid">
          <div className="nb-form-group">
            <label>First Name <span className="required">*</span></label>
            <input
              name="firstName"
              autoComplete="one-time-code"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
              className={errors.firstName ? "is-invalid" : ""}
            />
            {errors.firstName && (
              <span className="error-msg">{errors.firstName}</span>
            )}
          </div>
          <div className="nb-form-group">
            <label>Last Name <span className="required">*</span></label>
            <input
              name="lastName"
              autoComplete="one-time-code"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
              className={errors.lastName ? "is-invalid" : ""}
            />
            {errors.lastName && (
              <span className="error-msg">{errors.lastName}</span>
            )}
          </div>
          <div className="nb-form-group col-span-2">
            <label>Email <span className="required">*</span></label>
            <input
              name="email"
              type="email"
              autoComplete="one-time-code"
              value={formData.email}
              onChange={handleChange}
              placeholder="john@example.com"
              className={errors.email ? "is-invalid" : ""}
            />
            {errors.email && (
              <span className="error-msg">{errors.email}</span>
            )}
          </div>
          <div className="nb-form-group col-span-2">
            <label>Phone Number <span className="required">*</span></label>
            <input
              name="phone"
              type="text"
              autoComplete="one-time-code"
              value={formData.phone}
              onChange={handleChange}
              placeholder="9876543210"
              className={errors.phone ? "is-invalid" : ""}
            />
            {errors.phone && (
              <span className="error-msg">{errors.phone}</span>
            )}
          </div>
        </div>
      </div>

      <div className="nb-form-section">
        <div className="nb-form-section-title">
          <i className="fa fa-home" style={{ marginRight: 8 }} />
          Billing Address
        </div>
        <div className="nb-form-grid">
          <div className="nb-form-group col-span-2">
            <label>Street Address <span className="required">*</span></label>
            <input
              name="address"
              autoComplete="one-time-code"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 Main Street"
              className={errors.address ? "is-invalid" : ""}
            />
            {errors.address && (
              <span className="error-msg">{errors.address}</span>
            )}
          </div>
          
          {/* Country Selection */}
          <div className="nb-form-group col-span-2">
            <label>Country <span className="required">*</span></label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                autoComplete="one-time-code"
                placeholder="Search country"
                value={countrySearchTerm}
                onChange={(e) => {
                  setCountrySearchTerm(e.target.value);
                  setShowCountryDropdown(true);
                }}
                onFocus={() => setShowCountryDropdown(true)}
                onBlur={() => setTimeout(() => setShowCountryDropdown(false), 200)}
                className={errors.country ? "is-invalid" : ""}
              />
              {errors.country && (
                <span className="error-msg">{errors.country}</span>
              )}
              {showCountryDropdown && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  maxHeight: "200px", overflowY: "auto", background: "#ffffff",
                  border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: "2px"
                }}>
                  {allCountries.filter(c => c.name.toLowerCase().includes(countrySearchTerm.toLowerCase())).map(c => (
                    <div
                      key={c.isoCode}
                      style={{ 
                        padding: "10px 12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #f5f5f5",
                        color: "#333333",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                      onMouseDown={() => handleCountrySelect(c.isoCode, c.name)}
                      onMouseOver={(e) => {
                        e.target.style.background = "#f0f7ff";
                        e.target.style.color = "#000000";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#333333";
                      }}
                    >
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* State Selection */}
          <div className="nb-form-group">
            <label>State <span className="required">*</span></label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                autoComplete="one-time-code"
                placeholder="Search state"
                value={stateSearchTerm}
                onChange={(e) => {
                  setStateSearchTerm(e.target.value);
                  setShowStateDropdown(true);
                }}
                onFocus={() => setShowStateDropdown(true)}
                onBlur={() => setTimeout(() => setShowStateDropdown(false), 200)}
                disabled={!formData.country}
                className={errors.state ? "is-invalid" : ""}
              />
              {errors.state && (
                <span className="error-msg">{errors.state}</span>
              )}
              {showStateDropdown && states.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  maxHeight: "200px", overflowY: "auto", background: "#ffffff",
                  border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: "2px"
                }}>
                  {states.filter(s => s.name.toLowerCase().includes(stateSearchTerm.toLowerCase())).map(s => (
                    <div
                      key={s.isoCode}
                      style={{ 
                        padding: "10px 12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #f5f5f5",
                        color: "#333333",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                      onMouseDown={() => handleStateSelect(s.isoCode, s.name)}
                      onMouseOver={(e) => {
                        e.target.style.background = "#f0f7ff";
                        e.target.style.color = "#000000";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#333333";
                      }}
                    >
                      {s.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* City Selection */}
          <div className="nb-form-group">
            <label>City <span className="required">*</span></label>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                autoComplete="one-time-code"
                placeholder="Search city"
                value={citySearchTerm}
                onChange={(e) => {
                  setCitySearchTerm(e.target.value);
                  setFormData(prev => ({ ...prev, city: e.target.value }));
                  setShowCityDropdown(true);
                }}
                onFocus={() => setShowCityDropdown(true)}
                onBlur={() => setTimeout(() => setShowCityDropdown(false), 200)}
                disabled={!formData.state}
                className={errors.city ? "is-invalid" : ""}
              />
              {errors.city && (
                <span className="error-msg">{errors.city}</span>
              )}
              {showCityDropdown && cities.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0,
                  maxHeight: "200px", overflowY: "auto", background: "#ffffff",
                  border: "1px solid #ccc", borderRadius: "4px", zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)", marginTop: "2px"
                }}>
                  {cities.filter(c => c.name.toLowerCase().includes(citySearchTerm.toLowerCase())).map(c => (
                    <div
                      key={c.name}
                      style={{ 
                        padding: "10px 12px", 
                        cursor: "pointer", 
                        borderBottom: "1px solid #f5f5f5",
                        color: "#333333",
                        fontSize: "14px",
                        fontWeight: "500"
                      }}
                      onMouseDown={() => handleCitySelect(c.name)}
                      onMouseOver={(e) => {
                        e.target.style.background = "#f0f7ff";
                        e.target.style.color = "#000000";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#333333";
                      }}
                    >
                      {c.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="nb-form-group col-span-2">
            <label>Zip Code <span className="required">*</span></label>
            <input
              name="zip"
              autoComplete="one-time-code"
              value={formData.zip}
              onChange={handleChange}
              placeholder="10001"
              className={errors.zip ? "is-invalid" : ""}
            />
            {errors.zip && (
              <span className="error-msg">{errors.zip}</span>
            )}
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="co-btn primary"
        style={{ width: "100%", height: 48 }}
      >
        {loading ? (
          <i className="fa fa-spinner fa-spin" />
        ) : isSubscription ? (
          <>
            <i className="fa fa-lock" />
            &nbsp;Subscribe with PayBito Sovereign
          </>
        ) : (
          <>
            <i className="fa fa-lock" />
            &nbsp;{`Pay ${currencySymbol || currency} ${amount} with Card`}
          </>
        )}
      </button>
    </form>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CheckoutPaymentOptions = () => {
  const NETWORK_MODE = isTestMode ? "TESTNET" : "MAINNET";

  // ─── Active addresses based on NETWORK_MODE ───────────────────────────────────
  const ETH_MERCHANT_ADDRESS =
    NETWORK_MODE === "MAINNET"
      ? MAINNET_ETH_MERCHANT_ADDRESS
      : TESTNET_ETH_MERCHANT_ADDRESS;
  const ERC20_USDT_ADDRESS =
    NETWORK_MODE === "MAINNET"
      ? MAINNET_ERC20_USDT_ADDRESS
      : TESTNET_ERC20_USDT_ADDRESS;
  const ERC20_USDC_ADDRESS =
    NETWORK_MODE === "MAINNET"
      ? MAINNET_ERC20_USDC_ADDRESS
      : TESTNET_ERC20_USDC_ADDRESS;
  const TRON_MERCHANT_ADDRESS =
    NETWORK_MODE === "MAINNET"
      ? MAINNET_TRON_MERCHANT_ADDRESS
      : TESTNET_TRON_MERCHANT_ADDRESS;
  const TRC20_USDT_ADDRESS =
    NETWORK_MODE === "MAINNET"
      ? MAINNET_TRC20_USDT_ADDRESS
      : TESTNET_TRC20_USDT_ADDRESS;

  const [invoiceData, setInvoiceData] = useState(null);
  const [paypalSubscriptionId, setPaypalSubscriptionId] = useState("");
  const [rawDataParam, setRawDataParam] = useState("");
  const [decodedDataParam, setDecodedDataParam] = useState(null);
  const [companyName, setCompanyName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_companyName") || "PayBito"
  );
  const [autoConnectAttempted, setAutoConnectAttempted] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [convertedAmt, setConvertedAmt] = useState(null);
  const [totalAmt, setTotalAmt] = useState(null);
  const [feeAmt, setFeeAmt] = useState(null);
  const [feePct, setFeePct] = useState(null);
  const [grandTotal, setGrandTotal] = useState(null);
  const [merchantName, setMerchantName] = useState("");
  const [merchantAddr, setMerchantAddr] = useState("");
  const [exchangeName, setExchangeName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_exchange") || ""
  );
  const [urlForSubmit, setUrlForSubmit] = useState("");
  const [hostedCatalogId, setHostedCatalogId] = useState(0);
  const [emailBillId, setEmailBillId] = useState(0);
  const [catalogItem, setCatalogItem] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [billInfo, setBillInfo] = useState(null);
  const [hfFeeApplied, setHfFeeApplied] = useState("0");
  const [hfFeePct, setHfFeePct] = useState("0");
  const [hfFeeAmt, setHfFeeAmt] = useState("0");
  const [selectedOpt, setSelectedOpt] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [paymentExpired, setPaymentExpired] = useState(false);
  const [connectedWallet, setConnectedWallet] = useState(null);
  const [externalConnecting, setExternalConnecting] = useState(false);
  const [paymentType, setPaymentType] = useState("eth");
  const [ethProvider, setEthProvider] = useState(null);
  const [ethSigner, setEthSigner] = useState(null);
  const [txStatus, setTxStatus] = useState("");
  const [txHash, setTxHash] = useState("");
  const [txConfirmed, setTxConfirmed] = useState(false);
  const [exchangeWalletExists, setExchangeWalletExists] = useState(false);
  const [externalWalletExists, setExternalWalletExists] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState("");
  const [paypalOrderId, setPaypalOrderId] = useState("");
  const [paypalResultModal, setPaypalResultModal] = useState(null);
  const paymentIntentCache = useRef({});
  const externalWalletIntentRef = useRef(null);
  const externalWalletChargeRef = useRef(null);
  const captchaRef = useRef(null);
  const signupCaptchaRef = useRef(null);
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripePaymentIntentId, setStripePaymentIntentId] = useState("");
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const [stripeResultModal, setStripeResultModal] = useState(null);
  // Subscription-specific Stripe state
  const [stripeSubRedirecting, setStripeSubRedirecting] = useState(false);

  // ── KurvPay state ─────────────────────────────────────────────────────────
  const [kurvTokenizationKey, setKurvTokenizationKey] = useState("");
  const [kurvProcessing, setKurvProcessing] = useState(false);
  const [kurvGatewayPlanId, setKurvGatewayPlanId] = useState("");
  // ── HMS state ───────────────────────────────────────────────────────────
const [hmsTokenizationKey, setHmsTokenizationKey] = useState(HMS_TOKENIZATION_KEY);
const [hmsProcessing, setHmsProcessing] = useState(false);
const [hmsGatewayPlanId, setHmsGatewayPlanId] = useState("");
  // ── NMI state ───────────────────────────────────────────────────────────
  const [nmiTokenizationKey, setNmiTokenizationKey] = useState("");
  const [nmiProcessing, setNmiProcessing] = useState(false);
  const [nmiGatewayPlanId, setNmiGatewayPlanId] = useState("");
  // Increment on every Zenith open so React fully unmounts/remounts NMIPaymentForm,
  // giving CollectJS a clean initialization identical to a fresh page load.
  const [nmiFormKey, setNmiFormKey] = useState(0);

  // ── NetBilling state ──────────────────────────────────────────────────────
  const [netbillingProcessing, setNetbillingProcessing] = useState(false);
  const [countries, setCountries] = useState([]);

  // ── Card flo state ────────────────────────────────────────────────────────
  const [cardfloProcessing, setCardfloProcessing] = useState(false);

  useEffect(() => {
    getCountries()
      .then((res) => {
        if (res.data?.error?.error_data === 0) {
          const sorted = (res.data.countries || []).sort((a, b) =>
            a.country.localeCompare(b.country)
          );
          setCountries(sorted);
        }
      })
      .catch(() => {});
  }, []);

  const handleNetBillingSubmit = async (cardData) => {
    setNetbillingProcessing(true);
    try {
      const amount = String(orderGrandTotal || grandTotal || 0);
      const currency = detectedCurrency?.code || "USD";

      // Compute per-line-item breakdown amounts (mirrors KurvPay/Stripe logic)
      const _sp = shippingProfile || {};
      const _hPct = parseFloat(_sp.handlingFeeValue || 0);
      const _tPct = parseFloat(_sp.taxRate || 0);
      const _getBase = (p) => {
        const priceObj = p.prices?.[0];
        const ce =
          priceObj?.currencies?.find(
            (c) => c.currency === detectedCurrency?.code
          ) ||
          priceObj?.currencies?.find((c) => c.default === true) ||
          priceObj?.currencies?.[0];
        return parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0);
      };
      const _products = Array.isArray(orderProducts) ? orderProducts : [];
      const fiatSubtotal = parseFloat(
        _products.reduce((acc, p) => acc + _getBase(p), 0).toFixed(2)
      );
      const fiatShippingAmt = parseFloat(
        _products.reduce((acc, p) => acc + (_getBase(p) * _hPct) / 100, 0).toFixed(2)
      );
      const fiatTaxAmt = parseFloat(
        _products.reduce((acc, p) => acc + (_getBase(p) * _tPct) / 100, 0).toFixed(2)
      );
      let _discount = 0;
      try {
        _discount = discountAmount || 0;
      } catch (e) {
        _discount = 0;
      }
      const fiatDiscountAmt = parseFloat(_discount || 0);
      const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

      const payload = {
        paymentIntentId: paymentIntentId,
        amount: amount,
        currency: currency,
        marketPrice: "0",
        blockchain: "",
        gatewayTransactionId: "",
        walletProvider: "",
        walletAddress: "",
        network: "NATIVE",
        baseAmount: String(fiatSubtotal),
        taxAmount: String(fiatTaxAmt),
        discountAmount: String(fiatDiscountAmt),
        shippingAmount: String(fiatShippingAmt),
        processingFeeAmount: String(fiatProcessingFeeAmt),
        ...cardData,
        description: "Invoice Payment",
        paymentMethodId: 8,
      };

      console.log("[NetBilling] createCharge payload:", payload);

      const response = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status) {
        showToast("success", "Payment processed successfully!");
        if (result.redirectUrl && result.redirectUrl.includes("/success")) {
          window.location.href = buildRedirectUrl(result.redirectUrl.split("?")[0], paymentIntentId, "success", loginEmail, amount, currency, redirectAdvanceOptions);
        } else if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
        } else {
          // Fallback redirect
          const transactionId = paymentIntentId;
          const red = buildRedirectUrl(
            redirectSuccessUrl ||
              `${window.location.origin}/merchant/checkout/success`,
            transactionId,
            "success",
            loginEmail,
            amount,
            currency,
            redirectAdvanceOptions
          );
          window.location.href = red;
        }
      } else {
        showToast(
          "error",
          result.message || "Payment failed. Please try again."
        );
      }
    } catch (err) {
      console.error("NetBilling Error:", err);
      showToast("error", "An error occurred while processing payment.");
    } finally {
      setNetbillingProcessing(false);
    }
  };

  const handleCardFloSubmit = async (cardData) => {
    setCardfloProcessing(true);
    try {
      const amount = String(orderGrandTotal || grandTotal || 0);
      const currency = detectedCurrency?.code || "USD";

      // Compute per-line-item breakdown amounts (mirrors NetBilling/KurvPay/Stripe logic)
      const _sp = shippingProfile || {};
      const _hPct = parseFloat(_sp.handlingFeeValue || 0);
      const _tPct = parseFloat(_sp.taxRate || 0);
      const _getBase = (p) => {
        const priceObj = p.prices?.[0];
        const ce =
          priceObj?.currencies?.find(
            (c) => c.currency === detectedCurrency?.code
          ) ||
          priceObj?.currencies?.find((c) => c.default === true) ||
          priceObj?.currencies?.[0];
        return parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0);
      };
      const _products = Array.isArray(orderProducts) ? orderProducts : [];
      const fiatSubtotal = parseFloat(
        _products.reduce((acc, p) => acc + _getBase(p), 0).toFixed(2)
      );
      const fiatShippingAmt = parseFloat(
        _products.reduce((acc, p) => acc + (_getBase(p) * _hPct) / 100, 0).toFixed(2)
      );
      const fiatTaxAmt = parseFloat(
        _products.reduce((acc, p) => acc + (_getBase(p) * _tPct) / 100, 0).toFixed(2)
      );
      let _discount = 0;
      try {
        _discount = discountAmount || 0;
      } catch (e) {
        _discount = 0;
      }
      const fiatDiscountAmt = parseFloat(_discount || 0);
      const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

      const payload = {
        currency: currency,
        country: cardData.country,
        amount: Number(amount),

        firstName: cardData.firstName,
        lastName: cardData.lastName,
        email: cardData.email,
        phone: cardData.phone,
        address: cardData.address,
        city: cardData.city,
        state: cardData.state,
        zip: cardData.zip,

        cardNumber: cardData.cardNumber,
        cvv: cardData.cvv,
        cardExpiry: String(cardData.cardExpiry),

        ipAddress: loginDeviceDetails?.ip || "124.0.0.1",

        payload: paymentIntentId,
        reconciliationId: paymentIntentId,

        initiatedBy: "customer",
        merchantId: Number(invoiceData?.merchant_id || invoiceData?.merchantId || decodedDataParam?.merchant_id || decodedDataParam?.merchantId || localStorage.getItem(SITENAMEALIAS + "_merchant_id") || localStorage.getItem(SITENAMEALIAS + "_merchantId") || 1),
        paymentIntentId: paymentIntentId,
        marketPrice: "0",
        gatewayTransactionId: "",
        walletProvider: "",
        walletAddress: "",
        blockchain: "",
        network: "NATIVE",

        baseAmount: Number(fiatSubtotal),
        taxAmount: Number(fiatTaxAmt),
        discountAmount: Number(fiatDiscountAmt),
        shippingAmount: Number(fiatShippingAmt),
        processingFeeAmount: Number(fiatProcessingFeeAmt),

        save_card: false,
        credit_card_token: "",
        encrypted_cvv: "",

        paymentMethodId: 10,
        description: "Invoice Payment"
      };

      console.log("[Card flo] createCharge payload:", payload);

      const response = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.status) {
        showToast("success", "Payment processed successfully via PayBito Sovereign!");
        if (result.data?.gatewayUrl) {
          window.location.href = result.data.gatewayUrl;
        } else if (result.redirectUrl && result.redirectUrl.includes("/success")) {
          window.location.href = buildRedirectUrl(result.redirectUrl.split("?")[0], paymentIntentId, "success", loginEmail, amount, currency, redirectAdvanceOptions);
        } else if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
        } else {
          // Fallback redirect
          const transactionId = paymentIntentId;
          const red = buildRedirectUrl(
            redirectSuccessUrl ||
              `${window.location.origin}/merchant/checkout/success`,
            transactionId,
            "success",
            loginEmail,
            amount,
            currency,
            redirectAdvanceOptions
          );
          window.location.href = red;
        }
      } else {
        showToast(
          "error",
          result.message || "Payment failed. Please try again."
        );
      }
    } catch (err) {
      console.error("Card flo Error:", err);
      showToast("error", "An error occurred while processing payment.");
    } finally {
      setCardfloProcessing(false);
    }
  };

  // Add this near your other refs at the top of the component
  const [payLoading, setPayLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [paybitoToken, setPaybitoToken] = useState("");
  const [paybitoUserId, setPaybitoUserId] = useState("");
  const [paybitoUuid, setPaybitoUuid] = useState("");
  const [depositAddress, setDepositAddress] = useState("");
  const [depositTag, setDepositTag] = useState(null);
  const [depositLoading, setDepositLoading] = useState(false);
  const [showReceiveQR, setShowReceiveQR] = useState(false);
  const depositBalancePollRef = React.useRef(null);
  const moonPayBalanceIntervalRef = React.useRef(null);
  const pabtekPollingRef = React.useRef(null);
  const [showPabtekPopup, setShowPabtekPopup] = useState(false);
  const [pabtekCurrencyCode, setPabtekCurrencyCode] = useState("");
  const [pabtekCurrencyId, setPabtekCurrencyId] = useState(null);
  const [pabtekSelectedNetwork, setPabtekSelectedNetwork] = useState("");
  const [pabtekPollingError, setPabtekPollingError] = useState("");
  const [pabtekDirectPolling, setPabtekDirectPolling] = useState(false);
  const [pabtekDirectLoading, setPabtekDirectLoading] = useState(false);
  const stompClientRef = React.useRef(null);
  const balanceMapRef = React.useRef({});
  const selectedCryptoRef = React.useRef("");
  const loginPollRef = React.useRef(null);
  const [showLoginIframe, setShowLoginIframe] = useState(false);
  const [loginIframeUrl, setLoginIframeUrl] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginPasswordVisible, setLoginPasswordVisible] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");
  const [phoneOtp, setPhoneOtp] = useState("");
  const [gaOtp, setGaOtp] = useState("");
  const [isGoogleAuthEnable, setIsGoogleAuthEnable] = useState(false);
  const [isPhoneVerificationEnable, setIsPhoneVerificationEnable] =
    useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [smsOtpCountdown, setSmsOtpCountdown] = useState(0);
  const otpEndTimeRef = useRef(Date.now());
  const otpTimerRef = useRef(null);
  const smsOtpEndTimeRef = useRef(Date.now());
  const smsOtpTimerRef = useRef(null);
  const [loginUserPhone, setLoginUserPhone] = useState("");
  const [loginUserCountryCode, setLoginUserCountryCode] = useState("");
  const [loginDeviceDetails, setLoginDeviceDetails] = useState(null);
  const [brandWalletAvaliability, setBrandWalletAvaliability] = useState("0");
  const [trailAvaliability, setTrailAvaliability] = useState("0");
  const [exchangeBrokerId, setExchangeBrokerId] = useState("");
  const [isZeroPayment, setIsZeroPayment] = useState(false);
  const [zeroPayLoading, setZeroPayLoading] = useState(false);

  // ── Signup modal state ─────────────────────────────────────────────────────
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupFirstName, setSignupFirstName] = useState("");
  const [signupLastName, setSignupLastName] = useState("");
  const [signupMiddleName, setSignupMiddleName] = useState("");
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupRetypePassword, setSignupRetypePassword] = useState("");
  const [signupGender, setSignupGender] = useState("Male");
  const [signupPasswordVisible, setSignupPasswordVisible] = useState(false);
  const [signupRetypePasswordVisible, setSignupRetypePasswordVisible] =
    useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupErrors, setSignupErrors] = useState({});
  const [signupCountryList, setSignupCountryList] = useState([]);
  const [signupSelectedCountry, setSignupSelectedCountry] = useState(null);
  const [signupCountryOpen, setSignupCountryOpen] = useState(false);
  const [signupCountrySearch, setSignupCountrySearch] = useState("");
  const [signupFilteredCountries, setSignupFilteredCountries] = useState([]);
  const [showSignupCaptchaModal, setShowSignupCaptchaModal] = useState(false);
  const [signupDeviceDetails, setSignupDeviceDetails] = useState(null);
  // Registration OTP state (after signup)
  const [showRegOtpModal, setShowRegOtpModal] = useState(false);
  const [regEmailOtp, setRegEmailOtp] = useState("");
  const [regPhoneOtp, setRegPhoneOtp] = useState("");
  const [regShowPhoneOtp, setRegShowPhoneOtp] = useState(false);
  const [regOtpCountdown, setRegOtpCountdown] = useState(300);
  const [regOtpLoading, setRegOtpLoading] = useState(false);
  const regOtpTimerRef = useRef(null);

  // ── Right panel state ─────────────────────────────────────────────────────
  const [detectedCurrency, setDetectedCurrency] = useState({
    code: "USD",
    symbol: "$",
  });
  const [fxRate, setFxRate] = useState(1);
  const [orderLoading, setOrderLoading] = useState(true);
  const [billingProfile, setBillingProfile] = useState(null);
  const [shippingProfile, setShippingProfile] = useState(null);
  const [orderProducts, setOrderProducts] = useState([]);
  const [orderSubtotal, setOrderSubtotal] = useState(0);
  const [orderGrandTotal, setOrderGrandTotal] = useState(0);
  const [orderFeeApplied, setOrderFeeApplied] = useState(false);
  const [orderFeePercent, setOrderFeePercent] = useState(0);
  const [orderFeeAmount, setOrderFeeAmount] = useState(0);
  const [orderFeeLoading, setOrderFeeLoading] = useState(false);
  const profileFetchSeqRef = React.useRef(0);
  // Ref that always holds the latest detectedCurrency so async fetch callbacks
  // can read the current value without stale-closure issues and without needing
  // detectedCurrency in the effect dependency array (which caused recursive re-fetches).
  const detectedCurrencyRef = React.useRef(detectedCurrency);

  // ── Wallet redeem state ───────────────────────────────────────────────────
  const [redeemAmount, setRedeemAmount] = useState(0);

  // ── Discount state ────────────────────────────────────────────────────────
  const [discounts, setDiscounts] = useState([]);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountSubtotal, setDiscountSubtotal] = useState(0);

  // ── Redirect URLs state ───────────────────────────────────────────────────
  const [redirectSuccessUrl, setRedirectSuccessUrl] = useState(null);
  const [redirectFailureUrl, setRedirectFailureUrl] = useState(null);
  const [redirectAdvanceOptions, setRedirectAdvanceOptions] = useState([]);

  // ── Crypto state ──────────────────────────────────────────────────────────
  const [cryptoExpanded, setCryptoExpanded] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [cryptoMarketPrice, setCryptoMarketPrice] = useState(null);
  const [cryptoConvertedAmt, setCryptoConvertedAmt] = useState(null);
  const cryptoConvertedAmtRef = useRef(null);
  const [cryptoPriceLoading, setCryptoPriceLoading] = useState(false);
  const [minCryptoAmt, setMinCryptoAmt] = useState(null);
  const [cryptoAmtInputError, setCryptoAmtInputError] = useState("");

  // ── Payment intent state ──────────────────────────────────────────────────
  const [paymentIntentLoading, setPaymentIntentLoading] = useState(false);
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [stripePaymentUrl, setStripePaymentUrl] = useState("");
  const [stripeSessionId, setStripeSessionId] = useState("");
  const [stripePaymentIntentSecret, setStripePaymentIntentSecret] =
    useState("");

  // ── EIP-6963 multi-wallet state ───────────────────────────────────────────
  const [detectedEVMWallets, setDetectedEVMWallets] = useState([]);
  const [showEVMWalletPicker, setShowEVMWalletPicker] = useState(false);

  // Add this state if not already present
  const [currentNetwork, setCurrentNetwork] = useState("");
  const [convertBaseAmount, setConvertBaseAmount] = useState(null);
  const [convertTaxAmount, setConvertTaxAmount] = useState(null);
  const [convertDiscountAmountValue, setConvertDiscountAmountValue] =
    useState(null);
  const [convertShippingAmount, setConvertShippingAmount] = useState(null);
  const [convertProcessingFeeAmount, setConvertProcessingFeeAmount] =
    useState(null);

  // Add this useEffect to check network status
  useEffect(() => {
    const checkNetwork = async () => {
      if (!window.ethereum) return;

      try {
        const chainId = await window.ethereum.request({
          method: "eth_chainId",
        });
        if (NETWORK_MODE === "TESTNET") {
          if (chainId === NETWORK_CONFIG.TESTNET.chainId) {
            setCurrentNetwork("Sepolia ✓");
          } else {
            setCurrentNetwork("Wrong Network - Switch to Sepolia");
          }
        } else {
          // For MAINNET, show the current network
          if (chainId === "0x1") {
            setCurrentNetwork("Ethereum Mainnet ✓");
          } else if (chainId === "0xaa36a7") {
            setCurrentNetwork("Sepolia Testnet (⚠️ Testnet)");
          } else {
            setCurrentNetwork(`Network: ${chainId}`);
          }
        }
      } catch (error) {
        setCurrentNetwork("");
      }
    };

    checkNetwork();

    // Listen for network changes
    if (window.ethereum) {
      window.ethereum.on("chainChanged", checkNetwork);
      return () => {
        window.ethereum.removeListener("chainChanged", checkNetwork);
      };
    }
  }, []);

  // ── Discount Calculation Function ─────────────────────────────────────────
  const calculateDiscount = (subtotalValue, discountsList) => {
    if (!discountsList || discountsList.length === 0) {
      return {
        appliedDiscount: null,
        discountAmount: 0,
        discountedSubtotal: subtotalValue,
      };
    }

    // Find the best discount (highest amount that meets minimum cart value)
    let bestDiscount = null;
    let bestDiscountAmount = 0;

    for (const discount of discountsList) {
      const minValue = parseFloat(discount.minimumCartValue || 0);
      const discountPct = parseFloat(
        discount.discountPercentage || discount.discountPercent || 0
      );

      if (subtotalValue >= minValue && discountPct > 0) {
        const discountAmountValue = (subtotalValue * discountPct) / 100;

        // Choose the discount with highest amount
        if (!bestDiscount || discountAmountValue > bestDiscountAmount) {
          bestDiscount = discount;
          bestDiscountAmount = discountAmountValue;
        }
      }
    }

    if (bestDiscount) {
      const discountedSubtotal = subtotalValue - bestDiscountAmount;
      return {
        appliedDiscount: bestDiscount,
        discountAmount: bestDiscountAmount,
        discountedSubtotal: discountedSubtotal,
      };
    }

    return {
      appliedDiscount: null,
      discountAmount: 0,
      discountedSubtotal: subtotalValue,
    };
  };

  // ── IP Geo ────────────────────────────────────────────────────────────────
  useEffect(() => {
    IpGeo()
      .then((res) => {
        const cur = res.data?.currency;
        if (cur?.code)
          setDetectedCurrency({
            code: cur.code,
            symbol: cur.symbol || cur.code,
            name: cur.name || cur.code,
          });
      })
      .catch(() => {});
  }, []);

  // Keep detectedCurrencyRef in sync so profile fetch callbacks always read the latest value
  useEffect(() => {
    detectedCurrencyRef.current = detectedCurrency;
  }, [detectedCurrency]);

  // ── profileDetailsById + broker info ─────────────────────────────────────
  // Load profile data
  useEffect(() => {
    const dp = getUrlParameter("data");
    if (!dp) {
      setOrderLoading(false);
      return;
    }
    let decoded;
    try {
      const parsed = JSON.parse(
        decodeURIComponent(escape(atob(dp.replace(/ /g, "+"))))
      );
      decoded = Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
      setOrderLoading(false);
      return;
    }
    const pId = decoded.paymentId || decoded.payment_id || "";
    const mId = decoded.merchantId || decoded.merchant_id || "";
    const bId = decoded.brokerId || decoded.broker_id || "";
    console.log("Decoded URL data:", decoded);
    const brandWalletAvaliabilityStatus =
      decoded.brandWalletAvaliabilityStatus || "0";
    setBrandWalletAvaliability(brandWalletAvaliabilityStatus);
    const hasAvailedTrial = decoded.hasAvailedTrial || 0;
    setTrailAvaliability(hasAvailedTrial);
    const decodedRedeemAmount = parseFloat(decoded.redeemAmount || 0);
    setRedeemAmount(decodedRedeemAmount);
    const decodedIsZeroPayment =
      decoded.isZeroPayment === 1 || decoded.isZeroPayment === "1";
    setIsZeroPayment(decodedIsZeroPayment);

    if (bId) {
      localStorage.setItem(SITENAMEALIAS + "_broker_id", bId);
      localStorage.setItem(SITENAMEALIAS + "_brokerId", bId);
      fetch(
        `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${bId}`
      )
        .then((r) => r.json())
        .then((res) => {
          const info = res.value?.[0] || {};
          const name = info.exchange || "";
          const logo = info.exchange_logo || "";
          localStorage.setItem(SITENAMEALIAS + "_exchange", name);
          localStorage.setItem(SITENAMEALIAS + "_exchangeLogo", logo);
          localStorage.setItem(
            SITENAMEALIAS + "_companyName",
            info.company_name || ""
          );
          if (name) setExchangeName(name);
          if (logo) {
            const lnk = document.createElement("link");
            lnk.rel = "icon";
            lnk.href = logo;
            document.head.appendChild(lnk);
          }
          if (name) document.title = name;
          if (res.color) {
            localStorage.setItem(
              SITENAMEALIAS + "_backgroundColour",
              `linear-gradient(90deg,${res.color.backgroundColor1} 0%,${res.color.backgroundColor2} 60%)`
            );
            localStorage.setItem(
              SITENAMEALIAS + "_navbarColour",
              res.color.navbarColor
            );
            localStorage.setItem(
              SITENAMEALIAS + "_buttonColour",
              res.color.buttonColor
            );
          }
        })
        .catch(() => {});
    }

    if (!pId || !mId) {
      setOrderLoading(false);
      return;
    }

    const mySeq = ++profileFetchSeqRef.current;

    fetch(
      `${WEBSERVICE}/payment/profileDetailsById?id=${encodeURIComponent(
        pId
      )}&merchantId=${encodeURIComponent(mId)}`
    )
      .then((r) => r.json())
      .then((data) => {
        // Stale — IpGeo fired a newer currency, discard old response
        if (mySeq !== profileFetchSeqRef.current) return;
        if (data.error !== 0 && data.error !== "0") {
          setOrderLoading(false);
          return;
        }

        const billing = data.billingProfiles?.[0] || null;
        const shipping = data.shippingProfiles?.[0] || null;
        const prods = data.products || [];
        const discountsList = data.discounts || [];
        const redirectProfiles = data.redirectUrlProfile || [];

        // Log redirect URL profile for debugging
        console.log(
          "redirectUrlProfile:",
          redirectProfiles.length > 0 ? redirectProfiles : []
        );

        // Get redirect URLs
        const defaultRedirect =
          redirectProfiles.find((r) => r.isDefault === 1) ||
          redirectProfiles[0];

        // Helper function to ensure URL has protocol
        const normalizeUrl = (url) => {
          if (!url) return null;
          if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
          }
          return `https://${url}`;
        };

        if (
          defaultRedirect &&
          defaultRedirect.successUrl &&
          defaultRedirect.failureUrl
        ) {
          const successUrl = normalizeUrl(defaultRedirect.successUrl);
          const failureUrl = normalizeUrl(defaultRedirect.failureUrl);
          const advanceOptions = defaultRedirect.advanceOptions || [];

          console.log(
            "Using custom redirect URLs - Success:",
            successUrl,
            "| Failure:",
            failureUrl,
            "| Advance Options:",
            advanceOptions
          );
          setRedirectSuccessUrl(successUrl);
          setRedirectFailureUrl(failureUrl);
          setRedirectAdvanceOptions(advanceOptions);
        } else {
          // Set default URLs if no redirect profile exists
          console.log("No valid redirect profile found, using default URLs");
          setRedirectSuccessUrl(
            `${window.location.origin}/merchant/checkout/success`
          );
          setRedirectFailureUrl(
            `${window.location.origin}/merchant/checkout/failure`
          );
          setRedirectAdvanceOptions([]);
        }

        setBillingProfile(billing);
        setShippingProfile(shipping);
        setOrderProducts(prods);
        setDiscounts(discountsList);

        const hPct = parseFloat(shipping?.handlingFeeValue || 0);
        const tPct = parseFloat(shipping?.taxRate || 0);

        // ──────────────────────────────────────────────────────────────
        // VALIDATE DETECTED CURRENCY & APPLY FX CONVERSION IF NEEDED
        // ──────────────────────────────────────────────────────────────
        const allPriceCurrencies = new Set(
          prods.flatMap((p) =>
            (p.prices || []).flatMap((pr) =>
              (pr.currencies || []).map((c) => c.currency)
            )
          )
        );

        let effectiveCurrency;
        let needsFxConversion = false;
        let firstCode = null;

        // Use ref to get the latest currency value (avoids stale closure and re-fetch loop)
        const latestCurrency = detectedCurrencyRef.current;

        if (
          allPriceCurrencies.size === 0 ||
          allPriceCurrencies.has(latestCurrency.code)
        ) {
          // Detected currency matches — use it directly
          effectiveCurrency = latestCurrency;
        } else {
          // Detected currency not in product currencies — use the product's default currency
          const firstPrice = prods?.[0]?.prices?.[0];
          const defaultCurr =
            firstPrice?.currencies?.find((c) => c.default === true) ||
            firstPrice?.currencies?.[0];
          if (defaultCurr) {
            const CURRENCY_SYMBOLS = {
              USD: "$",
              EUR: "€",
              GBP: "£",
              JPY: "¥",
              INR: "₹",
              AUD: "A$",
              CAD: "C$",
              CHF: "CHF",
              SGD: "S$",
              HKD: "HK$",
              MYR: "RM",
              NZD: "NZ$",
              BRL: "R$",
              ZAR: "R",
              BTC: "₿",
              ETH: "Ξ",
              USDT: "USDT",
              USDC: "USDC",
              LTC: "Ł",
              BCH: "BCH",
              XRP: "XRP",
              DOGE: "Ð",
              HCX: "HCX",
            };
            effectiveCurrency = {
              code: defaultCurr.currency,
              symbol:
                CURRENCY_SYMBOLS[defaultCurr.currency] || defaultCurr.currency,
              name: defaultCurr.currency,
            };
          } else {
            effectiveCurrency = { code: "USD", symbol: "$", name: "US Dollar" };
          }
          // No FX conversion — we now directly read the amount from the matching currency entry
          needsFxConversion = false;
        }

        // Update the detected currency state if the effective currency differs.
        // setDetectedCurrency here updates the display but no longer re-triggers
        // this effect (dependency changed to [] below).
        if (effectiveCurrency.code !== latestCurrency.code) {
          setDetectedCurrency(effectiveCurrency);
        }

        // If FX conversion is needed, fetch the rate and calculate
        if (needsFxConversion && firstCode) {
          fetch(`${WEBSERVICE}/merchant/getMarketPrice?counter=${firstCode}`)
            .then((r) => r.json())
            .then((priceRes) => {
              if (
                (priceRes.error === "0" || priceRes.error === 0) &&
                parseFloat(priceRes.price) > 0
              ) {
                const rate = parseFloat(priceRes.price);
                setFxRate(rate);
                let usdSub = 0;
                prods.forEach((p) => {
                  const priceObj = p.prices?.[0];
                  const ce =
                    priceObj?.currencies?.find(
                      (c) => c.currency === firstCode
                    ) || priceObj?.currencies?.[0];
                  const qty = parseInt(p.itemQuantity ?? 1, 10);
                  const priceInFirstCode =
                    parseFloat(
                      p.basePrice || p.price || p.amount || ce?.amount || 0
                    ) * qty;
                  const base = priceInFirstCode / rate; // Convert to USD
                  usdSub += base;
                });
                usdSub = parseFloat(usdSub);
                setOrderSubtotal(usdSub);
                const {
                  appliedDiscount: d,
                  discountAmount: da,
                  discountedSubtotal: ds,
                } = calculateDiscount(usdSub, discountsList);
                setAppliedDiscount(d);
                setDiscountAmount(da);
                setDiscountSubtotal(ds);

                const handlingAmtFx = parseFloat(((usdSub * hPct) / 100));
                const taxAmtFx = parseFloat(((usdSub * tPct) / 100));
                const totalBeforeFeeFx = parseFloat(
                  (ds + handlingAmtFx + taxAmtFx)
                );

                setOrderGrandTotal(
                  Math.max(0, parseFloat((totalBeforeFeeFx - decodedRedeemAmount)))
                );

                // Process fee after totals are set
                const hasFee = isOn(data.isProcessingFeeApplied);
                if (hasFee && usdSub > 0) {
                  setOrderFeeApplied(true);
                  setOrderFeeLoading(true);
                  fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      merchantId: String(mId),
                      currencyId: "1",
                      amount: totalBeforeFeeFx,
                    }),
                  })
                    .then((r) => r.json())
                    .then((feeData) => {
                      if (feeData.error === "0" || feeData.error === 0) {
                        setOrderFeePercent(parseFloat(feeData.txnCharge || 0));
                        setOrderFeeAmount(
                          parseFloat(feeData.transactionFee || 0)
                        );
                        setOrderGrandTotal(
                          Math.max(
                            0,
                            parseFloat(
                              (
                                parseFloat(feeData.totalAmount || totalBeforeFeeFx) -
                                decodedRedeemAmount
                              )
                            )
                          )
                        );
                      } else {
                        showToast("error", feeData.error_msg || feeData.message || "Failed to calculate processing fee.");
                      }
                    })
                    .catch(() => {
                      showToast("error", "Failed to calculate processing fee.");
                    })
                    .finally(() => setOrderFeeLoading(false));
                }
              }
              setOrderLoading(false);
            })
            .catch(() => {
              setOrderLoading(false);
            });
        } else {
          // No FX conversion needed - calculate directly
          let sub = 0;
          prods.forEach((p) => {
            const priceObj = p.prices?.[0];
            const ce =
              priceObj?.currencies?.find(
                (c) => c.currency === effectiveCurrency.code
              ) ||
              priceObj?.currencies?.find((c) => c.default) ||
              priceObj?.currencies?.[0];
            const qty = parseInt(p.itemQuantity ?? 1, 10);
            const base =
              parseFloat(
                p.basePrice || p.price || p.amount || ce?.amount || 0
              ) * qty;
            sub += base;
          });
          sub = parseFloat(sub);
          setOrderSubtotal(sub);

          // Apply discount before processing fee calculation
          const {
            appliedDiscount: discount,
            discountAmount: discAmount,
            discountedSubtotal: discSub,
          } = calculateDiscount(sub, discountsList);
          setAppliedDiscount(discount);
          setDiscountAmount(discAmount);
          setDiscountSubtotal(discSub);

          const handlingAmt = parseFloat(((sub * hPct) / 100));
          const taxAmt = parseFloat(((sub * tPct) / 100));
          const totalBeforeFee = parseFloat(
            (discSub + handlingAmt + taxAmt)
          );

          setOrderGrandTotal(
            Math.max(0, parseFloat((totalBeforeFee - decodedRedeemAmount)))
          );

          const hasFee = isOn(data.isProcessingFeeApplied);
          if (hasFee && sub > 0) {
            setOrderFeeApplied(true);
            setOrderFeeLoading(true);
            fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                merchantId: String(mId),
                currencyId: "1",
                amount: totalBeforeFee,
              }),
            })
              .then((r) => r.json())
              .then((feeData) => {
                if (feeData.error === "0" || feeData.error === 0) {
                  setOrderFeePercent(parseFloat(feeData.txnCharge || 0));
                  setOrderFeeAmount(parseFloat(feeData.transactionFee || 0));
                  setOrderGrandTotal(
                    Math.max(
                      0,
                      parseFloat(
                        (
                          parseFloat(feeData.totalAmount || totalBeforeFee) -
                          decodedRedeemAmount
                        )
                      )
                    )
                  );
                } else {
                  showToast("error", feeData.error_msg || feeData.message || "Failed to calculate processing fee.");
                }
              })
              .catch(() => {
                showToast("error", "Failed to calculate processing fee.");
              })
              .finally(() => setOrderFeeLoading(false));
          }
          setOrderLoading(false);
        }
      })
      .catch(() => setOrderLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // detectedCurrencyRef.current is used inside instead of detectedCurrency state

  // ── Device details ────────────────────────────────────────────────────────
  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((r) => r.json())
      .then((data) => {
        setLoginDeviceDetails({
          ip: data.ip || "",
          latitude: data.latitude || "",
          longitude: data.longitude || "",
          country_name: data.country_name || "",
        });
      })
      .catch(() => {});
  }, []);

  // ── Market price for selected crypto ─────────────────────────────────────
  useEffect(() => {
    if (!selectedCrypto) {
      setCryptoMarketPrice(null);
      setCryptoConvertedAmt(null);
      setMinCryptoAmt(null);
      setCryptoAmtInputError("");
      return;
    }
    const selectedCoin = availableCryptos.find(
      (c) => c.currency_code === selectedCrypto
    );
    setCryptoPriceLoading(true);
    const baseCurrency = detectedCurrency?.code || "USD";
    fetch(
      `${WEBSERVICE}/merchant/getMarketPriceWithBaseCurrency?currency=${selectedCrypto}&baseCurrency=${baseCurrency}`
    )
      .then((r) => r.json())
      .then((res) => {
        if (res.error !== "0" && res.error !== 0) return;
        const price = parseFloat(res.price);
        setCryptoMarketPrice(price);
        const fiatTotal = orderGrandTotal || grandTotal || 0;
        console.log("fiatTotal", fiatTotal);

        if (price > 0 && fiatTotal > 0) {
          // price = crypto per 1 fiat unit → multiply
          const converted = (fiatTotal / price).toFixed(8);
          setCryptoConvertedAmt(converted);
          cryptoConvertedAmtRef.current = converted;
          setMinCryptoAmt(converted);
          setCryptoAmtInputError("");
        }
      })
      .catch(() => {})
      .finally(() => setCryptoPriceLoading(false));
  }, [
    selectedCrypto,
    selectedNetwork,
    orderGrandTotal,
    grandTotal,
    detectedCurrency,
  ]);

  useEffect(() => {
    selectedCryptoRef.current = selectedCrypto;
  }, [selectedCrypto]);

  // Fetch balance from REST on crypto change; WS will override in real-time
  useEffect(() => {
    if (paybitoToken && paybitoUuid && selectedCrypto) {
      // In Test Mode, the balance API requires an intentId
      if (isTestMode && !paymentIntentId) {
        console.log(
          "[useEffect] Waiting for paymentIntentId before fetching balance in Test Mode"
        );
        return;
      }

      const cur = selectedCrypto.toUpperCase();
      const cached = balanceMapRef.current[cur];
      if (cached !== undefined) {
        setWalletBalance(cached);
      } else {
        fetchWalletBalance(paybitoToken, paybitoUuid);
      }
      setShowReceiveQR(false);
      setDepositAddress("");
      setDepositTag(null);
      stopDepositBalancePolling();
    }
  }, [
    selectedCrypto,
    selectedNetwork,
    paymentIntentId,
    paybitoToken,
    paybitoUuid,
  ]);

  // Clear payment intent cache when crypto selection changes
  useEffect(() => {
    // Clear cache for crypto payment methods when selection changes
    paymentIntentCache.current[3] = null;
    paymentIntentCache.current[4] = null;
    paymentIntentCache.current[5] = null;
  }, [selectedCrypto, selectedNetwork]);

  // Auto-trigger ONLY the createCharge API when the user picks a different
  // crypto while a crypto payment option is active. Reuses the existing
  // paymentIntentId (already created when the user first opened the option)
  // so createPaymentIntent is NOT re-invoked.
  useEffect(() => {
    if (!selectedCrypto) return;
    if (!cryptoMarketPrice) return;
    if (!paymentIntentId) return;

    const selectedCoin = availableCryptos.find(
      (c) => c.currency_code === selectedCrypto
    );
    const networks = selectedCoin?.network || [];
    const isNativeOnly = networks.length === 1 && networks[0] === "NATIVE";
    if (!isNativeOnly && !selectedNetwork) return;

    const pmIdByOpt = { wallet: 3, external: 4, guest: 5 };
    const pmId = pmIdByOpt[selectedOpt];
    if (!pmId) return;
    // pmId 4 (external wallet) has its own charge flow — skip here
    if (pmId === 4) return;

    let cancelled = false;

    (async () => {
      try {
        const isStablecoin = ["USDT", "USDC"].includes(selectedCrypto);
        let blockchain = isStablecoin ? "ERC" : "NATIVE";
        if (
          isStablecoin &&
          (selectedNetwork === "TRC" || paymentType === "trc20")
        ) {
          blockchain = "TRON";
        }

        const marketPrice = cryptoMarketPrice || 0;
        const convertToCrypto = (fiatAmount) => {
          const price = parseFloat(marketPrice);
          if (price <= 0 || fiatAmount <= 0) return "0";
          return (fiatAmount / price).toFixed(8);
        };

        const _sp1 = shippingProfile || {};
        const _hPct1 = parseFloat(_sp1.handlingFeeValue || 0);
        const _tPct1 = parseFloat(_sp1.taxRate || 0);
        const _getBase1 = (p) => {
          const priceObj = p.prices?.[0];
          const ce =
            priceObj?.currencies?.find(
              (c) => c.currency === detectedCurrency.code
            ) ||
            priceObj?.currencies?.find((c) => c.default === true) ||
            priceObj?.currencies?.[0];
          return parseFloat(
            p.basePrice || p.price || p.amount || ce?.amount || 0
          );
        };

        const fiatSubtotal = parseFloat(
          orderProducts.reduce((acc, p) => acc + _getBase1(p), 0).toFixed(2)
        );
        const fiatShippingAmt = parseFloat(
          orderProducts
            .reduce((acc, p) => acc + (_getBase1(p) * _hPct1) / 100, 0)
            .toFixed(2)
        );
        const fiatTaxAmt = parseFloat(
          orderProducts
            .reduce((acc, p) => acc + (_getBase1(p) * _tPct1) / 100, 0)
            .toFixed(2)
        );
        const fiatDiscountAmt = parseFloat(discountAmount || 0);
        const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

        const baseAmount = convertToCrypto(fiatSubtotal);
        setConvertBaseAmount(baseAmount);
        const taxAmount = convertToCrypto(fiatTaxAmt);
        const discountAmountValue = convertToCrypto(fiatDiscountAmt);
        const shippingAmount = convertToCrypto(fiatShippingAmt);
        const processingFeeAmount = convertToCrypto(fiatProcessingFeeAmt);
        setConvertTaxAmount(taxAmount);
        setConvertDiscountAmountValue(discountAmountValue);
        setConvertShippingAmount(shippingAmount);
        setConvertProcessingFeeAmount(processingFeeAmount);

        // Brand wallet (pmId 3): createCharge is deferred to the "Proceed to Pay"
        // button click inside handleWalletPayment — do not call it here.
        if (pmId === 3) return;

        const amount =
          cryptoConvertedAmt || grandTotal || orderGrandTotal || "0";

        const chargePayload = {
          paymentIntentId,
          amount: String(amount),
          currency: selectedCrypto,
          marketPrice: String(marketPrice),
          blockchain,
          gatewayTransactionId: "",
          walletProvider:
            pmId === 3
              ? "Crypto"
              : localStorage.getItem("last_connected_wallet_name") || "",
          walletAddress: pmId === 3 ? null : connectedWallet || "",
          network: blockchain,
          baseAmount,
          taxAmount,
          discountAmount: discountAmountValue,
          shippingAmount,
          processingFeeAmount,
        };

        console.log(
          "[auto-createCharge] selectedCrypto changed → createCharge payload:",
          chargePayload
        );

        const chargeResponse = await fetch(
          `${WEBSERVICE}/checkout/createCharge`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(chargePayload),
          }
        );
        const chargeData = await chargeResponse.json();
        if (cancelled) return;

        if (!chargeData.status) {
          console.error(
            "[auto-createCharge] Failed:",
            chargeData.message || chargeData
          );
          showToast(
            "error",
            chargeData.message ||
              "Failed to initialize charge. Please try again."
          );
          return;
        }

        console.log("[auto-createCharge] Success:", chargeData);
        externalWalletChargeRef.current = chargeData.data || null;
      } catch (err) {
        if (cancelled) return;
        console.error("[auto-createCharge] Error:", err);
        showToast("error", "Network error while initializing charge.");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCrypto,
    selectedNetwork,
    cryptoMarketPrice,
    selectedOpt,
    paymentIntentId,
  ]);

  // Simple auto-connect function
  const attemptAutoConnect = async () => {
    // Skip auto-connect if user explicitly disconnected this session
    if (sessionStorage.getItem("wallet_user_disconnected") === "true") {
      console.log("User previously disconnected — skipping auto-connect");
      setAutoConnectAttempted(true);
      return false;
    }

    // Check if MetaMask is installed
    if (!window.ethereum) {
      console.log("No wallet detected");
      return false;
    }

    try {
      console.log("Attempting auto-connect on MAINNET...");

      // Get the current network
      const chainId = await window.ethereum.request({ method: "eth_chainId" });
      console.log("Current network:", chainId);

      // Try to get accounts (silent request - no popup)
      const accounts = await window.ethereum.request({
        method: "eth_accounts", // This is silent, no popup
      });

      console.log("Detected accounts:", accounts);

      // If there are connected accounts, auto-connect
      if (accounts && accounts.length > 0) {
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        setEthProvider(provider);
        setEthSigner(signer);
        setConnectedWallet(accounts[0]);
        setSelectedOpt("external");

        // Check if on a supported network (for MAINNET, all networks are accepted)
        let networkStatus = "";
        if (NETWORK_MODE === "TESTNET") {
          const isCorrectNetwork = chainId === NETWORK_CONFIG.TESTNET.chainId;
          networkStatus = isCorrectNetwork
            ? " (Sepolia ✓)"
            : " (Wrong Network - Please switch to Sepolia)";
        } else {
          networkStatus = ` (Network: ${chainId})`;
        }

        showToast(
          "success",
          `Wallet auto-connected: ${accounts[0].slice(
            0,
            6
          )}...${accounts[0].slice(-4)}${networkStatus}`
        );
        return true;
      }

      // If no accounts found but we had a previously connected wallet,
      // try to request accounts (this will show the connection popup)
      const lastWalletAddress = localStorage.getItem(
        "last_connected_wallet_address"
      );
      if (lastWalletAddress) {
        console.log(
          "Previously connected wallet found, requesting connection..."
        );
        const requestedAccounts = await window.ethereum.request({
          method: "eth_requestAccounts", // This will show the popup
        });

        if (requestedAccounts && requestedAccounts.length > 0) {
          const provider = new BrowserProvider(window.ethereum);
          const signer = await provider.getSigner();
          setEthProvider(provider);
          setEthSigner(signer);
          setConnectedWallet(requestedAccounts[0]);
          setSelectedOpt("external");
          showToast(
            "success",
            `Wallet connected: ${requestedAccounts[0].slice(
              0,
              6
            )}...${requestedAccounts[0].slice(-4)}`
          );
          return true;
        }
      }

      console.log("No wallet connected");
      return false;
    } catch (error) {
      console.error("Auto-connect failed:", error);
      // Don't show error toast for auto-connect attempts
      return false;
    } finally {
      setAutoConnectAttempted(true);
    }
  };

  const disconnectWallet = () => {
    setConnectedWallet(null);
    setEthProvider(null);
    setEthSigner(null);
    setTxStatus("");
    setTxHash("");
    setTxConfirmed(false);
    setSelectedOpt(null);

    // Clear saved connection info
    localStorage.removeItem("last_connected_wallet_type");
    localStorage.removeItem("last_connected_wallet_address");
    localStorage.removeItem("last_connected_wallet_name");

    // Prevent auto-connect for the rest of this session
    sessionStorage.setItem("wallet_user_disconnected", "true");

    showToast("info", "Wallet disconnected");
  };

  // Silent disconnect triggered by UI context changes (network / crypto switch).
  // Does not show a toast and does not set the wallet_user_disconnected flag so
  // auto-connect is not permanently suppressed for the new context.
  const silentDisconnectWallet = () => {
    setConnectedWallet(null);
    setEthProvider(null);
    setEthSigner(null);
    setTxStatus("");
    setTxHash("");
    setTxConfirmed(false);
    setSelectedOpt(null);
    localStorage.removeItem("last_connected_wallet_type");
    localStorage.removeItem("last_connected_wallet_address");
    localStorage.removeItem("last_connected_wallet_name");
  };

  // Try to auto-connect when page loads
  // Try to auto-connect when page loads
  useEffect(() => {
    // Only try once and only if not already connected
    if (!connectedWallet && !autoConnectAttempted) {
      attemptAutoConnect();
    }
  }, [connectedWallet, autoConnectAttempted]);

  // Add this useEffect to listen for account changes
  // Listen for account changes
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        // User disconnected their wallet
        setConnectedWallet(null);
        setEthProvider(null);
        setEthSigner(null);
        setTxStatus("");
        setTxHash("");
        setTxConfirmed(false);
        setSelectedOpt(null);
        showToast("info", "Wallet disconnected");

        // Clear stored wallet info and prevent auto-reconnect
        sessionStorage.setItem("wallet_user_disconnected", "true");
        localStorage.removeItem("last_connected_wallet_type");
        localStorage.removeItem("last_connected_wallet_address");
        localStorage.removeItem("last_connected_wallet_name");
      } else if (accounts[0] !== connectedWallet) {
        // Account changed
        const reconnect = async () => {
          try {
            const provider = new BrowserProvider(window.ethereum);
            const signer = await provider.getSigner();
            setEthProvider(provider);
            setEthSigner(signer);
            setConnectedWallet(accounts[0]);

            // Get current network
            const chainId = await window.ethereum.request({
              method: "eth_chainId",
            });
            const networkName =
              chainId === "0x1"
                ? "Ethereum Mainnet"
                : chainId === "0xaa36a7"
                ? "Sepolia Testnet"
                : `Chain ID: ${chainId}`;

            showToast(
              "success",
              `Switched to: ${accounts[0].slice(0, 6)}...${accounts[0].slice(
                -4
              )} on ${networkName}`
            );

            // Update stored wallet info
            localStorage.setItem("last_connected_wallet_address", accounts[0]);
          } catch (error) {
            console.error("Failed to reconnect after account change:", error);
          }
        };
        reconnect();
      }
    };

    const handleChainChanged = (chainId) => {
      console.log("Network changed to:", chainId);
      // For MAINNET, we don't need to reload, just update the UI
      const networkName =
        chainId === "0x1"
          ? "Ethereum Mainnet"
          : chainId === "0xaa36a7"
          ? "Sepolia Testnet"
          : `Chain ID: ${chainId}`;
      //showToast("info", `Network switched to ${networkName}`); //NOTE: showing this is commented as per Raj's instruction on 30th Jul 26

      // Optionally update currentNetwork state
      setCurrentNetwork(networkName);
    };

    window.ethereum.on("accountsChanged", handleAccountsChanged);
    window.ethereum.on("chainChanged", handleChainChanged);

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );
        window.ethereum.removeListener("chainChanged", handleChainChanged);
      }
    };
  }, [connectedWallet]);

  // ── createPaymentIntent ───────────────────────────────────────────────────
  const createPaymentIntent = async (pmId, cryptoDetails = null) => {
    // Crypto payment methods: 3 (brand wallet), 4 (external wallet), 5 (guest checkout)
    // PayPal = 2, Stripe = 1 are NOT crypto methods
    const isCryptoPayment = pmId === 3 || pmId === 4 || pmId === 5;

    // For non-crypto payments, use cache
    if (!isCryptoPayment && paymentIntentCache.current[pmId]) {
      const cached = paymentIntentCache.current[pmId];
      if (pmId === 2 && cached.gatewayPublicKey)
        setPaypalClientId(cached.gatewayPublicKey);
      setPaypalOrderId(cached.gatewayIntentId);
      setPaypalSubscriptionId(cached.gatewaySubscriptionId || "");
      if (pmId === 1 && cached.gatewayPublicKey)
        setStripePublishableKey(cached.gatewayPublicKey);
      setPaymentIntentId(cached.gatewayIntentId || "");
      return cached;
    }

    const dp = getUrlParameter("data");
    let sessionId = "";
    if (dp) {
      try {
        const parsed = JSON.parse(
          decodeURIComponent(escape(atob(dp.replace(/ /g, "+"))))
        );
        const obj = Array.isArray(parsed) ? parsed[0] : parsed;
        sessionId = obj.sessionId || obj.session_id || "";
      } catch {}
    }

    const clientSecret = Array.from(
      { length: 32 },
      () =>
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"[
          Math.floor(Math.random() * 62)
        ]
    ).join("");

    setPaymentIntentLoading(true);

    try {
      const res = await fetch(`${WEBSERVICE}/checkout/createPaymentIntent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: String(sessionId),
          paymentMethodId: String(pmId),
          clientSecret,
        }),
      });
      const data = await res.json();

      if (data.status && data.data) {
        const result = data.data;
        console.log("PAYMENT INTENT => ", result);

        // Only cache non-crypto payment methods
        if (!isCryptoPayment) {
          paymentIntentCache.current[pmId] = result;
        }

        setPaymentIntentId(result.paymentIntentId || "");

        // If in Test Mode and we just created an intent for the brand wallet,
        // fetch the balance immediately with the new intent ID.
        if (isTestMode && pmId === 3 && paybitoToken && paybitoUuid) {
          fetchWalletBalance(
            paybitoToken,
            paybitoUuid,
            result.paymentIntentId || ""
          );
        }

        setPaypalOrderId(result.gatewayIntentId);
        setPaypalSubscriptionId(result.gatewaySubscriptionId || "");
        const pubKey = result.gatewayPublicKey || "";
        if (pmId === 2 && pubKey) setPaypalClientId(pubKey);
        if (pmId === 1 && pubKey) setStripePublishableKey(pubKey);
        if (pmId === 7 && pubKey) setKurvTokenizationKey(pubKey);
        if (pmId === 7) setKurvGatewayPlanId(result.gatewayPriceId || "");
        if (pmId === 9) setHmsTokenizationKey(HMS_TOKENIZATION_KEY);
        if (pmId === 9) setHmsGatewayPlanId(result.gatewayPriceId || "");
        if (pmId === 11 && pubKey) setNmiTokenizationKey(pubKey);
        if (pmId === 11) setNmiGatewayPlanId(result.gatewayPriceId || "");
        if (pmId === 1) {
          const sessionId =
            result.gatewaySubscriptionId ||
            result.gatewayIntentId ||
            result.paymentIntentId;
          setPaymentIntentId(sessionId);
          setStripePaymentIntentId(sessionId);
          setStripePaymentUrl(result.gatewayUrl || "");
          setStripeSessionId(
            result.gatewaySubscriptionId || result.gatewayIntentId || ""
          );
          setStripePaymentIntentSecret(result.gatewayIntentSecret || "");
          console.log("[Stripe] session ID to use:", sessionId);
        }

        // ─── ONLY call createCharge for crypto payment methods (3, 4, 5) ───
        let chargeResponseData = null;

        if (result.paymentIntentId && pmId !== 3 && pmId !== 4 && pmId !== 7 && pmId !== 8 && pmId !== 9 && pmId !== 10 && pmId !== 11) {
          try {
              // Determine blockchain based on selected network
              const isStablecoin = ["USDT", "USDC"].includes(selectedCrypto);
              let blockchain = isStablecoin ? "ERC" : "NATIVE";
              if (
                isStablecoin &&
                (selectedNetwork === "TRC" || paymentType === "trc20")
              ) {
                blockchain = "TRON";
              }

              // Use provided crypto details or current state
              const amount =
                cryptoDetails?.amount ||
                cryptoConvertedAmt ||
                grandTotal ||
                orderGrandTotal ||
                "0";
              const currency =
                cryptoDetails?.currency ||
                selectedCrypto ||
                invoiceData?.currency ||
                "BTC";
              const marketPrice =
                cryptoDetails?.marketPrice || cryptoMarketPrice || "0";

              const _sp1 = shippingProfile || {};
              const _hPct1 = parseFloat(_sp1.handlingFeeValue || 0);
              const _tPct1 = parseFloat(_sp1.taxRate || 0);
              const _getBase1 = (p) => {
                const priceObj = p.prices?.[0];
                const ce =
                  priceObj?.currencies?.find(
                    (c) => c.currency === detectedCurrency.code
                  ) ||
                  priceObj?.currencies?.find((c) => c.default === true) ||
                  priceObj?.currencies?.[0];
                return parseFloat(
                  p.basePrice || p.price || p.amount || ce?.amount || 0
                );
              };

              // Calculate fiat amounts (in detected currency)
              const fiatSubtotal = parseFloat(
                orderProducts
                  .reduce((acc, p) => acc + _getBase1(p), 0)
                  .toFixed(2)
              );
              const fiatShippingAmt = parseFloat(
                orderProducts
                  .reduce((acc, p) => acc + (_getBase1(p) * _hPct1) / 100, 0)
                  .toFixed(2)
              );
              const fiatTaxAmt = parseFloat(
                orderProducts
                  .reduce((acc, p) => acc + (_getBase1(p) * _tPct1) / 100, 0)
                  .toFixed(2)
              );

              // FIXED: Get discount amount safely
              let currentDiscountAmount = 0;
              try {
                currentDiscountAmount = discountAmount || 0;
              } catch (e) {
                currentDiscountAmount = 0;
              }

              const fiatDiscountAmt = parseFloat(currentDiscountAmount || 0);
              const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

              // Helper function to convert fiat to crypto using market price
              const convertToCrypto = (fiatAmount) => {
                const price = parseFloat(marketPrice);
                if (price <= 0 || fiatAmount <= 0) return "0";
                return (fiatAmount / price).toFixed(8);
              };

              // Prepare payload amounts
              let baseAmount,
                taxAmount,
                discountAmountValue,
                shippingAmount,
                processingFeeAmount;

              if (isCryptoPayment) {
                // Convert all amounts to equivalent crypto price
                baseAmount = convertToCrypto(fiatSubtotal);
                taxAmount = convertToCrypto(fiatTaxAmt);
                discountAmountValue = convertToCrypto(fiatDiscountAmt); // FIXED: renamed variable
                shippingAmount = convertToCrypto(fiatShippingAmt);
                processingFeeAmount = convertToCrypto(fiatProcessingFeeAmt);
              } else {
                // Use fiat amounts directly
                baseAmount = String(fiatSubtotal);
                taxAmount = String(fiatTaxAmt);
                discountAmountValue = String(fiatDiscountAmt); // FIXED: renamed variable
                shippingAmount = String(fiatShippingAmt);
                processingFeeAmount = String(fiatProcessingFeeAmt);
              }

              const chargePayload = {
                paymentIntentId: result.paymentIntentId,
                amount: String(amount),
                currency: isCryptoPayment ? currency : detectedCurrency.code,
                marketPrice: String(marketPrice),
                blockchain: isCryptoPayment ? blockchain : "",
                gatewayTransactionId: "",
                walletProvider:
                  pmId === 3 || pmId === 5
                    ? "Crypto"
                    : localStorage.getItem("last_connected_wallet_name") || "",
                walletAddress:
                  pmId === 3 || pmId === 5 ? null : connectedWallet || "",
                network: blockchain,
                baseAmount: baseAmount,
                taxAmount: taxAmount,
                discountAmount: discountAmountValue, // FIXED: using renamed variable
                shippingAmount: shippingAmount,
                processingFeeAmount: processingFeeAmount,
              };

              console.log(
                "[createCharge] Payload for crypto payment:",
                chargePayload
              );

              const chargeResponse = await fetch(
                `${WEBSERVICE}/checkout/createCharge`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(chargePayload),
                }
              );
              const chargeData = await chargeResponse.json();

              if (!chargeData.status) {
                console.error(
                  "[createCharge] Failed:",
                  chargeData.message || chargeData
                );
                showToast(
                  "error",
                  chargeData.message ||
                    "Failed to initialize charge. Please try again."
                );
                throw new Error("Create charge failed");
              }

              console.log("[createCharge] Success:", chargeData);
              chargeResponseData = chargeData.data;
            } catch (err) {
              console.error("[createCharge] Error:", err);
              showToast("error", "Network error while initializing charge.");
              throw err;
            }
        } else if (!isCryptoPayment) {
          console.log(
            `[createPaymentIntent] Skipping createCharge for pmId ${pmId} (non-crypto payment method)`
          );
        }

        // ✅ MERGE the charge response with the payment intent result (only for crypto)
        const mergedResult = {
          ...result,
          ...(chargeResponseData && {
            chargeId: chargeResponseData.chargeId,
            receivingAddress: chargeResponseData.receivingAddress,
            maxRetry: chargeResponseData.maxRetry,
            retryCount: chargeResponseData.retryCount,
          }),
        };

        console.log("[createPaymentIntent] Returning result:", mergedResult);
        return mergedResult;
      } else {
        showToast("error", data.message || "Failed to initialize payment.");
        return null;
      }
    } catch (error) {
      console.error("[createPaymentIntent] Error:", error);
      showToast("error", "Network error initializing payment.");
      return null;
    } finally {
      setPaymentIntentLoading(false);
    }
  };
  // ── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const brokerId = getBrokerIdFromUrl();
    if (brokerId) {
      localStorage.setItem(SITENAMEALIAS + "_broker_id", brokerId);
      localStorage.setItem(SITENAMEALIAS + "_brokerId", brokerId);
      fetch(
        `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${brokerId}`
      )
        .then((r) => r.json())
        .then((res) => {
          const info = res.value?.[0] || {};
          const name = info.exchange || "";
          const logo = info.exchange_logo || "";
          localStorage.setItem(SITENAMEALIAS + "_exchange", name);
          localStorage.setItem(SITENAMEALIAS + "_exchangeLogo", logo);
          localStorage.setItem(
            SITENAMEALIAS + "_companyName",
            info.company_name || ""
          );
          setExchangeName(name);
          if (logo) {
            const lnk = document.createElement("link");
            lnk.rel = "icon";
            lnk.href = logo;
            document.head.appendChild(lnk);
          }
          if (name) document.title = name;
          if (res.color) {
            localStorage.setItem(
              SITENAMEALIAS + "_backgroundColour",
              `linear-gradient(90deg,${res.color.backgroundColor1} 0%,${res.color.backgroundColor2} 60%)`
            );
            localStorage.setItem(
              SITENAMEALIAS + "_navbarColour",
              res.color.navbarColor
            );
            localStorage.setItem(
              SITENAMEALIAS + "_buttonColour",
              res.color.buttonColor
            );
          }
        })
        .catch(() => {});
    }
    const dataParam = getUrlParameter("data");
    if (!dataParam) {
      setLoading(false);
      return;
    }
    setRawDataParam(dataParam);
    let decoded;
    try {
      const parsed = JSON.parse(atob(dataParam));
      decoded = Array.isArray(parsed) ? parsed[0] : parsed;
    } catch {
      setLoading(false);
      return;
    }
    if (!decoded.brokerId && !decoded.broker_id) {
      const rb = getBrokerIdFromUrl();
      if (rb) {
        decoded.brokerId = rb;
        setRawDataParam(btoa(JSON.stringify(decoded)));
      }
    }
    setDecodedDataParam(decoded);
    // Handle currency as either string or object
    const currencyValue = decoded.currency || decoded.currencyCode || "";
    const currencyCode =
      typeof currencyValue === "object" && currencyValue.code
        ? currencyValue.code
        : currencyValue;
    decoded.currency = currencyCode;
    setInvoiceData(decoded);
    const cur = (currencyCode || "").toUpperCase();
    const txAddr = decoded.transaction_address || "";
    const net = (decoded.network || "").toUpperCase();
    if (net === "TRC" || txAddr.startsWith("T")) setPaymentType("trc20");
    else if (cur === "ETH") setPaymentType("eth");
    else setPaymentType("erc20");
    if (decoded.invoice_id && decoded.merchant_id) {
      fetch(`${WEBSERVICE}/merchant/get_invoice_status_paid_amount`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          invoice_id: decoded.invoice_id,
          merchant_id: decoded.merchant_id,
        }),
      })
        .then((r) => r.json())
        .then((res) => {
          const record = Array.isArray(res) ? res[0] : res;
          if (record?.status === 48) setPaymentExpired(true);
        })
        .catch(() => {});
    }
    const qty = parseInt(getUrlParameter("quantity") || "1") || 1;
    setQuantity(qty);
    const hcUrlParam = getUrlParameter("hosted_catalog_id");
    const ebUrlParam = getUrlParameter("email_bill_id");
    const hcInData = decoded.hosted_catalog_id;
    const ebInData = decoded.bill_id;
    if (hcUrlParam) {
      setHostedCatalogId(atob(hcUrlParam));
      setUrlForSubmit(hosted_catalog_payment);
    } else if (hcInData) {
      setHostedCatalogId(String(hcInData));
      setUrlForSubmit(hosted_catalog_payment);
      setCatalogItem({
        hosted_catalog_id: hcInData,
        item_description: decoded.item_description || decoded.item_name || "—",
        item_price: decoded.item_price || "0",
        item_tax: decoded.item_tax || "0",
        item_shipping_handling: decoded.item_shipping_handling || "0",
        currency: decoded.currency,
      });
    } else if (ebInData) {
      setEmailBillId(String(ebInData));
      setUrlForSubmit(email_bill_payment);
      setBillInfo({
        bill_number: decoded.bill_number || "",
        due_date: decoded.due_date || "",
        currency: decoded.currency || decoded.currencyCode,
      });
      try {
        setBillItems(JSON.parse(atob(decoded.items)) || []);
      } catch {
        setBillItems([]);
      }
    } else if (ebUrlParam) {
      setEmailBillId(atob(ebUrlParam));
      setUrlForSubmit(email_bill_payment);
    } else {
      setUrlForSubmit(paynow_payment);
    }
    if ((hcInData || ebInData) && decoded.amount_to_be_paid !== undefined) {
      const base = parseFloat(decoded.amount_to_be_paid);
      setConvertedAmt(base);
      setTotalAmt(base);
      const applyFee =
        decoded.is_processing_fee_applied == 1 ||
        decoded.is_processing_fee_applied === "1";
      const finalizeEmbedded = (gt, feeData = null) => {
        setGrandTotal(gt);
        if (feeData) {
          setFeeAmt(parseFloat(feeData.transactionFee));
          setFeePct(feeData.txnCharge);
          setHfFeeApplied("1");
          setHfFeePct(String(feeData.txnCharge));
          setHfFeeAmt(parseFloat(feeData.transactionFee).toFixed(6));
        }
        setLoading(false);
        fetchMerchantInfo(decoded.merchant_id);
      };
      if (applyFee) {
        fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            merchantId: String(decoded.merchant_id),
            currencyId: String(decoded.currency_id),
            amount: parseFloat(base.toFixed(6)),
          }),
        })
          .then((r) => r.json())
          .then((fd) => {
            if (fd.error === "0")
              finalizeEmbedded(parseFloat(fd.totalAmount), fd);
            else finalizeEmbedded(base);
          })
          .catch(() => finalizeEmbedded(base));
      } else {
        finalizeEmbedded(base);
      }
      return;
    }
    fetch(`${WEBSERVICE}/merchant/getMarketPrice?counter=${decoded.currency}`)
      .then((r) => r.json())
      .then((priceRes) => {
        if (priceRes.error !== "0") {
          setLoading(false);
          return;
        }
        const converted =
          parseFloat(decoded.fiat_amount) / parseFloat(priceRes.price);
        setConvertedAmt(converted);
        const total = parseFloat((converted * qty).toFixed(8));
        setTotalAmt(total);
        const applyFee =
          decoded.is_processing_fee_applied == 1 ||
          decoded.is_processing_fee_applied === "1";
        const finalize = (gt, feeData = null) => {
          setGrandTotal(gt);
          if (feeData) {
            setFeeAmt(parseFloat(feeData.transactionFee));
            setFeePct(feeData.txnCharge);
            setHfFeeApplied("1");
            setHfFeePct(String(feeData.txnCharge));
            setHfFeeAmt(parseFloat(feeData.transactionFee).toFixed(6));
          }
          setLoading(false);
          fetchMerchantInfo(decoded.merchant_id);
          if (hcUrlParam)
            fetchCatalogFromApi(atob(hcUrlParam), decoded.currency);
          else if (ebUrlParam) fetchEmailBill(atob(ebUrlParam));
        };
        if (applyFee) {
          fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              merchantId: String(decoded.merchant_id),
              currencyId: String(decoded.currency_id),
              amount: parseFloat(total.toFixed(6)),
            }),
          })
            .then((r) => r.json())
            .then((fd) => {
              if (fd.error === "0") finalize(parseFloat(fd.totalAmount), fd);
              else finalize(total);
            })
            .catch(() => finalize(total));
        } else {
          finalize(total);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  const fetchMerchantInfo = (merchantId) => {
    fetch(`${WEBSERVICE}/merchant/get_merchant_address`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ merchant_id: merchantId }),
    })
      .then((r) => r.json())
      .then((data) => {
        const obj = Array.isArray(data) ? data[0] : data;
        setMerchantName(obj.organization_name || "");
        if (obj.display_address_on_bill === "yes")
          setMerchantAddr(
            [
              obj.inv_address_line_1,
              obj.inv_address_line_2,
              obj.inv_city,
              obj.inv_state,
              obj.inv_country,
              obj.inv_zip,
            ]
              .filter(Boolean)
              .join(", ")
          );
      })
      .catch(() => {});
  };
  const fetchCatalogFromApi = (catalogId, currency) => {
    fetch(`${WEBSERVICE}/merchant/get_hosted_catalog`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hosted_catalog_id: catalogId }),
    })
      .then((r) => r.json())
      .then((data) => {
        const obj = Array.isArray(data) ? data[0] : data;
        if (obj.error != 1) setCatalogItem({ ...obj, currency });
      })
      .catch(() => {});
  };
  const fetchEmailBill = (billId) => {
    fetch(`${WEBSERVICE}/merchant/get_email_bill`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bill_id: billId }),
    })
      .then((r) => r.json())
      .then((data) => {
        const obj = Array.isArray(data) ? data[0] : data;
        if (obj.error != 1) {
          setBillInfo({
            bill_number: obj.bill_number,
            due_date: obj.due_date,
            currency: obj.currency,
          });
          try {
            setBillItems(JSON.parse(obj.items) || []);
          } catch {
            setBillItems([]);
          }
        }
      })
      .catch(() => {});
  };

  // ── Wallet connect ────────────────────────────────────────────────────────

  // ── EIP-6963: Validate wallet is on a supported chain ────────────────────
  // TESTNET mode: wallet must already be on Sepolia
  // MAINNET mode: accept any chain — enforceNetwork handles switching
  const testEVMWallet = async (selectedProvider) => {
    try {
      const chainId = await selectedProvider.request({ method: "eth_chainId" });
      if (NETWORK_MODE === "TESTNET") {
        // If not on Sepolia, we'll try to switch, so don't fail here
        return true; // Always return true, we'll handle network switching separately
      }
      return true;
    } catch {
      return false;
    }
  };

  // ── EIP-6963: Switch wallet to the configured network if needed ───────────
  // MAINNET mode: skipped entirely (no enforcement)
  // TESTNET mode: prompts wallet to switch to Sepolia, adds chain if missing
  const enforceNetwork = async (selectedProvider) => {
    const config = NETWORK_CONFIG[NETWORK_MODE]; // works for both MAINNET and TESTNET
    const currentChain = await selectedProvider.request({
      method: "eth_chainId",
    });
    if (currentChain === config.chainId) return;

    try {
      await selectedProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: config.chainId }],
      });
    } catch (error) {
      if (error.code === 4902) {
        // Chain not in wallet — add it
        await selectedProvider.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainId: config.chainId,
              chainName: config.name,
              rpcUrls: [config.rpc],
              nativeCurrency: { name: "ETH", symbol: "ETH", decimals: 18 },
              blockExplorerUrls: [config.explorer.replace("/tx/", "")],
            },
          ],
        });
      } else {
        throw error;
      }
    }
  };
  // Add this function before the connectSelectedEVMWallet function
  const switchToSepolia = async (walletProvider) => {
    const config = NETWORK_CONFIG.TESTNET;

    try {
      // Try to switch to Sepolia
      await walletProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: config.chainId }],
      });
      return true;
    } catch (switchError) {
      // This error code means the chain hasn't been added to the wallet
      if (switchError.code === 4902) {
        try {
          // Add Sepolia network to wallet
          await walletProvider.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: config.chainId,
                chainName: config.name,
                rpcUrls: [config.rpc],
                nativeCurrency: {
                  name: "Sepolia ETH",
                  symbol: "SepoliaETH",
                  decimals: 18,
                },
                blockExplorerUrls: [config.explorer.replace("/tx/", "")],
              },
            ],
          });
          return true;
        } catch (addError) {
          console.error("Failed to add Sepolia network:", addError);
          return false;
        }
      }
      console.error("Failed to switch to Sepolia:", switchError);
      return false;
    }
  };
  // ── EIP-6963: Connect to a specific EVM wallet after user selects it ──────
  // Supports MetaMask, Trust Wallet, Coinbase Wallet, Phantom EVM, Brave etc.
  const connectSelectedEVMWallet = async (walletProvider, walletName) => {
    setExternalConnecting(true);
    setShowEVMWalletPicker(false);
    try {
      // For MAINNET, we don't enforce network switching
      if (NETWORK_MODE === "TESTNET") {
        const switched = await switchToSepolia(walletProvider);
        if (!switched) {
          showToast(
            "error",
            "Please add Sepolia network to your wallet or switch to it manually."
          );
          return;
        }
      } else {
        // For MAINNET, just log the current network but don't enforce
        const chainId = await walletProvider.request({ method: "eth_chainId" });
        const networkName =
          chainId === "0x1"
            ? "Ethereum Mainnet"
            : chainId === "0xaa36a7"
            ? "Sepolia Testnet"
            : `Chain ID: ${chainId}`;
        console.log(`Connecting to ${walletName} on ${networkName}`);

        // Optional: Show warning if not on mainnet
        if (chainId !== "0x1") {
          showToast(
            "warning",
            `Connected to ${networkName}. Please ensure this is correct for your payment.`
          );
        }
      }

      // Now request accounts (this will show the connection prompt)
      const accounts = await walletProvider.request({
        method: "eth_requestAccounts",
      });

      // Create provider and signer
      const provider = new BrowserProvider(walletProvider);
      const signer = await provider.getSigner();

      setEthProvider(provider);
      setEthSigner(signer);
      setConnectedWallet(accounts[0]);
      setSelectedOpt("external");
      setTxStatus("");
      setTxHash("");
      setTxConfirmed(false);

      // Get current network for display
      const chainId = await walletProvider.request({ method: "eth_chainId" });
      const networkName =
        chainId === "0x1"
          ? "Mainnet"
          : chainId === "0xaa36a7"
          ? "Sepolia"
          : `Chain ${chainId}`;

      // Save wallet info for auto-connect and clear disconnect flag
      sessionStorage.removeItem("wallet_user_disconnected");
      localStorage.setItem("last_connected_wallet_type", "evm");
      localStorage.setItem("last_connected_wallet_address", accounts[0]);
      localStorage.setItem("last_connected_wallet_name", walletName);
      localStorage.setItem("last_connected_network", chainId);

      showToast("success", `Connected to ${walletName} on ${networkName}`);

      // Call createCharge now that wallet is connected
      const intentResult = externalWalletIntentRef.current;
      if (intentResult?.paymentIntentId) {
        try {
          const isStablecoin = ["USDT", "USDC"].includes(selectedCrypto);
          const blockchain = isStablecoin
            ? selectedNetwork === "TRC"
              ? "TRON"
              : "ERC"
            : "NATIVE";
          const _sp2 = shippingProfile || {};
          const _hPct2 = parseFloat(_sp2.handlingFeeValue || 0);
          const _tPct2 = parseFloat(_sp2.taxRate || 0);
          const _getBase2 = (p) => {
            const priceObj = p.prices?.[0];
            const ce =
              priceObj?.currencies?.find(
                (c) => c.currency === detectedCurrency.code
              ) ||
              priceObj?.currencies?.find((c) => c.default === true) ||
              priceObj?.currencies?.[0];
            return parseFloat(
              p.basePrice || p.price || p.amount || ce?.amount || 0
            );
          };

          const fiatSubtotal = parseFloat(
            orderProducts.reduce((acc, p) => acc + _getBase2(p), 0).toFixed(2)
          );
          const fiatShippingAmt = parseFloat(
            orderProducts
              .reduce((acc, p) => acc + (_getBase2(p) * _hPct2) / 100, 0)
              .toFixed(2)
          );
          const fiatTaxAmt = parseFloat(
            orderProducts
              .reduce((acc, p) => acc + (_getBase2(p) * _tPct2) / 100, 0)
              .toFixed(2)
          );
          const fiatDiscountAmt = parseFloat(discountAmount || 0);
          const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

          const marketPrice = parseFloat(cryptoMarketPrice || "0");
          const convertToCrypto = (fiatAmount) => {
            if (marketPrice <= 0 || fiatAmount <= 0) return "0";
            return (fiatAmount / marketPrice).toFixed(8);
          };

          const chargePayload = {
            paymentIntentId: intentResult.paymentIntentId,
            amount: String(
              cryptoConvertedAmt ||
                cryptoConvertedAmtRef.current ||
                grandTotal ||
                orderGrandTotal ||
                "0"
            ),
            currency: selectedCrypto || "ETH",
            marketPrice: String(cryptoMarketPrice || "0"),
            blockchain: blockchain,
            gatewayTransactionId: "",
            walletProvider: walletName,
            walletAddress: accounts[0],
            network: blockchain,
            baseAmount: convertToCrypto(fiatSubtotal),
            taxAmount: convertToCrypto(fiatTaxAmt),
            discountAmount: convertToCrypto(fiatDiscountAmt),
            shippingAmount: convertToCrypto(fiatShippingAmt),
            processingFeeAmount: convertToCrypto(fiatProcessingFeeAmt),
          };
          console.log(
            "[createCharge][external wallet] Payload:",
            chargePayload
          );
          const chargeRes = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(chargePayload),
          });
          const chargeData = await chargeRes.json();
          if (!chargeData.status) {
            console.error(
              "[createCharge][external wallet] Failed:",
              chargeData.message
            );
            showToast(
              "error",
              chargeData.message ||
                "Failed to initialize charge. Please try again."
            );
            return;
          } else {
            console.log("[createCharge][external wallet] Success:", chargeData);
            externalWalletChargeRef.current = chargeData.data || null;
          }
        } catch (err) {
          console.error("[createCharge][external wallet] Error:", err);
        }
      }
    } catch (err) {
      console.error("Connection error:", err);
      const friendly = getFriendlyWalletError(err);
      const msg =
        err.code === 4001
          ? "Wallet connection cancelled."
          : friendly !== "Transaction failed. Please try again."
          ? friendly
          : `Failed to connect ${walletName}. Please make sure your wallet is unlocked.`;
      showToast("error", msg);
    } finally {
      setExternalConnecting(false);
    }
  };

  // ── EIP-6963: Detect all installed EVM wallets and show picker ────────────
  // Fires eip6963:requestProvider — all compliant wallets (MetaMask, Coinbase,
  // Trust, Phantom EVM, Brave etc.) announce themselves via eip6963:announceProvider
  // Falls back to window.ethereum for non-EIP-6963 wallets
  const initEIP6963AndConnect = () => {
    const wallets = [];

    const handleAnnounce = (event) => {
      const { info, provider } = event.detail;
      // Deduplicate by uuid
      if (!wallets.find((w) => w.info.uuid === info.uuid)) {
        wallets.push({ info, provider });
        setDetectedEVMWallets([...wallets]);
      }
    };

    window.addEventListener("eip6963:announceProvider", handleAnnounce);
    window.dispatchEvent(new Event("eip6963:requestProvider"));

    // Allow 300ms for all wallets to announce themselves
    setTimeout(() => {
      window.removeEventListener("eip6963:announceProvider", handleAnnounce);

      // TronLink is handled separately via connectTronWallet — exclude it here
      // so it never appears in the EVM picker for non-TRC networks.
      const isTRC =
        paymentType === "trc20" || selectedNetwork === "TRC";
      const evmWallets = isTRC
        ? wallets
        : wallets.filter(
            (w) => !w.info.name.toLowerCase().includes("tron")
          );
      setDetectedEVMWallets(evmWallets);

      if (evmWallets.length === 0) {
        // No EIP-6963 wallets — try legacy window.ethereum (older MetaMask etc.)
        if (window.ethereum) {
          const legacy = [
            {
              info: { name: "Browser Wallet", icon: "", uuid: "legacy" },
              provider: window.ethereum,
            },
          ];
          setDetectedEVMWallets(legacy);
          setShowEVMWalletPicker(true);
        } else {
          showToast(
            "error",
            "No EVM wallet detected. Please install MetaMask, Coinbase Wallet, or another Web3 wallet."
          );
        }
      } else if (evmWallets.length === 1) {
        // Only one wallet installed — connect directly, skip picker
        connectSelectedEVMWallet(evmWallets[0].provider, evmWallets[0].info.name);
      } else {
        // Multiple wallets detected — show picker modal
        setShowEVMWalletPicker(true);
      }
    }, 300);
  };

  // ── TronLink connect — unchanged ──────────────────────────────────────────
  const connectTronWallet = async () => {
    if (externalConnecting) return;
    if (!window.tronWeb) {
      showToast("error", "TronLink not detected. Please install TronLink.");
      return;
    }
    setExternalConnecting(true);
    try {
      await window.tronWeb.request({ method: "tron_requestAccounts" });
      const address = window.tronWeb.defaultAddress.base58;
      console.log("TronLink accounts:", address);
      if (address) {
        const fullNode = window.tronWeb.fullNode.host;

        const EXPECTED_NETWORK = isTestMode ? "NILE" : "MAINNET";

        if (EXPECTED_NETWORK === "NILE" && !fullNode.includes("nile")) {
          showToast("warning", "Please switch TronLink to Nile Testnet");
        }

        if (EXPECTED_NETWORK === "MAINNET" && fullNode.includes("nile")) {
          showToast("warning", "Please switch TronLink to Mainnet");
        }
        setConnectedWallet(address);
        setSelectedOpt("external");
        setTxStatus("");
        setTxHash("");
        setTxConfirmed(false);

        // Call createCharge now that TronLink is connected
        console.log(
          "TronLink connected with address:",
          externalWalletIntentRef.current
        );
        const intentResult = externalWalletIntentRef.current;
        if (intentResult?.paymentIntentId) {
          try {
            const _sp3 = shippingProfile || {};
            const _hPct3 = parseFloat(_sp3.handlingFeeValue || 0);
            const _tPct3 = parseFloat(_sp3.taxRate || 0);
            const _getBase3 = (p) => {
              const priceObj = p.prices?.[0];
              const ce =
                priceObj?.currencies?.find(
                  (c) => c.currency === detectedCurrency.code
                ) ||
                priceObj?.currencies?.find((c) => c.default === true) ||
                priceObj?.currencies?.[0];
              return parseFloat(
                p.basePrice || p.price || p.amount || ce?.amount || 0
              );
            };

            const fiatSubtotal = parseFloat(
              orderProducts.reduce((acc, p) => acc + _getBase3(p), 0).toFixed(2)
            );
            const fiatShippingAmt = parseFloat(
              orderProducts
                .reduce((acc, p) => acc + (_getBase3(p) * _hPct3) / 100, 0)
                .toFixed(2)
            );
            const fiatTaxAmt = parseFloat(
              orderProducts
                .reduce((acc, p) => acc + (_getBase3(p) * _tPct3) / 100, 0)
                .toFixed(2)
            );
            const fiatDiscountAmt = parseFloat(discountAmount || 0);
            const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

            const marketPrice = parseFloat(cryptoMarketPrice || "0");
            const convertToCrypto = (fiatAmount) => {
              if (marketPrice <= 0 || fiatAmount <= 0) return "0";
              return (fiatAmount / marketPrice).toFixed(8);
            };

            const chargePayload = {
              paymentIntentId: intentResult.paymentIntentId,
              amount: String(
                cryptoConvertedAmt ||
                  cryptoConvertedAmtRef.current ||
                  grandTotal ||
                  orderGrandTotal ||
                  "0"
              ),
              currency: selectedCrypto || "USDT",
              marketPrice: String(cryptoMarketPrice || "0"),
              blockchain: "TRON",
              gatewayTransactionId: "",
              walletProvider: "TronLink",
              walletAddress: address,
              network: "TRON",
              baseAmount: convertToCrypto(fiatSubtotal),
              taxAmount: convertToCrypto(fiatTaxAmt),
              discountAmount: convertToCrypto(fiatDiscountAmt),
              shippingAmount: convertToCrypto(fiatShippingAmt),
              processingFeeAmount: convertToCrypto(fiatProcessingFeeAmt),
            };
            console.log("[createCharge][TronLink] Payload:", chargePayload);
            const chargeRes = await fetch(
              `${WEBSERVICE}/checkout/createCharge`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(chargePayload),
              }
            );
            const chargeData = await chargeRes.json();
            if (!chargeData.status) {
              console.error(
                "[createCharge][TronLink] Failed:",
                chargeData.message
              );
              showToast(
                "error",
                chargeData.message ||
                  "Failed to initialize charge. Please try again."
              );
              return;
            } else {
              console.log("[createCharge][TronLink] Success:", chargeData);
            }
          } catch (err) {
            console.error("[createCharge][TronLink] Error:", err);
          }
        }
      } else showToast("error", "Please unlock TronLink and try again.");
    } catch (err) {
      showToast(
        "error",
        err.code === 4001
          ? "TronLink connection cancelled."
          : "Failed to connect TronLink."
      );
    } finally {
      setExternalConnecting(false);
    }
  };

  // ── connectExternalWallet — TRC20 uses TronLink, all others use EIP-6963 ──
  const connectExternalWallet = () => {
    // If TRC is selected but an EVM wallet (0x…) is connected, silently
    // disconnect it so the TronLink flow can run below.
    const needsTron =
      paymentType === "trc20" || selectedNetwork === "TRC";
    if (needsTron && connectedWallet && connectedWallet.startsWith("0x")) {
      setConnectedWallet(null);
      setEthProvider(null);
      setEthSigner(null);
      setTxStatus("");
      setTxHash("");
      setTxConfirmed(false);
      setSelectedOpt(null);
      localStorage.removeItem("last_connected_wallet_type");
      localStorage.removeItem("last_connected_wallet_address");
      localStorage.removeItem("last_connected_wallet_name");
      // fall through — do NOT return — so TronLink connect runs below
    }

    // If wallet is already connected, just create intent + charge directly
    if (connectedWallet && !(needsTron && connectedWallet.startsWith("0x"))) {
      createPaymentIntent(4).then(async (intentResult) => {
        externalWalletIntentRef.current = intentResult || null;
        if (!intentResult?.paymentIntentId) return;
        try {
          const isStablecoin = ["USDT", "USDC"].includes(selectedCrypto);
          const blockchain = isStablecoin
            ? selectedNetwork === "TRC"
              ? "TRON"
              : "ERC"
            : "NATIVE";
          const walletName =
            localStorage.getItem("last_connected_wallet_name") ||
            "Browser Wallet";
          const _sp4 = shippingProfile || {};
          const _hPct4 = parseFloat(_sp4.handlingFeeValue || 0);
          const _tPct4 = parseFloat(_sp4.taxRate || 0);
          const _getBase4 = (p) => {
            const priceObj = p.prices?.[0];
            const ce =
              priceObj?.currencies?.find(
                (c) => c.currency === detectedCurrency.code
              ) ||
              priceObj?.currencies?.find((c) => c.default === true) ||
              priceObj?.currencies?.[0];
            return parseFloat(
              p.basePrice || p.price || p.amount || ce?.amount || 0
            );
          };

          const fiatSubtotal = parseFloat(
            orderProducts.reduce((acc, p) => acc + _getBase4(p), 0).toFixed(2)
          );
          const fiatShippingAmt = parseFloat(
            orderProducts
              .reduce((acc, p) => acc + (_getBase4(p) * _hPct4) / 100, 0)
              .toFixed(2)
          );
          const fiatTaxAmt = parseFloat(
            orderProducts
              .reduce((acc, p) => acc + (_getBase4(p) * _tPct4) / 100, 0)
              .toFixed(2)
          );
          const fiatDiscountAmt = parseFloat(discountAmount || 0);
          const fiatProcessingFeeAmt = parseFloat(orderFeeAmount || 0);

          const marketPrice = parseFloat(cryptoMarketPrice || "0");
          const convertToCrypto = (fiatAmount) => {
            if (marketPrice <= 0 || fiatAmount <= 0) return "0";
            return (fiatAmount / marketPrice).toFixed(8);
          };

          const chargePayload = {
            paymentIntentId: intentResult.paymentIntentId,
            amount: String(
              cryptoConvertedAmt ||
                cryptoConvertedAmtRef.current ||
                grandTotal ||
                orderGrandTotal ||
                "0"
            ),
            currency: selectedCrypto || "ETH",
            marketPrice: String(cryptoMarketPrice || "0"),
            blockchain,
            gatewayTransactionId: "",
            walletProvider: walletName,
            walletAddress: connectedWallet,
            network: blockchain,
            baseAmount: convertToCrypto(fiatSubtotal),
            taxAmount: convertToCrypto(fiatTaxAmt),
            discountAmount: convertToCrypto(fiatDiscountAmt),
            shippingAmount: convertToCrypto(fiatShippingAmt),
            processingFeeAmount: convertToCrypto(fiatProcessingFeeAmt),
          };
          console.log(
            "[createCharge][already-connected] Payload:",
            chargePayload
          );
          const chargeRes = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(chargePayload),
          });
          const chargeData = await chargeRes.json();
          if (!chargeData.status) {
            console.error(
              "[createCharge][already-connected] Failed:",
              chargeData.message
            );
          } else {
            console.log(
              "[createCharge][already-connected] Success:",
              chargeData
            );
            externalWalletChargeRef.current = chargeData.data || null;
          }
        } catch (err) {
          console.error("[createCharge][already-connected] Error:", err);
        }
      });
      return;
    }

    // Wallet not connected yet — initiate connection flow
    createPaymentIntent(4).then((intentResult) => {
      externalWalletIntentRef.current = intentResult || null;
      if (paymentType === "trc20" || selectedNetwork === "TRC") {
        connectTronWallet();
      } else {
        initEIP6963AndConnect();
      }
    });
  };

  // Maps raw Web3 / MetaMask error codes and message strings to user-friendly text.
  // Handles "unknown_error", "invalid RPC url", rejected transactions, etc.
  const getFriendlyWalletError = (err) => {
    const code = err?.code;
    const msg = (err?.message || "").toLowerCase();

    if (
      code === 4001 ||
      msg.includes("user rejected") ||
      msg.includes("user denied")
    )
      return "Transaction cancelled.";
    if (code === 4902)
      return "This network is not added to your wallet. Please add it manually and try again.";
    if (code === -32603 || msg.includes("internal json-rpc"))
      return "A network error occurred. Please try again.";
    if (code === -32005)
      return "Too many requests. Please wait a moment and try again.";
    if (msg.includes("insufficient"))
      return "Insufficient funds to complete this payment.";
    if (msg.includes("unknown_error") || msg.includes("unknown error"))
      return "Something went wrong. Please try again or contact support.";
    if (
      msg.includes("invalid rpc") ||
      msg.includes("rpc url") ||
      msg.includes("rpc error") ||
      msg.includes("could not detect network")
    )
      return "Network connection error. Please check your internet connection and try again.";
    if (msg.includes("timeout") || msg.includes("timed out"))
      return "The request timed out. Please try again.";
    if (msg.includes("network") || msg.includes("connection"))
      return "Network connection error. Please try again.";
    if (msg.includes("nonce"))
      return "Transaction error (nonce mismatch). Please reset your wallet activity and try again.";
    return "Transaction failed. Please try again.";
  };

  const payETH = async () => {
    setTxStatus("Creating transaction…");
    const amt =
      cryptoConvertedAmtRef.current || cryptoConvertedAmt || grandTotal;
    const toAddress =
      externalWalletChargeRef.current?.receivingAddress || ETH_MERCHANT_ADDRESS;
    const tx = await ethSigner.sendTransaction({
      to: toAddress,
      value: parseEther(parseFloat(amt).toFixed(8)),
    });
    setTxHash(tx.hash);
    setTxStatus("Transaction submitted ✓ Redirecting…");
    setTxConfirmed(true);
    await redirectAfterTx(tx.hash, selectedCrypto || "ETH");
  };
  const redirectAfterTx = (hash, crypto) => {
    window.location.href = buildRedirectUrl(
      redirectSuccessUrl ||
        `${window.location.origin}/merchant/checkout/success`,
      hash,
      "success",
      loginEmail || "",
      String(
        cryptoConvertedAmtRef.current || cryptoConvertedAmt || grandTotal || "0"
      ),
      crypto,
      redirectAdvanceOptions
    );
  };
  const payERC20 = async () => {
    const cur = (selectedCrypto || invoiceData?.currency || "").toUpperCase();
    const tokenAddress =
      cur === "USDC" ? ERC20_USDC_ADDRESS : ERC20_USDT_ADDRESS;
    const decimals = ERC20_DECIMALS[cur] || 6;
    setTxStatus(`Creating ${cur} token transaction…`);
    const token = new Contract(tokenAddress, ERC20_ABI, ethSigner);
    const amt =
      cryptoConvertedAmtRef.current || cryptoConvertedAmt || grandTotal;
    const toAddress =
      externalWalletChargeRef.current?.receivingAddress || ETH_MERCHANT_ADDRESS;
    console.log(
      `[payERC20] ${cur} | contract: ${tokenAddress} | to: ${toAddress} | amt: ${amt} | decimals: ${decimals} | network: ${NETWORK_MODE}`
    );
    const tx = await token.transfer(
      toAddress,
      parseUnits(parseFloat(amt).toFixed(decimals), decimals)
    );
    setTxHash(tx.hash);
    setTxStatus(`${cur} TX submitted ✓ Redirecting…`);
    setTxConfirmed(true);
    await redirectAfterTx(tx.hash, cur);
  };
  const payTRC20 = async () => {
    setTxStatus("Sending TRC20 token via TronLink…");
    // Contract address is the TRC20 token contract, not the receiving address
    const trc20ContractAddress = TRC20_USDT_ADDRESS;
    const contract = await window.tronWeb.contract().at(trc20ContractAddress);
    const amt =
      cryptoConvertedAmtRef.current || cryptoConvertedAmt || grandTotal;
    const tronToAddress =
      externalWalletChargeRef.current?.receivingAddress ||
      TRON_MERCHANT_ADDRESS;
    console.log(
      `[payTRC20] contract: ${trc20ContractAddress} | to: ${tronToAddress} | amt: ${amt} | network: ${NETWORK_MODE}`
    );
    const txId = await contract
      .transfer(tronToAddress, Math.round(parseFloat(amt) * 10 ** 6))
      .send();
    setTxHash(txId);
    setTxStatus("TRC20 TX submitted ✓ Redirecting…");
    setTxConfirmed(true);
    await redirectAfterTx(txId, selectedCrypto || "USDT");
  };
  const monitorEthTransaction = async (hash) => {
    if (!ethProvider) return;
    while (true) {
      try {
        const receipt = await ethProvider.getTransactionReceipt(hash);
        if (receipt) {
          const c = await receipt.confirmations();
          setTxStatus(`Confirmations: ${c} / 2`);
          if (c >= 2) {
            setTxStatus("Transaction confirmed ✓");
            setTxConfirmed(true);

            // ── Call confirmation API ────────────────────────────────────────
            try {
              const intentResult = externalWalletIntentRef.current;
              const confirmRes = await fetch(
                `${WEBSERVICE}/checkout/confirmPayment`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    paymentIntentId: intentResult?.paymentIntentId || "",
                    txHash: hash,
                    walletAddress: connectedWallet || "",
                    currency: selectedCrypto || "",
                    amount: String(cryptoConvertedAmt || grandTotal || "0"),
                  }),
                }
              );
              const confirmData = await confirmRes.json();
              console.log("[confirmPayment][EVM] Response:", confirmData);

              const isSuccess =
                confirmData.status === true || confirmData.status === "success";
              const paymentId =
                confirmData.data?.paymentId || confirmData.paymentId || hash;
              const redirectBase = isSuccess
                ? redirectSuccessUrl ||
                  `${window.location.origin}/merchant/checkout/success`
                : redirectFailureUrl ||
                  `${window.location.origin}/merchant/checkout/failure`;

              setTimeout(() => {
                window.location.href = buildRedirectUrl(
                  redirectBase,
                  paymentId,
                  isSuccess ? "success" : "failed",
                  loginEmail || "",
                  String(cryptoConvertedAmt || grandTotal || "0"),
                  selectedCrypto || "",
                  redirectAdvanceOptions
                );
              }, 1500);
            } catch (apiErr) {
              console.error("[confirmPayment][EVM] API error:", apiErr);
              // Fallback — redirect to success anyway since chain confirmed
              setTimeout(() => {
                window.location.href = buildRedirectUrl(
                  redirectSuccessUrl ||
                    `${window.location.origin}/merchant/checkout/success`,
                  hash,
                  "success",
                  loginEmail || "",
                  String(cryptoConvertedAmt || grandTotal || "0"),
                  selectedCrypto || "",
                  redirectAdvanceOptions
                );
              }, 1500);
            }
            break;
          }
        }
      } catch {}
      await new Promise((r) => setTimeout(r, 3000));
    }
  };
  const monitorTRC20Transaction = async (txid) => {
    while (true) {
      try {
        const info = await window.tronWeb.trx.getTransactionInfo(txid);
        if (info?.receipt?.result === "SUCCESS") {
          setTxStatus("TRC20 transaction confirmed ✓");
          setTxConfirmed(true);

          // ── Call confirmation API ────────────────────────────────────────
          try {
            const intentResult = externalWalletIntentRef.current;
            const confirmRes = await fetch(
              `${WEBSERVICE}/checkout/confirmPayment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  paymentIntentId: intentResult?.paymentIntentId || "",
                  txHash: txid,
                  walletAddress: connectedWallet || "",
                  currency: selectedCrypto || "USDT",
                  amount: String(cryptoConvertedAmt || grandTotal || "0"),
                  blockchain: "TRON",
                }),
              }
            );
            const confirmData = await confirmRes.json();
            console.log("[confirmPayment][TRC20] Response:", confirmData);

            const isSuccess =
              confirmData.status === true || confirmData.status === "success";
            const paymentId =
              confirmData.data?.paymentId || confirmData.paymentId || txid;
            const redirectBase = isSuccess
              ? redirectSuccessUrl ||
                `${window.location.origin}/merchant/checkout/success`
              : redirectFailureUrl ||
                `${window.location.origin}/merchant/checkout/failure`;

            setTimeout(() => {
              window.location.href = buildRedirectUrl(
                redirectBase,
                paymentId,
                isSuccess ? "success" : "failed",
                loginEmail || "",
                String(cryptoConvertedAmt || grandTotal || "0"),
                selectedCrypto || "USDT",
                redirectAdvanceOptions
              );
            }, 1500);
          } catch (apiErr) {
            console.error("[confirmPayment][TRC20] API error:", apiErr);
            // Fallback — redirect to success anyway since chain confirmed
            setTimeout(() => {
              window.location.href = buildRedirectUrl(
                redirectSuccessUrl ||
                  `${window.location.origin}/merchant/checkout/success`,
                txid,
                "success",
                loginEmail || "",
                String(cryptoConvertedAmt || grandTotal || "0"),
                selectedCrypto || "USDT",
                redirectAdvanceOptions
              );
            }, 1500);
          }
          break;
        }
      } catch {}
      await new Promise((r) => setTimeout(r, 3000));
    }
  };
  const proceedWithExternalWallet = async () => {
    if (!connectedWallet || !(grandTotal || orderGrandTotal)) return;
    if (!cryptoConvertedAmtRef.current && !cryptoConvertedAmt) {
      showToast("error", "Please select a cryptocurrency first.");
      return;
    }
    setExternalConnecting(true);
    const crypto = (
      selectedCrypto ||
      invoiceData?.currency ||
      ""
    ).toUpperCase();
    const network = (selectedNetwork || "").toUpperCase();
    const effectiveType =
      network === "TRC"
        ? "trc20"
        : (crypto === "USDT" || crypto === "USDC") && network === "ERC"
        ? "erc20"
        : crypto === "ETH" ||
          (!network && crypto !== "USDT" && crypto !== "USDC")
        ? "eth"
        : paymentType;
    // ── TronLink network validation before proceeding ──────────────────────
    if (effectiveType === "trc20" && window.tronWeb) {
      const fullNode = window.tronWeb.fullNode?.host || "";
      const isTronTestnet = fullNode.includes("nile");
      if (isTestMode && !isTronTestnet) {
        showToast("warning", "Please switch TronLink to Nile Testnet");
        setExternalConnecting(false);
        return;
      }
      if (!isTestMode && isTronTestnet) {
        showToast("warning", "Please switch TronLink to Mainnet");
        setExternalConnecting(false);
        return;
      }
    }

    try {
      if (effectiveType === "trc20") await payTRC20();
      else if (effectiveType === "erc20") await payERC20();
      else await payETH();
    } catch (err) {
      const msg = getFriendlyWalletError(err);
      showToast("error", msg);
      setTxStatus("");

      const paymentId = "";
      const customerEmail = loginEmail || "";
      const amount = String(cryptoConvertedAmt || grandTotal || "0");
      const status = "failed";

      const redirectUrl = buildRedirectUrl(
        redirectFailureUrl ||
          `${window.location.origin}/merchant/checkout/failure`,
        paymentId,
        status,
        customerEmail,
        amount,
        crypto,
        redirectAdvanceOptions
      );

      const intentId = externalWalletIntentRef.current?.paymentIntentId;
      if (intentId) {
        try {
          const updateRes = await fetch(
            `${WEBSERVICE}/payment/updatePaymentStatus`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ paymentIntentId: intentId }),
            }
          );
          const updateData = await updateRes.json();
          if (updateData.status) {
            window.location.href = redirectUrl;
          }
        } catch {
          // API call failed, do not redirect
        }
      }
    } finally {
      setExternalConnecting(false);
    }
  };

  const fetchWalletBalance = async (token, uuid, intentId = null) => {
    const activeIntentId = intentId || paymentIntentId;
    if (isTestMode && !activeIntentId) return;

    try {
      if (isTestMode) {
        const response = await fetch(
          `${WEBSERVICE}/payment/all-currency-wallet-balances?intentId=${activeIntentId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        const result = await response.json();
        if (result.status) {
          const cur = (
            selectedCryptoRef.current ||
            invoiceData?.currency ||
            ""
          ).toUpperCase();
          const selectedCoin = CRYPTO_MASTER_LIST.find(
            (c) => c.currency_code === cur
          );
          if (selectedCoin) {
            const match = (result.data || []).find(
              (b) => b.currencyId === selectedCoin.currency_id
            );
            setWalletBalance(match ? parseFloat(match.balance) : 0);
          } else {
            setWalletBalance(0);
          }
        } else {
          setWalletBalance(0);
        }
      } else {
        const response = await fetch(
          `${PAYBITO_ACCOUNTS}/transaction/getUserBalance`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: "BEARER " + token,
            },
            body: JSON.stringify({
              customerId:
                localStorage.getItem(SITENAMEALIAS + "_paybito_id") ||
                localStorage.getItem("userId") ||
                localStorage.getItem("user_id"),
              uuid,
            }),
          }
        );
        if (response.status === 401) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("uuid");
          localStorage.removeItem("userId");
          setPaybitoToken("");
          setPaybitoUserId("");
          setPaybitoUuid("");
          setWalletBalance(0);
          showToast("error", "Brand wallet session expired. Please login again.");
          initWalletInline();
          return;
        }
        const result = await response.json();
        const cur = (
          selectedCryptoRef.current ||
          invoiceData?.currency ||
          ""
        ).toUpperCase();
        const match = (result.userBalanceList || []).find(
          (b) => b.currencyCode?.toUpperCase() === cur
        );
        setWalletBalance(match ? parseFloat(match.closingBalance) : 0);
      }
    } catch {
      setWalletBalance(0);
    }
  };

  const connectBalanceWs = (uuid) => {
    disconnectBalanceWs();
    balanceMapRef.current = {};
    const WS_URL = "https://api.paybito.com:9443/TransactionApi/ws/balance";
    const client = new Client({
      webSocketFactory: () => new SockJS(WS_URL),
      reconnectDelay: 5000,
      onConnect: () => {
        client.subscribe(`/topic/balance/${uuid}`, (message) => {
          try {
            const data = JSON.parse(message.body);
            const cur = (data.currency || "").toUpperCase();
            if (cur) {
              balanceMapRef.current[cur] = parseFloat(data.balance) || 0;
              const activeCur = (
                selectedCryptoRef.current ||
                invoiceData?.currency ||
                ""
              ).toUpperCase();
              if (cur === activeCur) {
                setWalletBalance(balanceMapRef.current[cur]);
              }
            }
          } catch {
            /* ignore malformed messages */
          }
        });
      },
      onStompError: () => {},
      onWebSocketClose: () => {},
    });
    client.activate();
    stompClientRef.current = client;
  };

  const disconnectBalanceWs = () => {
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
    }
  };
  const handleWalletPayment = async () => {
    try {
      setPayLoading(true);

      const isStablecoin = ["USDT", "USDC"].includes(selectedCrypto);
      let blockchain = isStablecoin ? "ERC" : "NATIVE";
      if (
        isStablecoin &&
        (selectedNetwork === "TRC" || paymentType === "trc20")
      ) {
        blockchain = "TRON";
      }

      // ── createCharge must be called first for brand wallet ────────────
      const walletChargePayload = {
        paymentIntentId,
        amount: String(
          cryptoConvertedAmtRef.current || cryptoConvertedAmt || grandTotal || "0"
        ),
        currency: selectedCrypto,
        marketPrice: String(cryptoMarketPrice || "0"),
        blockchain,
        gatewayTransactionId: "",
        walletProvider: "Crypto",
        walletAddress: null,
        network: blockchain,
        baseAmount: String(convertBaseAmount || "0"),
        taxAmount: String(convertTaxAmount || "0"),
        discountAmount: String(convertDiscountAmountValue || "0"),
        shippingAmount: String(convertShippingAmount || "0"),
        processingFeeAmount: String(convertProcessingFeeAmount || "0"),
      };
      console.log("[createCharge][brand wallet] Payload:", walletChargePayload);
      const walletChargeRes = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(walletChargePayload),
      });
      const walletChargeData = await walletChargeRes.json();
      if (!walletChargeData.status) {
        showToast(
          "error",
          walletChargeData.message || "Failed to initialize charge. Please try again."
        );
        return;
      }
      console.log("[createCharge][brand wallet] Success:", walletChargeData);
      // ─────────────────────────────────────────────────────────────────

      if (isTestMode) {
        const payload = {
          intentId: paymentIntentId,
          amount: String(
            cryptoConvertedAmt ||
              invoiceData?.amount_to_be_paid ||
              grandTotal ||
              "0"
          ),
          currency: selectedCrypto,
          marketPrice: String(cryptoMarketPrice || "0"),
          network: blockchain,
          baseAmount: String(convertBaseAmount || "0"),
          taxAmount: String(convertTaxAmount || "0"),
          discountAmount: String(convertDiscountAmountValue || "0"),
          shippingAmount: String(convertShippingAmount || "0"),
          processingFeeAmount: String(convertProcessingFeeAmount || "0"),
        };

        const response = await fetch(
          `${WEBSERVICE}/payment/brand-wallet-checkout`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              //Authorization: "bearer " + getAuthToken(),
              //UUID: getAuthUuid(),
            },
            body: JSON.stringify(payload),
          }
        );
        const data = await response.json();
        if (data.status) {
          showToast("success", data.data?.message || "Payment successful");
          stopMoonPayPolling();
          const redirectUrl = buildRedirectUrl(
            redirectSuccessUrl ||
              `${window.location.origin}/merchant/checkout/success`,
            paymentIntentId || "",
            "success",
            loginEmail || "",
            String(
              cryptoConvertedAmtRef.current ||
                cryptoConvertedAmt ||
                grandTotal ||
                "0"
            ),
            selectedCrypto,
            redirectAdvanceOptions
          );
          window.location.href = redirectUrl;
        } else {
          showToast("error", data.message || "Payment failed");
        }
      } else {
        // Compute shipping (handling fee) and tax amounts
        const sp = shippingProfile || {};
        const hPct = parseFloat(sp.handlingFeeValue || 0);
        const tPct = parseFloat(sp.taxRate || 0);
        const cur = detectedCurrency;
        const getBase = (p) => {
          const priceObj = p.prices?.[0];
          const ce =
            priceObj?.currencies?.find((c) => c.currency === cur.code) ||
            priceObj?.currencies?.find((c) => c.default === true) ||
            priceObj?.currencies?.[0];
          return parseFloat(
            p.basePrice || p.price || p.amount || ce?.amount || 0
          );
        };

        const payload = {
          uuid: paybitoUuid,
          userId: paybitoUserId,
          intentId: paymentIntentId,
          currency: selectedCrypto,
          sendAmount: Number(
            cryptoConvertedAmt ||
              invoiceData?.amount_to_be_paid ||
              grandTotal ||
              0
          ),
          marketPrice: Number(cryptoMarketPrice || 0),
          baseAmount: Number(convertBaseAmount) || 0,
          shippingAmount: Number(convertShippingAmount) || 0,
          taxAmount: Number(convertTaxAmount),
          processingFeeAmount: Number(convertProcessingFeeAmount || 0),
          discountAmount: Number(convertDiscountAmountValue || 0),
        };
        const response = await fetch(
          `${PAYBITO_ACCOUNTS}/transaction/exchange-wallet-payment-checkout`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: "BEARER " + localStorage.getItem("access_token"),
            },
            body: JSON.stringify(payload),
          }
        );
        const data = await response.json();
        if (data.error?.error_data === 0) {
          showToast("success", data.error?.error_msg || "Payment successful");
          stopMoonPayPolling();
          const redirectUrl = buildRedirectUrl(
            redirectSuccessUrl ||
              `${window.location.origin}/merchant/checkout/success`,
            paymentIntentId || "",
            "success",
            loginEmail || "",
            String(
              cryptoConvertedAmtRef.current ||
                cryptoConvertedAmt ||
                grandTotal ||
                "0"
            ),
            selectedCrypto,
            redirectAdvanceOptions
          );
          window.location.href = redirectUrl;
        } else showToast("error", data.error?.error_msg || "Payment failed");
      }
    } catch (error) {
      console.error("Wallet payment error:", error);
      showToast("error", "Something went wrong");
    } finally {
      setPayLoading(false);
    }
  };
  const stopMoonPayPolling = () => {
    if (moonPayBalanceIntervalRef.current) {
      clearInterval(moonPayBalanceIntervalRef.current);
      moonPayBalanceIntervalRef.current = null;
    }
    if (moonPayBalanceIntervalRef._visibilityHandler) {
      document.removeEventListener(
        "visibilitychange",
        moonPayBalanceIntervalRef._visibilityHandler
      );
      moonPayBalanceIntervalRef._visibilityHandler = null;
    }
    if (moonPayBalanceIntervalRef._moonPayTabCheck) {
      clearInterval(moonPayBalanceIntervalRef._moonPayTabCheck);
      moonPayBalanceIntervalRef._moonPayTabCheck = null;
    }
  };
  const handleMoonPayRedirection = (currencyCode) => {
    // Prefer currencyId from profileDetailsById (billingProfile.currencies) as the authoritative source
    const billingCurrency = billingProfile?.currencies?.find(
      (c) => (c.currency || "").toUpperCase() === (currencyCode || "").toUpperCase()
    );
    const coin = CRYPTO_MASTER_LIST.find(
      (c) => c.currency_code === currencyCode
    );
    const resolvedCurrencyId = billingCurrency?.currencyId ?? coin?.currency_id ?? null;
    stopMoonPayPolling();
    setPabtekPollingError("");
    setPabtekCurrencyCode(currencyCode);
    setPabtekCurrencyId(resolvedCurrencyId);
    // Forward the network already chosen on the checkout page
    setPabtekSelectedNetwork(selectedNetwork || "");
    setShowPabtekPopup(true);
  };

  const handleMoonPayPopupClose = () => {
    stopPabtekPolling();
    setShowPabtekPopup(false);
    setPabtekSelectedNetwork("");
    setPabtekDirectPolling(false);
    stopMoonPayPolling();
  };

  const stopPabtekPolling = () => {
    if (pabtekPollingRef.current) {
      clearInterval(pabtekPollingRef.current);
      pabtekPollingRef.current = null;
    }
  };

  // ── Pabtek card payment polling ───────────────────────────────────────────
  // Called by PabtekPaymentPopup when the external payment link is opened.
  // Polls every 5 s for payment status.
  const handlePabtekPaymentLinkOpened = ({ amount, currencyId, currencyCode, tokenType, marketPrice, paymentLinkId }) => {
    stopPabtekPolling();

    // Derive blockchain string from tokenType (erc→ERC, trc→TRON, bep→BSC, pol→POL, NATIVE→NATIVE)
    const tokenTypeToBlockchain = { erc: "ERC", trc: "TRON", bep: "BSC", pol: "POL", NATIVE: "NATIVE" };
    const blockchain = tokenTypeToBlockchain[tokenType] || "NATIVE";

    const buildChargePayload = () => ({
      paymentIntentId,
      amount: String(amount || "0"),
      currency: currencyCode,
      marketPrice: String(marketPrice || "0"),
      blockchain,
      gatewayTransactionId: "",
      walletProvider: "Card",
      walletAddress: null,
      network: blockchain,
      baseAmount: String(convertBaseAmount || "0"),
      taxAmount: String(convertTaxAmount || "0"),
      discountAmount: String(convertDiscountAmountValue || "0"),
      shippingAmount: String(convertShippingAmount || "0"),
      processingFeeAmount: String(convertProcessingFeeAmount || "0"),
    });

    const handlePabtekSuccess = async () => {
      stopPabtekPolling();
      try {
        // 1. createCharge
        const chargeRes = await fetch(`${WEBSERVICE}/checkout/createCharge`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildChargePayload()),
        });
        const chargeData = await chargeRes.json();
        if (!chargeData.status) {
          showToast("error", chargeData.message || "Failed to finalize charge.");
          handleMoonPayPopupClose();
          return;
        }

        // 2. exchange-wallet-payment-checkout
        const checkoutPayload = {
          uuid: paybitoUuid,
          userId: paybitoUserId,
          intentId: paymentIntentId,
          currency: currencyCode,
          sendAmount: Number(amount || 0),
          marketPrice: Number(marketPrice || 0),
          baseAmount: Number(convertBaseAmount) || 0,
          shippingAmount: Number(convertShippingAmount) || 0,
          taxAmount: Number(convertTaxAmount) || 0,
          processingFeeAmount: Number(convertProcessingFeeAmount) || 0,
          discountAmount: Number(convertDiscountAmountValue) || 0,
        };
        const checkoutRes = await fetch(
          `${PAYBITO_ACCOUNTS}/transaction/exchange-wallet-payment-checkout`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: "BEARER " + localStorage.getItem("access_token"),
            },
            body: JSON.stringify(checkoutPayload),
          }
        );
        const checkoutData = await checkoutRes.json();

        handleMoonPayPopupClose();
        if (checkoutData.error?.error_data === 0) {
          showToast("success", checkoutData.error?.error_msg || "Payment successful");
          const redirectUrl = buildRedirectUrl(
            redirectSuccessUrl || `${window.location.origin}/merchant/checkout/success`,
            paymentIntentId || "",
            "success",
            loginEmail || "",
            String(amount || "0"),
            currencyCode,
            redirectAdvanceOptions
          );
          window.location.href = redirectUrl;
        } else {
          showToast("error", checkoutData.error?.error_msg || "Payment checkout failed.");
        }
      } catch (err) {
        console.error("[Pabtek polling] success handler error:", err);
        showToast("error", "Something went wrong while confirming your payment.");
        handleMoonPayPopupClose();
      }
    };

    const handlePabtekFailure = async () => {
      stopPabtekPolling();
      try {
        // createCharge to record the failed attempt
        await fetch(`${WEBSERVICE}/checkout/createCharge`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildChargePayload()),
        });
      } catch (err) {
        console.error("[Pabtek polling] failure createCharge error:", err);
      }
      handleMoonPayPopupClose();
      const failureUrl = buildRedirectUrl(
        redirectFailureUrl || `${window.location.origin}/merchant/checkout/failure`,
        paymentIntentId || "",
        "failed",
        loginEmail || "",
        String(amount || "0"),
        currencyCode,
        redirectAdvanceOptions
      );
      window.location.href = failureUrl;
    };

    pabtekPollingRef.current = setInterval(async () => {
      try {
        const statusRes = await fetch(
          "https://accounts.paybito.com/api/transaction/checkPaymentStatusByPaymentIntent",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: `BEARER ${localStorage.getItem("access_token")}`,
            },
            body: JSON.stringify({
              paymentIntentId: paymentIntentId || "",
              counterCurrencyId: String(currencyId ?? ""),
              tokenType: tokenType || "",
              userUuid: paybitoUuid || "",
              paymentLinkId: paymentLinkId || "",
            }),
          }
        );
        const statusData = await statusRes.json();

        // Show error_msg in popup but keep polling
        if (statusData?.error?.error_data === 1) {
          setPabtekPollingError(
            statusData.error.error_msg || "An error occurred while checking payment status."
          );
          return; // continue interval
        }

        // Clear any previous error on a clean response
        setPabtekPollingError("");

        const isProcessed = statusData?.isPaymentProcessed;

        if (String(isProcessed) === "1") {
          stopPabtekPolling();
          handleMoonPayPopupClose();
          handleWalletPayment();
        } else if (String(isProcessed) === "2") {
          handlePabtekFailure();
        }
        // isPaymentProcessed === undefined/0 or any other value — continue polling
      } catch (err) {
        console.error("[Pabtek polling] status check error:", err);
      }
    }, 5000);
  };

  const autoLoginRedirectToPage = (targetPage) => {
    const ALIAS = SITENAMEALIAS;
    const accessToken = getAuthToken();
    const uuid = getAuthUuid();
    if (!accessToken || !uuid) {
      const domain =
        localStorage.getItem(ALIAS + "_brokerDomain") || window.location.origin;
      const normalizedDomain = domain.endsWith("/")
        ? domain.slice(0, -1)
        : domain;
      const normalizedTargetPage = targetPage.startsWith("/")
        ? targetPage.slice(1)
        : targetPage;
      window.open(`${normalizedDomain}/${normalizedTargetPage}`, "_blank");
      return;
    }
    fetch(`${WEBSERVICE}/merchant/auto-login/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
        UUID: uuid,
      },
      body: JSON.stringify({
        merchantId: localStorage.getItem(ALIAS + "_merchant_id"),
        brokerId: exchangeBrokerId || getBrokerIdFromUrl(),
        uuid,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error?.error_data !== 0) {
          showToast(
            "error",
            data.error?.error_message || "Failed to initialize session."
          );
          return;
        }
        return fetch(`${PAYBITO_ACCOUNTS}/user/GetUserDetails`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + data.token,
          },
          body: JSON.stringify({ uuid: data.uuid, userId: data.userId }),
        })
          .then((r) => r.json())
          .then((ud) => {
            const u = ud.userResult;
            const params = new URLSearchParams({
              brokerId: u.brokerId,
              uuid: u.uuid,
              email: u.email,
              token: data.token,
              callFrom: "payments",
              redirectTo: targetPage,
            });
            window.open(
              `${window.location.origin}/autologin?${params}`,
              "_blank"
            );
          });
      })
      .catch(() => showToast("error", "Failed to connect to exchange."));
  };

  const handleCryptoAmtInputChange = (e) => {
    const val = e.target.value;
    setCryptoConvertedAmt(val);
    cryptoConvertedAmtRef.current = val;
    const num = parseFloat(val);
    if (!val || isNaN(num) || num <= 0) {
      setCryptoAmtInputError("Enter a valid amount.");
    } else if (minCryptoAmt && num < parseFloat(minCryptoAmt)) {
      setCryptoAmtInputError(
        `Must be ≥ ${minCryptoAmt} ${selectedCrypto || ""}.`
      );
    } else {
      setCryptoAmtInputError("");
    }
  };

  const handleUSDCDirectPayment = async () => {
    if (!cryptoConvertedAmt) {
      showToast("error", "Please wait for price to load.");
      return;
    }
    setPabtekDirectLoading(true);
    try {
      // Step 1: Fetch market price
      const priceRes = await fetch(
        `${PAYBITO_ACCOUNTS}/home/marketPrice`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ currency: "USDC", baseCurrency: "USD", action: 1 }),
        }
      );
      const priceData = await priceRes.json();
      const unitRate = priceData?.marketPrice;
      if (priceData?.error?.error_data !== 0 || !unitRate || unitRate <= 0) {
        showToast("error", priceData?.error?.error_msg || "Failed to fetch market price.");
        return;
      }

      // Step 2: Resolve currency ID
      const billingCurrency = billingProfile?.currencies?.find(
        (c) => (c.currency || "").toUpperCase() === "USDC"
      );
      const coin = CRYPTO_MASTER_LIST.find((c) => c.currency_code === "USDC");
      const resolvedCurrencyId = billingCurrency?.currencyId ?? coin?.currency_id ?? null;
      const tokenType = "erc"; // USDC is always ERC-20
      // If user has a partial balance, only charge the shortfall
      const totalRequired = parseFloat(cryptoConvertedAmt || 0);
      const walletBalanceNum = parseFloat(walletBalance || 0);
      const counterAmount =
        walletBalanceNum > 0 && walletBalanceNum < totalRequired
          ? Math.max(0, totalRequired - walletBalanceNum)
          : totalRequired;
      const usdAmount = counterAmount * parseFloat(unitRate);

      // Step 3: Generate payment link directly
      const linkRes = await fetch(
        `${PAYBITO_ACCOUNTS}/transaction/generatePabtekPaymentLink`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${localStorage.getItem("access_token")}`,
          },
          body: JSON.stringify({
            userUuid: paybitoUuid || "",
            baseCurrencyId: "1",
            amount: String(usdAmount),
            counterCurrencyId: String(resolvedCurrencyId ?? ""),
            counterAmount: String(counterAmount),
            marketPrice: String(unitRate),
            tokenType,
            paymentIntentId: paymentIntentId || "",
          }),
        }
      );
      const linkData = await linkRes.json();

      if (linkData?.paymentLink) {
        // Step 4: Open payment link in new tab
        window.open(linkData.paymentLink, "_blank", "noopener,noreferrer");

        // Step 5: Show only the polling popup (skip amount-entry screen)
        stopMoonPayPolling();
        setPabtekPollingError("");
        setPabtekCurrencyCode("USDC");
        setPabtekCurrencyId(resolvedCurrencyId);
        setPabtekSelectedNetwork(selectedNetwork || "");
        setPabtekDirectPolling(true);
        setShowPabtekPopup(true);

        // Step 6: Start status polling
        handlePabtekPaymentLinkOpened({
          amount: counterAmount,
          currencyId: resolvedCurrencyId,
          currencyCode: "USDC",
          tokenType,
          marketPrice: parseFloat(unitRate),
          paymentLinkId: linkData.paymentLinkId || "",
        });
      } else {
        showToast(
          "error",
          linkData?.error?.error_msg || "Failed to generate payment link. Please try again."
        );
      }
    } catch {
      showToast("error", "Something went wrong. Please try again.");
    } finally {
      setPabtekDirectLoading(false);
    }
  };

  const handleBuyCrypto = () => {
    const crypto = (selectedCrypto || "").toUpperCase();
    if (crypto === "USDC") {
      handleUSDCDirectPayment();
    } else {
      handleMoonPayRedirection(crypto);
    }
  };

  const handleReceiveCrypto = () => {
    autoLoginRedirectToPage("my-wallet");
  };

  const fetchDepositAddress = async () => {
    const crypto = (selectedCrypto || "").toUpperCase();
    const coin = CRYPTO_MASTER_LIST.find((c) => c.currency_code === crypto);
    if (!coin || !paybitoToken || !paybitoUuid) return;
    setDepositLoading(true);
    setDepositAddress("");
    setDepositTag(null);
    try {
      // Step 1: Get the default token type for this currency
      let tokenType = "";
      try {
        const tokenRes = await fetch(
          `${PAYBITO_ACCOUNTS}/transaction/currencyTokenTypeById`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              authorization: "BEARER " + paybitoToken,
            },
            body: JSON.stringify({ currencyId: coin.currency_id }),
          }
        );
        const tokenData = await tokenRes.json();
        if (tokenData.error?.error_data === 0 && tokenData.response) {
          const match = tokenData.response.find(
            (t) => t.currencyId === coin.currency_id
          );
          if (match) tokenType = match.tokenType || "";
        }
      } catch {}

      // Step 2: Override token type for multi-network currencies
      if (crypto === "USDT" || crypto === "HCX") {
        const net = selectedNetwork || "ERC";
        tokenType = net === "BSC" || net === "bsc" ? "bep" : net.toLowerCase();
      } else {
        // For other currencies, use the default token type from step 1
      }

      // Step 3: Call getCryptoAddress (same API as exchange My Wallet)
      const rcvPayload = {
        uuid: paybitoUuid,
        currencyId: coin.currency_id,
        marginType: "1",
        tokenType: tokenType,
      };
      const response = await fetch(
        `${PAYBITO_ACCOUNTS}/transaction/getCryptoAddress`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
          body: JSON.stringify(rcvPayload),
        }
      );
      const data = await response.json();
      if (data.error?.error_data === 0 && data.customerLedgerResult) {
        setDepositAddress(data.customerLedgerResult.publicKey || "");
        setDepositTag(data.customerLedgerResult.memo || null);
      } else {
        showToast(
          "error",
          data.error?.error_msg || "Failed to generate deposit address."
        );
        setDepositAddress("");
      }
    } catch {
      showToast("error", "Failed to fetch deposit address.");
      setDepositAddress("");
    } finally {
      setDepositLoading(false);
    }
  };

  const startDepositBalancePolling = () => {
    stopDepositBalancePolling();
    // Balance updates are now received via WebSocket in real-time
  };

  const stopDepositBalancePolling = () => {
    if (depositBalancePollRef.current) {
      clearInterval(depositBalancePollRef.current);
      depositBalancePollRef.current = null;
    }
    if (depositBalancePollRef._visHandler) {
      document.removeEventListener(
        "visibilitychange",
        depositBalancePollRef._visHandler
      );
      depositBalancePollRef._visHandler = null;
    }
  };

  const handleShowReceiveQR = async () => {
    setShowReceiveQR(true);
    await fetchDepositAddress();
  };

  const handleCloseReceiveQR = () => {
    setShowReceiveQR(false);
    setDepositAddress("");
    setDepositTag(null);
    // Balance is updated in real-time via WebSocket
  };

  React.useEffect(() => {
    return () => {
      stopMoonPayPolling();
      stopDepositBalancePolling();
      disconnectBalanceWs();
      if (loginPollRef.current) clearInterval(loginPollRef.current);
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
      if (smsOtpTimerRef.current) clearInterval(smsOtpTimerRef.current);
    };
  }, []);

  // Add this after your orderProducts is set
  const isOneTimePayment = React.useMemo(() => {
    if (!orderProducts || orderProducts.length === 0) return true;

    // Check if ALL products have only one-time prices
    return orderProducts.every((product) =>
      (product.prices || []).every((price) => price.priceType === "one-time")
    );
  }, [orderProducts]);

  const recurringInterval = React.useMemo(() => {
    if (isOneTimePayment || !orderProducts || orderProducts.length === 0)
      return "";
    for (const product of orderProducts) {
      const recurringPrice = (product.prices || []).find(
        (price) => price.priceType === "recurring"
      );
      if (recurringPrice && recurringPrice.intervalType) {
        const type = recurringPrice.intervalType.toLowerCase();
        if (type === "day") return "Daily";
        if (type === "week") return "Weekly";
        if (type === "month") return "Monthly";
        if (type === "year") return "Yearly";
        return recurringPrice.intervalType;
      }
    }
    return "";
  }, [orderProducts, isOneTimePayment]);

  // ── Stripe subscription — redirectToCheckout ──────────────────────────────
  // paymentIntentId (gatewayIntentId from createPaymentIntent) IS the Stripe checkout session ID
  const handleStripeSubscription = async () => {
    if (!paymentIntentId) {
      showToast("error", "Stripe session not ready. Please try again.");
      return;
    }
    setStripeSubRedirecting(true);
    try {
      // Direct redirect to Stripe Checkout URL using session ID
      // redirectToCheckout is deprecated — use direct URL instead
      const sessionId = stripePaymentIntentId || paymentIntentId;
      console.log("[Stripe] redirecting with sessionId:", sessionId);

      if (sessionId?.startsWith("cs_")) {
        window.location.href =
          stripePaymentUrl !== ""
            ? stripePaymentUrl
            : `${window.location.origin}/merchant/checkout/failure`;
      } else {
        showToast("error", "Invalid Stripe session. Please try again.");
        setStripeSubRedirecting(false);
      }
    } catch (err) {
      showToast("error", err.message || "Something went wrong.");
      setStripeSubRedirecting(false);
      const redirectUrl = buildRedirectUrl(
        redirectFailureUrl ||
          `${window.location.origin}/merchant/checkout/failure`,
        paymentIntentId || "",
        "failed",
        loginEmail || "",
        String(orderGrandTotal || grandTotal || "0"),
        detectedCurrency.code || "USD",
        redirectAdvanceOptions
      );
      window.location.href = redirectUrl;
    }
  };

  const closeLoginIframe = () => {
    if (loginPollRef.current) {
      clearInterval(loginPollRef.current);
      loginPollRef.current = null;
    }
    setShowLoginIframe(false);
    setLoginIframeUrl("");
    setWalletLoading(false);
  };
  const closeLoginModal = () => {
    setShowLoginModal(false);
    setLoginEmail("");
    setLoginPassword("");
    setLoginPasswordVisible(false);
    setLoginLoading(false);
  };

  const closeSignupModal = () => {
    setShowSignupModal(false);
    resetSignupForm();
  };

  // ── Signup / Registration functions ───────────────────────────────────────
  const resetSignupForm = () => {
    setSignupFirstName("");
    setSignupLastName("");
    setSignupMiddleName("");
    setSignupEmail("");
    setSignupPhone("");
    setSignupPassword("");
    setSignupRetypePassword("");
    setSignupGender("Male");
    setSignupErrors({});
    setSignupCountryOpen(false);
    setSignupCountrySearch("");
    setRegEmailOtp("");
    setRegPhoneOtp("");
    setRegShowPhoneOtp(false);
    setRegOtpLoading(false);
    setSignupDeviceDetails(null);
    if (regOtpTimerRef.current) clearInterval(regOtpTimerRef.current);
  };

  const fetchSignupDeviceDetails = () => {
    fetch(
      "https://api.ipgeolocation.io/ipgeo?apiKey=693da481af1b4a3e80e3dfea9115dc52"
    )
      .then((r) => r.json())
      .then((data) => setSignupDeviceDetails(data))
      .catch(() => {});
  };

  const loadSignupCountries = () => {
    const brokerId = getBrokerIdFromUrl();
    if (!brokerId) return;
    fetch(`${PAYBITO_ACCOUNTS}/home/getExchangeCountries/${brokerId}`)
      .then((r) => r.json())
      .then((res) => {
        const sorted = (res.countries || [])
          .sort((a, b) => a.country.localeCompare(b.country))
          .map((c) => ({
            value: c.dialCode,
            country: c.country,
            flag: `https://flagcdn.com/w40/${c.code.toLowerCase()}.png`,
          }));
        setSignupCountryList(sorted);
        setSignupFilteredCountries(sorted);
        const us = sorted.find((c) => c.country === "United States");
        setSignupSelectedCountry(us || sorted[0] || null);
      })
      .catch(() => {});
  };

  const handleSignupCountrySearch = (term) => {
    setSignupCountrySearch(term);
    setSignupFilteredCountries(
      !term
        ? signupCountryList
        : signupCountryList.filter(
            (c) =>
              c.country.toLowerCase().includes(term.toLowerCase()) ||
              c.value.toString().includes(term)
          )
    );
  };

  const validateSignupForm = () => {
    const errs = {};
    if (!signupFirstName.trim()) errs.firstName = "First name is required";
    else if (!/^[A-Za-z ]+$/.test(signupFirstName))
      errs.firstName = "Only letters and spaces allowed";
    if (!signupLastName.trim()) errs.lastName = "Last name is required";
    else if (!/^[A-Za-z ]+$/.test(signupLastName))
      errs.lastName = "Only letters and spaces allowed";
    if (!signupEmail.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupEmail))
      errs.email = "Invalid email address";
    if (!signupPhone.trim()) errs.phone = "Phone number is required";
    else if (signupPhone.length < 8) errs.phone = "At least 8 digits required";
    else if (signupPhone.length > 15) errs.phone = "At most 15 digits allowed";
    const pwdRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$@.!])[a-zA-Z\d$@.!]{8,35}$/;
    if (!signupPassword) errs.password = "Password is required";
    else if (!pwdRegex.test(signupPassword))
      errs.password =
        "8-35 chars, must include uppercase, lowercase, number and special char ($ @ . !)";
    if (!signupRetypePassword)
      errs.retypePassword = "Please retype your password";
    else if (signupPassword !== signupRetypePassword)
      errs.retypePassword = "Passwords do not match";
    if (!signupSelectedCountry)
      errs.countryCode = "Please select a country code";
    return errs;
  };

  const validatePhoneNumber = async (phone) => {
    if (signupSelectedCountry) {
      if (phone) {
        const data = {
          countryCode: signupSelectedCountry?.value,
          phone: phone,
        };
        const response = await axios.post(
          `${PAYBITO_ACCOUNTS}/user/CheckPhone`,
          data
        );
        if (response?.data?.error?.error_data === 1) {
          showToast("error", response?.data?.error?.error_msg);
          setSignupPhone("");
        } else {
          if (response?.data?.userResult.checkEmailPhoneFlag === 1) {
            showToast(
              "error",
              "Phone No. already registered , please try with another phone no."
            );
            setSignupPhone("");
          }
        }
      } else {
        showToast("warning", "Please Provide Phone No");
      }
    } else {
      showToast("error", "Please select phone country code");
    }
  };

  const checkEmail = (email) => {
    if (email) {
      const emailObj = { email };

      axios
        .post(`${PAYBITO_ACCOUNTS}/user/CheckEmail`, emailObj, {
          headers: { "Content-Type": "application/json" },
        })
        .then((response) => {
          const result = response.data;
          if (result.error.error_data !== 0) {
            showToast("error", result.error.error_msg);
          } else if (result.userResult.checkEmailPhoneFlag === 1) {
            showToast(
              "warning",
              "Email already registered, try with another email address."
            );
          }
        })
        .catch(() => {
          showToast("error", "Internal Server Error");
        });
    } else {
      showToast("warning", "Please Provide Email Id");
    }
  };

  const handleSignupFormSubmit = async (e) => {
    e.preventDefault();
    const errs = validateSignupForm();
    setSignupErrors(errs);
    if (Object.keys(errs).length > 0) return;

    try {
      const { token: captcha, sessionId } =
        await signupCaptchaRef.current.verify();
      setSignupLoading(true);
      const brokerId = getBrokerIdFromUrl();
      const payload = {
        brokerId,
        email: signupEmail,
        password: signupPassword,
        firstName: signupFirstName,
        lastName: signupLastName,
        middleName: signupMiddleName,
        gender: signupGender,
        phone: signupPhone,
        repassword1: signupRetypePassword,
        countryCode: signupSelectedCountry?.value,
        country: signupSelectedCountry?.country || "",
        latitude: signupDeviceDetails?.latitude || "",
        longitude: signupDeviceDetails?.longitude || "",
        ipAddress: signupDeviceDetails?.ip || "",
        location: signupDeviceDetails?.country_name || "",
        deviceType: /Mobi/i.test(navigator.userAgent)
          ? "mobile"
          : "Desktop browser",
        browser: navigator.userAgent.substring(0, 50),
        gRecaptchaResponse: captcha,
        sessionId,
        referralCode: null,
      };
      fetch(`${PAYBITO_ACCOUNTS}/user/AddUserDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data?.error?.error_data === 0) {
            showToast(
              "success",
              "Please check your email for the verification token."
            );
            localStorage.setItem("emailForUser", signupEmail);
            localStorage.setItem("phoneUsedForRegistration", signupPhone);
            localStorage.setItem(
              "phoneCountryUsedForRegistration",
              signupSelectedCountry?.value
            );
            localStorage.setItem("userId", data?.userResult?.userId);
            const enablePhone =
              data?.userResult?.enablePhoneOtp === 1 ||
              data?.userResult?.enablePhoneOtp === "1";
            setRegShowPhoneOtp(enablePhone);
            startRegOtpCountdown();
            setShowRegOtpModal(true);
            setSignupLoading(false);
            exchangeBrokerDomainUrl(brokerId);
          } else {
            showToast("error", data?.error?.error_msg || "Signup failed");
            setSignupLoading(false);
          }
        })
        .catch(() => {
          showToast("error", "Something went wrong. Please try again.");
          setSignupLoading(false);
        });
    } catch (err) {
      // Captcha cancelled or failed
    }
  };

  const exchangeBrokerDomainUrl = async (bId) => {
    fetch(
      `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${bId}`
    )
      .then((r) => r.json())
      .then((res) => {
        const info = res.domain;

        localStorage.setItem(SITENAMEALIAS + "_brokerDomain", info);
      })
      .catch(() => {});
  };

  const startRegOtpCountdown = () => {
    if (regOtpTimerRef.current) clearInterval(regOtpTimerRef.current);
    let count = 300;
    setRegOtpCountdown(count);
    regOtpTimerRef.current = setInterval(() => {
      count -= 1;
      setRegOtpCountdown(count);
      if (count <= 0) clearInterval(regOtpTimerRef.current);
    }, 1000);
  };

  const resendRegOtp = () => {
    fetch(`${PAYBITO_ACCOUNTS}/user/ResendOTP/registration`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: localStorage.getItem("userId") }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error?.error_data === 0) {
          showToast("success", "OTP has been sent to your email");
          startRegOtpCountdown();
        } else {
          showToast("error", data?.error?.error_msg || "Failed to resend OTP");
        }
      })
      .catch(() => showToast("error", "Something went wrong."));
  };

  const handleRegOtpSubmit = () => {
    if (!regEmailOtp || regEmailOtp.length !== 8) {
      showToast("error", "Please enter the 8-digit OTP");
      return;
    }
    setRegOtpLoading(true);
    const payload = {
      brokerId: getBrokerIdFromUrl(),
      email: signupEmail,
      otp: regEmailOtp,
      userId: localStorage.getItem("userId"),
    };
    if (regShowPhoneOtp && regPhoneOtp) payload.phoneOtp = regPhoneOtp;
    fetch(`${PAYBITO_ACCOUNTS}/user/CheckOTP`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error?.error_data === 0) {
          showToast("success", "Account verified successfully!");
          const token = data?.token;
          const uuid = data?.userResult?.uuid;
          const userId = data?.userResult?.userId;
          localStorage.setItem("access_token", token);
          localStorage.setItem("uuid", uuid);
          localStorage.setItem("userId", userId);
          setPaybitoToken(token);
          setPaybitoUserId(String(userId));
          setPaybitoUuid(uuid);
          fetchWalletBalance(token, uuid);
          connectBalanceWs(uuid);
          setWalletLoading(false);
          setShowRegOtpModal(false);
          setShowSignupModal(false);
          resetSignupForm();
        } else {
          showToast(
            "error",
            data?.error?.error_msg || "OTP verification failed"
          );
          setRegOtpLoading(false);
        }
      })
      .catch(() => {
        showToast("error", "Something went wrong.");
        setRegOtpLoading(false);
      });
  };

  const closeRegOtpModal = () => {
    setShowRegOtpModal(false);
    setRegEmailOtp("");
    setRegPhoneOtp("");
    if (regOtpTimerRef.current) clearInterval(regOtpTimerRef.current);
  };
  const closeOtpModal = () => {
    setShowOtpModal(false);
    setEmailOtp("");
    setPhoneOtp("");
    setGaOtp("");
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    if (smsOtpTimerRef.current) clearInterval(smsOtpTimerRef.current);
  };
  const startEmailOtpCountdown = () => {
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    otpEndTimeRef.current = Date.now() + 300000;
    const tick = () => {
      const rem = Math.max(
        Math.floor((otpEndTimeRef.current - Date.now()) / 1000),
        0
      );
      setOtpCountdown(rem);
      if (rem === 0 && otpTimerRef.current) clearInterval(otpTimerRef.current);
    };
    tick();
    otpTimerRef.current = setInterval(tick, 1000);
  };
  const startSmsOtpCountdown = () => {
    if (smsOtpTimerRef.current) clearInterval(smsOtpTimerRef.current);
    smsOtpEndTimeRef.current = Date.now() + 300000;
    const tick = () => {
      const rem = Math.max(
        Math.floor((smsOtpEndTimeRef.current - Date.now()) / 1000),
        0
      );
      setSmsOtpCountdown(rem);
      if (rem === 0 && smsOtpTimerRef.current)
        clearInterval(smsOtpTimerRef.current);
    };
    tick();
    smsOtpTimerRef.current = setInterval(tick, 1000);
  };
  const sendLoginEmailOtp = () => {
    startEmailOtpCountdown();
    fetch(`${PAYBITO_ACCOUNTS}/user/SendOtp/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: loginEmail }),
    })
      .then((r) => r.json())
      .then((result) => {
        if (result.error?.error_data === 0) {
          showToast("success", "OTP sent to your email");
          setShowOtpModal(true);
        } else
          showToast("error", result.error?.error_msg || "Failed to send OTP");
      })
      .catch(() => showToast("error", "Failed to send OTP"));
  };
  const sendLoginSmsOtp = (phone, countryCode) => {
    if (!phone) return;
    startSmsOtpCountdown();
    fetch(`${PAYBITO_ACCOUNTS}/user/sendSmsOtp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phone, countryCode, action: "login" }),
    })
      .then((r) => r.json())
      .then((result) => {
        if (result.error?.error_data === 0)
          showToast("success", "OTP sent to your phone");
      })
      .catch(() => {});
  };
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      showToast("error", "Please enter email and password");
      return;
    }
    try {
      const { token: captcha, sessionId } = await captchaRef.current.verify();
      setLoginLoading(true);
      fetch(`${PAYBITO_ACCOUNTS}/user/getBrokerByUserEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: loginEmail,
        }),
      })
        .then((r) => r.json())
        .then((brokerData) => {
          const brokerId =
            brokerData?.brokerId ??
            brokerData?.result?.brokerId ??
            brokerData?.data?.brokerId;
          setExchangeBrokerId(brokerId);
          return fetch(`${PAYBITO_ACCOUNTS}/user/checkMfaStatus`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email: loginEmail,
              password: loginPassword,
              gRecaptchaResponse: captcha,
              sessionId,
              brokerId,
            }),
          });
        })
        .then((r) => r.json())
        .then((data) => {
          if (
            data?.error?.error_data == "1" ||
            data?.error?.error_data == "2"
          ) {
            showToast("error", data?.error?.error_msg);
            setLoginLoading(false);
            return;
          }
          if (data?.userResult?.userTag == 6) {
            setLoginLoading(true);
            handleOtpLogin("12345678");
            return;
          }
          const user = data?.userResult;
          localStorage.setItem("user_name", user?.firstName);
          localStorage.setItem("first_name", user?.firstName);
          localStorage.setItem("last_name", user?.lastName);
          localStorage.setItem("userId", user?.userId);
          localStorage.setItem("email", user?.email);
          setIsGoogleAuthEnable(user?.twoFactorAuth == 1);
          if (user?.phoneValidation == 1) {
            setIsPhoneVerificationEnable(true);
            setLoginUserPhone(user?.phone);
            setLoginUserCountryCode(user?.countryCode);
            sendLoginSmsOtp(user?.phone, user?.countryCode);
          } else setIsPhoneVerificationEnable(false);
          sendLoginEmailOtp();
          setLoginLoading(false);
        })
        .catch(() => {
          showToast("error", "Login failed. Please try again.");
          setLoginLoading(false);
        });
    } catch (err) {
      // Captcha cancelled or failed
    }
  };
  const handleOtpLogin = (forcedOtp) => {
    const otp = forcedOtp || emailOtp;
    if (!otp || (otp !== "12345678" && otp.length !== 8)) {
      showToast("error", "Please enter the full length OTP");
      return;
    }
    setLoginLoading(true);
    // const brokerId = getBrokerIdFromUrl();
    const storedDeviceId = localStorage.getItem("deviceId");
    const payload = {
      email: loginEmail,
      password: loginPassword,
      otp,
      brokerId: exchangeBrokerId,
      deviceId:
        storedDeviceId && !["null", "undefined", ""].includes(storedDeviceId)
          ? storedDeviceId
          : "checkout-" + Date.now(),
      deviceType: /Mobi/i.test(navigator.userAgent)
        ? "mobile"
        : "Desktop browser",
      browser: navigator.userAgent.substring(0, 50),
      latitude: loginDeviceDetails?.latitude || "",
      longitude: loginDeviceDetails?.longitude || "",
      ipAddress: loginDeviceDetails?.ip || "",
      location: loginDeviceDetails?.country_name || "",
      callFrom: "Desktop",
    };
    if (isPhoneVerificationEnable && phoneOtp) payload.phoneOtp = phoneOtp;
    if (isGoogleAuthEnable && gaOtp) payload.securityCode = gaOtp;
    fetch(`${PAYBITO_ACCOUNTS}/user/LoginWithUsernamePassword`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.error?.error_data === 0) {
          showToast("success", data?.error?.error_msg || "Login successful");
          const token = data?.token;
          const uuid = data?.userResult?.uuid;
          const userId = data?.userResult?.userId;
          localStorage.setItem("access_token", token);
          localStorage.setItem("uuid", uuid);
          localStorage.setItem("userId", userId);

          closeOtpModal();
          closeLoginModal();
          setLoginLoading(false);
          setPaybitoToken(token);
          setPaybitoUserId(String(userId));
          setPaybitoUuid(uuid);
          fetchWalletBalance(token, uuid);
          connectBalanceWs(uuid);
          setWalletLoading(false);
          exchangeBrokerDomainUrl(exchangeBrokerId);
        } else {
          showToast("error", data?.error?.error_msg || "Login failed");
          setLoginLoading(false);
        }
      })
      .catch(() => {
        showToast("error", "Login request failed. Please try again.");
        setLoginLoading(false);
      });
  };
  useEffect(() => {
    if (!showOtpModal) return;
    const emailReady = emailOtp?.length === 8;
    const phoneReady = !isPhoneVerificationEnable || phoneOtp?.length === 8;
    const gaReady = !isGoogleAuthEnable || gaOtp?.length === 6;
    if (emailReady && phoneReady && gaReady) handleOtpLogin();
  }, [emailOtp, phoneOtp, gaOtp, showOtpModal]);

  // ── Auto-scroll after network selection ───────────────────────────────────
  useEffect(() => {
    if (selectedNetwork) {
      // Small delay to allow the UI to update and render the additional content
      setTimeout(() => {
        const cryptoSection = document.querySelector(
          ".co-options .co-option:last-child"
        );
        const brandWalletOption = document.querySelector(".co-option.selected");

        // Try to scroll to the selected crypto option or the brand wallet section
        if (brandWalletOption) {
          brandWalletOption.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "nearest",
          });
        } else if (cryptoSection) {
          cryptoSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        // Additional scroll to ensure the payment options are visible
        window.scrollBy({
          top: 100,
          behavior: "smooth",
        });
      }, 300); // Delay to ensure DOM has updated with network selection UI
    }
  }, [selectedNetwork]);

  const maskEmail = (email) => {
    if (!email) return "";
    const i = email.indexOf("@");
    if (i > 3) return email.slice(0, 3) + "*".repeat(i - 3) + email.slice(i);
    return email;
  };
  const isWalletLoggedIn = !!paybitoToken && !!paybitoUuid;
  const walletPaymentAmount = parseFloat(
    cryptoConvertedAmt || invoiceData?.amount_to_be_paid || grandTotal || 0
  );
  const hasSufficientBalance =
    walletBalance !== null && parseFloat(walletBalance) >= walletPaymentAmount;
  // Amount Pabtek needs to charge: full amount when balance is 0/null, shortfall otherwise
  const pabtekRequiredAmount = (() => {
    const bal = parseFloat(walletBalance || 0);
    return bal > 0 && !hasSufficientBalance
      ? Math.max(0, walletPaymentAmount - bal)
      : walletPaymentAmount;
  })();
  const cryptoForBuy = (selectedCrypto || "").toUpperCase();
  const showBuyOption =
    MOONPAY_BUY_CURRENCIES.includes(cryptoForBuy) ||
    (cryptoForBuy === "USDT" &&
      (selectedNetwork === "ERC" || selectedNetwork === "TRC"));

  const initWalletInline = () => {
    if (walletLoading) return;
    setWalletLoading(true);
    setShowReceiveQR(false);
    setDepositAddress("");
    setDepositTag(null);
    const plainToken = localStorage.getItem("access_token");
    const plainUuid = localStorage.getItem("uuid");
    const plainUserId = localStorage.getItem("userId");
    if (plainToken && plainUuid && plainUserId) {
      setPaybitoToken(plainToken);
      setPaybitoUserId(String(plainUserId));
      setPaybitoUuid(plainUuid);
      fetchWalletBalance(plainToken, plainUuid);
      connectBalanceWs(plainUuid);
      setWalletLoading(false);
      return;
    }
    const ALIAS = SITENAMEALIAS;
    const accessToken = getAuthToken();
    const uuid = getAuthUuid();
    if (!accessToken || !uuid) {
      if (brandWalletAvaliability === "0") {
        if (signupCountryList.length === 0) loadSignupCountries();
        fetchSignupDeviceDetails();
        setSignupEmail(localStorage.getItem(SITENAMEALIAS + "_email") || "");
        setShowSignupModal(true);
      } else {
        setLoginEmail(localStorage.getItem(SITENAMEALIAS + "_email") || "");
        setShowLoginModal(true);
      }
      setWalletLoading(false);
      return;
    }
    autoLoginInline();
  };

  const autoLoginInline = () => {
    const ALIAS = SITENAMEALIAS;
    const accessToken = getAuthToken();
    const uuid = getAuthUuid();
    if (!accessToken || !uuid) {
      showToast("error", "Login session not found.");
      setWalletLoading(false);
      return;
    }
    setWalletLoading(true);
    fetch(`${WEBSERVICE}/merchant/auto-login/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + accessToken,
        UUID: uuid,
      },
      body: JSON.stringify({
        merchantId: localStorage.getItem(ALIAS + "_merchant_id"),
        brokerId: getBrokerIdFromUrl(),
        uuid,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error?.error_data !== 0) {
          showToast(
            "error",
            data.error?.error_message || "Failed to initialize session."
          );
          setWalletLoading(false);
          return;
        }
        const { token, userId } = data;
        const resUuid = data?.uuid;
        localStorage.setItem("access_token", token);
        return fetch(`${PAYBITO_ACCOUNTS}/user/GetUserDetails`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + token,
          },
          body: JSON.stringify({ uuid: resUuid, userId }),
        })
          .then((r) => r.json())
          .then((ud) => {
            const u = ud.userResult;
            localStorage.setItem("userId", u.userId);
            localStorage.setItem("uuid", u.uuid);
            setPaybitoToken(token);
            setPaybitoUserId(String(u.userId));
            setPaybitoUuid(u.uuid);
            fetchWalletBalance(token, u.uuid);
            connectBalanceWs(u.uuid);
            setWalletLoading(false);
          });
      })
      .catch(() => {
        showToast("error", "Failed to initialize session with exchange.");
        setWalletLoading(false);
      });
  };

  const redirectToExchange = () => {
    initWalletInline();
  };

  const handleZeroPayment = async () => {
    setZeroPayLoading(true);
    const dp = getUrlParameter("data");
    let sessionId = "";
    if (dp) {
      try {
        const parsed = JSON.parse(
          decodeURIComponent(escape(atob(dp.replace(/ /g, "+"))))
        );
        const obj = Array.isArray(parsed) ? parsed[0] : parsed;
        sessionId = obj.sessionId || obj.session_id || "";
      } catch {}
    }

    const clientSecret = Array.from(
      { length: 32 },
      () =>
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789"[
          Math.floor(Math.random() * 62)
        ]
    ).join("");

    try {
      const res = await fetch(`${WEBSERVICE}/checkout/createPaymentIntent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: String(sessionId),
          paymentMethodId: "6",
          clientSecret,
        }),
      });
      const data = await res.json();
      if (data.status && data.data?.successUrl) {
        window.location.href = data.data.successUrl;
      } else {
        showToast(
          "error",
          data.message ||
            "Failed to process reward wallet payment. Please try again."
        );
        setZeroPayLoading(false);
      }
    } catch (err) {
      console.error("[ZeroPayment] Error:", err);
      showToast("error", "Network error. Please try again.");
      setZeroPayLoading(false);
    }
  };
  const submitForm = (fields, onError) => {
    const data = new URLSearchParams();
    Object.entries(fields).forEach(([k, v]) => data.append(k, v ?? ""));
    data.append("is_processing_fee_applied", hfFeeApplied);
    data.append("processing_fee_pct", hfFeePct);
    data.append("processing_fee_amt", hfFeeAmt);
    return fetch(urlForSubmit, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data,
    })
      .then((r) => r.text())
      .then((result) => {
        setTimeout(() => {
          try {
            window.location.href = parseRedirectUrl(result);
          } catch {
            onError("Failed to process. Please try again.");
          }
        }, 300);
      })
      .catch(() => onError("Request failed. Please try again."));
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const brandName = exchangeName || "Brand";
  const currency = invoiceData?.currency || "";
  const isTron = paymentType === "trc20";
  const walletLabel = isTron ? "Connect TronLink" : "Connect External Wallet";
  const walletDesc = isTron
    ? "Pay USDT via TronLink wallet (TRC20)"
    : "Pay using MetaMask, Trust Wallet, Phantom, Brave Wallet, TronLink or Coinbase Wallet";
  const proceedBtnClass = isTron ? "co-btn co-btn-tron" : "co-btn co-btn-green";
  const billingMethods = billingProfile?.billingMethod || [];
  const noBillingMethods = billingMethods.length === 0;
  const methodIds = billingMethods.map((m) => m.pmId);
  const showPayPalOption = !noBillingMethods && methodIds.includes(2);
  const showStripeOption = !noBillingMethods && methodIds.includes(1);
  const showKurvOption = !noBillingMethods && methodIds.includes(7);
  const showNetBillingOption = !noBillingMethods && methodIds.includes(8)
  // const showNetBillingOption = true

  const showHmsOption = !noBillingMethods && methodIds.includes(9);
  const showNmiOption = !noBillingMethods && methodIds.includes(11);
  const showCardFloOption = !noBillingMethods && methodIds.includes(10);
  const isSubscription = billingProfile !== null && !isOneTimePayment;
  // Subscriptions: always show brand wallet, never show external wallet or guest checkout
  const showBrandWallet = isSubscription
    ? true
    : noBillingMethods || methodIds.includes(3);
  const showExternalWallet = isSubscription
    ? false
    : noBillingMethods || methodIds.includes(4);
  const showGuestCheckout = isSubscription
    ? false
    : noBillingMethods || methodIds.includes(5);
  const showCryptoSection =
    showBrandWallet || showExternalWallet || showGuestCheckout;
  // True only after billingProfile has loaded and crypto is the sole payment method
  const isCryptoOnly =
    billingProfile !== null &&
    showCryptoSection &&
    !showPayPalOption &&
    !showStripeOption &&
    !showKurvOption &&
    !showNetBillingOption &&
    !showHmsOption &&
    !showNmiOption &&
    !showCardFloOption;
  // Section render order driven by displayOrder from the API
  const sectionCssOrder = (() => {
    const sorted = [...billingMethods].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
    );
    const order = [];
    for (const m of sorted) {
      if (m.pmId === 1 && !order.includes("stripe")) order.push("stripe");
      else if (m.pmId === 2 && !order.includes("paypal")) order.push("paypal");
      else if ([3, 4, 5].includes(m.pmId) && !order.includes("crypto"))
        order.push("crypto");
      else if (m.pmId === 7 && !order.includes("kurv")) order.push("kurv");
      else if (m.pmId === 8 && !order.includes("netbilling"))
        order.push("netbilling");
      else if (m.pmId === 9 && !order.includes("hms"))
        order.push("hms");
      else if (m.pmId === 10 && !order.includes("cardflo"))
        order.push("cardflo");
      else if (m.pmId === 11 && !order.includes("nmi"))
        order.push("nmi");
      }

      if (!order.includes("paypal")) order.push("paypal");
      if (!order.includes("stripe")) order.push("stripe");
      if (!order.includes("kurv")) order.push("kurv");
      if (!order.includes("netbilling")) order.push("netbilling");
      if (!order.includes("hms")) order.push("hms");
      if (!order.includes("nmi")) order.push("nmi");
      if (!order.includes("cardflo")) order.push("cardflo");

    if (!order.includes("crypto")) order.push("crypto");
    return {
      paypal: order.indexOf("paypal"),
      stripe: order.indexOf("stripe"),
      kurv: order.indexOf("kurv"),
      netbilling: order.indexOf("netbilling"),
      hms: order.indexOf("hms"),
      nmi: order.indexOf("nmi"),
      cardflo: order.indexOf("cardflo"),
      crypto: order.indexOf("crypto"),
    };
  })();
  // Sub-order inside Pay with Crypto driven by displayOrder from the API
  const cryptoSubCssOrder = (() => {
    const sorted = [...billingMethods]
      .filter((m) => [3, 4, 5].includes(m.pmId))
      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
    const order = [];
    for (const m of sorted) {
      if (m.pmId === 3) order.push("brandWallet");
      else if (m.pmId === 4) order.push("externalWallet");
      else if (m.pmId === 5) order.push("guestCheckout");
    }
    if (!order.includes("brandWallet")) order.push("brandWallet");
    if (!order.includes("externalWallet")) order.push("externalWallet");
    if (!order.includes("guestCheckout")) order.push("guestCheckout");
    return {
      brandWallet: order.indexOf("brandWallet"),
      externalWallet: order.indexOf("externalWallet"),
      guestCheckout: order.indexOf("guestCheckout"),
    };
  })();
  // SINGLE = one-time payment, else subscription
  const availableCryptos = (() => {
    const profileCurrencies = billingProfile?.currencies;
    const hasCurrencies =
      Array.isArray(profileCurrencies) && profileCurrencies.length > 0;
    if (!hasCurrencies) return CRYPTO_MASTER_LIST;
    // Return in sequence from profileCurrencies, matching with CRYPTO_MASTER_LIST data
    return profileCurrencies
      .map((c) =>
        CRYPTO_MASTER_LIST.find((m) => m.currency_code === c.currency)
      )
      .filter(Boolean);
  })();
  // Lock payment method panels until the user has made a valid coin + network choice
  const paymentMethodsLocked = (() => {
    // Multiple coins available but none selected yet
    if (availableCryptos.length > 1 && !selectedCrypto) return true;
    // Coin selected but it has multiple selectable networks and none is chosen
    if (selectedCrypto) {
      const coin = availableCryptos.find(
        (c) => c.currency_code === selectedCrypto
      );
      const selectableNets = (coin?.network || []).filter(
        (n) => n !== "NATIVE"
      );
      if (selectableNets.length > 1 && !selectedNetwork) return true;
    }
    return false;
  })();
  // Network selection guard for "Pay with Crypto" — when a coin requires
  // a network (e.g. USDT ERC/TRC) but the user hasn't picked one yet, the
  // converted amount is not available. Use this to show "0" instead of NaN
  // and to disable the proceed button.
  const selectedCoinForPay = availableCryptos.find(
    (c) => c.currency_code === selectedCrypto
  );
  const needsNetworkSelection = (() => {
    if (!selectedCrypto) return false;
    const nets = selectedCoinForPay?.network || [];
    if (nets.length === 0) return false;
    return !(nets.length === 1 && nets[0] === "NATIVE");
  })();
  const isNetworkMissing = needsNetworkSelection && !selectedNetwork;
  const safeDisplayCryptoAmt = (() => {
    const candidate =
      cryptoConvertedAmt ||
      invoiceData?.amount_to_be_paid ||
      (Number.isFinite(grandTotal) ? grandTotal.toFixed(6) : null);
    if (candidate === undefined || candidate === null || candidate === "")
      return "0";
    if (candidate === "NaN") return "0";
    const num = parseFloat(candidate);
    if (!Number.isFinite(num)) return "0";
    return candidate;
  })();
  const cur = detectedCurrency;
  const sp = shippingProfile || {};
  const hPct = parseFloat(sp.handlingFeeValue || 0);
  const tPct = parseFloat(sp.taxRate || 0);
  const getOrderBase = (p) => {
    const priceObj = p.prices?.[0];
    const ce =
      priceObj?.currencies?.find((c) => c.currency === cur.code) ||
      priceObj?.currencies?.find((c) => c.default === true) ||
      priceObj?.currencies?.[0];
    // Use product-level itemQuantity instead of inventory.quantity
    const qty = parseInt(p.itemQuantity ?? 1, 10);
    return (
      parseFloat(p.basePrice || p.price || p.amount || ce?.amount || 0) * qty
    );
  };
  const totalHandling = parseFloat(
    orderProducts
      .reduce((acc, p) => acc + (getOrderBase(p) * hPct) / 100, 0)
      .toFixed(2)
  );
  const totalTax = parseFloat(
    orderProducts
      .reduce((acc, p) => acc + (getOrderBase(p) * tPct) / 100, 0)
      .toFixed(2)
  );

  useEffect(() => {
    const selectedCoin = availableCryptos.find(
      (c) => c.currency_code === selectedCrypto
    );
    const networks = selectedCoin?.network || [];
    const firstSelectableNetwork = networks.find((n) => n !== "NATIVE");

    if (firstSelectableNetwork) {
      setSelectedNetwork(firstSelectableNetwork);
    }
    // Only re-run when the selected crypto changes.
    // availableCryptos is intentionally excluded — it's an inline IIFE that
    // produces a new array reference on every render, which would incorrectly
    // reset the user's chosen network on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrypto]);

  // Auto-expand the crypto section when it is the only payment method available
  useEffect(() => {
    if (!isCryptoOnly) return;
    setCryptoExpanded(true);
    // For single-coin merchants, also auto-select the coin + network
    // (mirrors the logic in the expand onClick handler)
    if (availableCryptos.length === 1) {
      const singleCrypto = availableCryptos[0];
      const code = singleCrypto.currency_code;
      const networks = singleCrypto.network || [];
      setSelectedCrypto(code);
      const firstNet = networks.find((n) => n !== "NATIVE");
      if (firstNet) setSelectedNetwork(firstNet);
    }
    // availableCryptos excluded intentionally — new reference every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCryptoOnly]);

  // Helper for discount message
  const getDiscountMessage = (discount) => {
    if (!discount) return null;
    const discountPct = discount.discountPercentage || discount.discountPercent;
    const minValue = discount.minimumCartValue;
    return `${discount.profileName} - ${discountPct}% off (Min. ${cur.symbol} ${minValue})`;
  };

  return (
    <>
      <style>{STYLES}</style>
      {isTestMode && (
        <style>{`
          :root {
            --co-primary: #f59e0b;
            --co-primary-hover: #d97706;
            --co-primary-disabled: #fcd34d;
          }
        `}</style>
      )}
      {/* Test Mode Banner */}
      {isTestMode && (
        <div
          style={{
            background: "#fef3c7",
            borderBottom: "1px solid #fcd34d",
            padding: "8px 20px",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#92400e"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <span
            style={{
              color: "#92400e",
              fontWeight: 800,
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >
            Test Mode
          </span>
          {/* <span
            style={{
              color: "#b45309",
              fontSize: "12px",
              fontWeight: 500,
            }}
          >
            (No real payments will be processed)
          </span> */}
        </div>
      )}
      <div className="co-wrap">
        <div className="co-page-inner">
          <div className="co-header">
            <div className="co-header-icon">
              <i className="fa fa-shopping-cart" />
            </div>
            <h2>{companyName ? `Pay to ${companyName}` : "Secure Checkout"}</h2>
            <p>Choose how you'd like to pay</p>
          </div>
          <div className="co-card">
            <div className="co-card-accent" />
            <div className="co-card-body">
              <div className="co-layout">
                {/* ════ LEFT ════ */}
                <div className="co-left">
                  <div
                    className="co-section-label"
                    style={{ fontSize: "22px", fontWeight: 700 }}
                  >
                    Select Payment Method
                  </div>
                  <p className="co-section-sub">
                    Select how you'd like to complete this payment.
                  </p>
                  {paymentExpired ? (
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "28px 24px",
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg,rgba(239,68,68,0.07),rgba(239,68,68,0.03))",
                        border: "1.5px solid rgba(239,68,68,0.25)",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          background: "rgba(239,68,68,0.10)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 16,
                        }}
                      >
                        <i
                          className="fa fa-clock-o"
                          style={{ fontSize: 24, color: "var(--co-danger)" }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 17,
                          fontWeight: 700,
                          color: "var(--co-danger)",
                          marginBottom: 8,
                        }}
                      >
                        Payment Expired
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "var(--co-gray-500)",
                          lineHeight: 1.6,
                        }}
                      >
                        This payment link has expired. Please contact the
                        merchant for a new link.
                      </div>
                    </div>
                  ) : isZeroPayment ? (
                    /* ── Zero-payment: wallet fully covers the total ── */
                    <div
                      style={{
                        marginTop: 8,
                        padding: "28px 24px",
                        borderRadius: 14,
                        background:
                          "linear-gradient(135deg,rgba(3,105,161,0.07),rgba(3,105,161,0.03))",
                        border: "1.5px solid rgba(3,105,161,0.25)",
                        textAlign: "center",
                      }}
                    >
                      {/* Icon */}
                      <div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          background: "rgba(3,105,161,0.10)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          marginBottom: 18,
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="28"
                          height="28"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#0369a1"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="5" width="20" height="14" rx="2" />
                          <line x1="2" y1="10" x2="22" y2="10" />
                        </svg>
                      </div>

                      {/* Heading */}
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: "#0c4a6e",
                          marginBottom: 8,
                        }}
                      >
                        Your wallet covers this order!
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "#0369a1",
                          lineHeight: 1.65,
                          marginBottom: 24,
                        }}
                      >
                        Your redeemed wallet balance fully covers the order
                        total. No additional payment is required — click below
                        to complete your purchase.
                      </div>

                      {/* Zero badge */}
                      {/* <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "10px 20px",
                          borderRadius: 30,
                          background: "rgba(3,105,161,0.10)",
                          border: "1px solid rgba(3,105,161,0.25)",
                          marginBottom: 24,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 28,
                            fontWeight: 800,
                            color: "#0369a1",
                            lineHeight: 1,
                          }}
                        >
                          {detectedCurrency.symbol}0.00
                        </span>
                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#0369a1",
                            opacity: 0.75,
                          }}
                        >
                          due today
                        </span>
                      </div> */}

                      {/* CTA button */}
                      <button
                        onClick={handleZeroPayment}
                        disabled={zeroPayLoading}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                          width: "100%",
                          padding: "14px 20px",
                          background: zeroPayLoading
                            ? "#7dd3fc"
                            : "linear-gradient(135deg,#0369a1,#0c4a6e)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 10,
                          fontSize: 15,
                          fontWeight: 700,
                          cursor: zeroPayLoading ? "not-allowed" : "pointer",
                          boxShadow: zeroPayLoading
                            ? "none"
                            : "0 6px 20px rgba(3,105,161,0.35)",
                          transition: "all .22s ease",
                        }}
                      >
                        {zeroPayLoading ? (
                          <>
                            <span className="co-spinner" />
                            &nbsp;Processing…
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#fff"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="2" y="5" width="20" height="14" rx="2" />
                              <line x1="2" y1="10" x2="22" y2="10" />
                            </svg>
                            Pay with Reward Wallet
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <div className="co-options">
                      {/* ── PayPal ── */}
                      {showPayPalOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "paypal" ? "selected" : ""
                          } ${
                            paymentIntentLoading && selectedOpt === "paypal"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.paypal }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "paypal";
                              setSelectedOpt((p) =>
                                p === "paypal" ? null : "paypal"
                              );
                              if (opening) {
                                paymentIntentCache.current[2] = null;
                                createPaymentIntent(2);
                              }
                            }}
                          >
                            <div
                              style={{
                                width: 46,
                                height: 46,
                                borderRadius: 11,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(0,156,222,0.10)",
                              }}
                            >
                              <PayPalIcon size={45} />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">
                                Pay with PayBito Titan
                              </div>
                              <div className="co-option-desc">
                                Pay securely using your PayBito Titan account or card
                              </div>
                            </div>
                          </div>
                          {selectedOpt === "paypal" && (
                            <div
                              style={{
                                padding: "0 18px 18px",
                                borderTop: "1px dashed var(--co-gray-200)",
                                paddingTop: 14,
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "12.5px",
                                  color: "var(--co-gray-500)",
                                  marginBottom: 14,
                                  lineHeight: 1.6,
                                }}
                              >
                                Complete your purchase using PayBito Titan.
                              </p>
                              {paymentIntentLoading ? (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                  }}
                                >
                                  <span
                                    className="co-spinner"
                                    style={{
                                      borderTopColor: "#009cde",
                                      borderColor: "rgba(0,156,222,0.3)",
                                    }}
                                  />
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 13,
                                      color: "var(--co-gray-500)",
                                    }}
                                  >
                                    Initializing PayBito Titan...
                                  </span>
                                </div>
                              ) : paypalClientId ? (
                                isOneTimePayment ? (
                                  <PayPalScriptProvider
                                    options={{
                                      "client-id": paypalClientId,
                                      currency: detectedCurrency?.code || "USD",
                                    }}
                                  >
                                    <PayPalButtons
                                      style={{
                                        layout: "vertical",
                                        shape: "rect",
                                        color: "blue",
                                        label: "pay",
                                      }}
                                      createOrder={(data, actions) => {
                                        console.log("TOTAL ", orderGrandTotal);
                                        if (paypalOrderId)
                                          return Promise.resolve(paypalOrderId);
                                        return actions.order.create({
                                          purchase_units: [
                                            {
                                              amount: {
                                                value: String(
                                                  orderGrandTotal ||
                                                    grandTotal ||
                                                    "0"
                                                ),
                                                currency_code:
                                                  detectedCurrency.code ||
                                                  "USD",
                                              },
                                              description:
                                                billingProfile?.profileName ||
                                                "Payment",
                                            },
                                          ],
                                        });
                                      }}
                                      onApprove={(data, actions) =>
                                        actions.order
                                          .capture()
                                          .then((details) => {
                                            const paymentId = data.orderID;
                                            const customerEmail =
                                              details.payer?.email_address ||
                                              loginEmail ||
                                              "";
                                            const amount =
                                              details.purchase_units?.[0]
                                                ?.amount?.value ||
                                              String(
                                                orderGrandTotal ||
                                                  grandTotal ||
                                                  "0"
                                              );
                                            const status = "success";

                                            const redirectUrl =
                                              buildRedirectUrl(
                                                redirectSuccessUrl ||
                                                  `${window.location.origin}/merchant/checkout/success`,
                                                paymentId,
                                                status,
                                                customerEmail,
                                                amount,
                                                detectedCurrency.code || "USD",
                                                redirectAdvanceOptions
                                              );

                                            window.location.href = redirectUrl;
                                          })
                                      }
                                      onError={async (err) => {
                                        console.error("[PayPal] Error:", err);
                                        const paymentId = paymentIntentId || "";
                                        const customerEmail = loginEmail || "";
                                        const amount = String(
                                          orderGrandTotal || grandTotal || "0"
                                        );
                                        const status = "failed";

                                        const redirectUrl = buildRedirectUrl(
                                          redirectFailureUrl ||
                                            `${window.location.origin}/merchant/checkout/failure`,
                                          paymentId,
                                          status,
                                          customerEmail,
                                          amount,
                                          detectedCurrency.code || "USD",
                                          redirectAdvanceOptions
                                        );

                                        if (paymentId) {
                                          try {
                                            const updateRes = await fetch(
                                              `${WEBSERVICE}/payment/updatePaymentStatus`,
                                              {
                                                method: "PUT",
                                                headers: {
                                                  "Content-Type":
                                                    "application/json",
                                                },
                                                body: JSON.stringify({
                                                  paymentIntentId: paymentId,
                                                }),
                                              }
                                            );
                                            const updateData =
                                              await updateRes.json();
                                            if (updateData.status) {
                                              window.location.href =
                                                redirectUrl;
                                            }
                                          } catch {
                                            // API call failed, do not redirect
                                          }
                                        }
                                      }}
                                      onCancel={async () => {
                                        const paymentId = paymentIntentId || "";
                                        const customerEmail = loginEmail || "";
                                        const amount = String(
                                          orderGrandTotal || grandTotal || "0"
                                        );
                                        const status = "cancelled";

                                        const redirectUrl = buildRedirectUrl(
                                          redirectFailureUrl ||
                                            `${window.location.origin}/merchant/checkout/failure`,
                                          paymentId,
                                          status,
                                          customerEmail,
                                          amount,
                                          detectedCurrency.code || "USD",
                                          redirectAdvanceOptions
                                        );

                                        if (paymentId) {
                                          try {
                                            const updateRes = await fetch(
                                              `${WEBSERVICE}/payment/updatePaymentStatus`,
                                              {
                                                method: "PUT",
                                                headers: {
                                                  "Content-Type":
                                                    "application/json",
                                                },
                                                body: JSON.stringify({
                                                  paymentIntentId: paymentId,
                                                }),
                                              }
                                            );
                                            const updateData =
                                              await updateRes.json();
                                            if (updateData.status) {
                                              window.location.href =
                                                redirectUrl;
                                            }
                                          } catch {
                                            // API call failed, do not redirect
                                          }
                                        }
                                      }}
                                    />
                                  </PayPalScriptProvider>
                                ) : (
                                  <PayPalScriptProvider
                                    options={{
                                      "client-id": paypalClientId,
                                      vault: true,
                                      intent: "subscription",
                                    }}
                                  >
                                    <PayPalButtons
                                      style={{
                                        layout: "vertical",
                                        shape: "rect",
                                        color: "blue",
                                        label: "subscribe",
                                      }}
                                      createSubscription={(data, actions) => {
                                        return paypalSubscriptionId;
                                      }}
                                      onApprove={async (data) => {
                                        setPaypalSubscriptionId(
                                          data.subscriptionID || ""
                                        );
                                        const paymentId =
                                          data.subscriptionID || "";
                                        const customerEmail = loginEmail || "";
                                        const amount = String(
                                          orderGrandTotal || grandTotal || "0"
                                        );
                                        const status = "success";

                                        const redirectUrl = buildRedirectUrl(
                                          redirectSuccessUrl ||
                                            `${window.location.origin}/merchant/checkout/success`,
                                          paymentId,
                                          status,
                                          customerEmail,
                                          amount,
                                          detectedCurrency.code || "USD",
                                          redirectAdvanceOptions
                                        );

                                        window.location.href = redirectUrl;
                                      }}
                                      onError={async (err) => {
                                        console.error(
                                          "[PayPal] Subscription error:",
                                          err
                                        );
                                        const paymentId = paymentIntentId || "";
                                        const customerEmail = loginEmail || "";
                                        const amount = String(
                                          orderGrandTotal || grandTotal || "0"
                                        );
                                        const status = "failed";

                                        const redirectUrl = buildRedirectUrl(
                                          redirectFailureUrl ||
                                            `${window.location.origin}/merchant/checkout/failure`,
                                          paymentId,
                                          status,
                                          customerEmail,
                                          amount,
                                          detectedCurrency.code || "USD",
                                          redirectAdvanceOptions
                                        );

                                        if (paymentId) {
                                          try {
                                            const updateRes = await fetch(
                                              `${WEBSERVICE}/payment/updatePaymentStatus`,
                                              {
                                                method: "PUT",
                                                headers: {
                                                  "Content-Type":
                                                    "application/json",
                                                },
                                                body: JSON.stringify({
                                                  paymentIntentId: paymentId,
                                                }),
                                              }
                                            );
                                            const updateData =
                                              await updateRes.json();
                                            if (updateData.status) {
                                              window.location.href =
                                                redirectUrl;
                                            }
                                          } catch {
                                            // API call failed, do not redirect
                                          }
                                        }
                                      }}
                                      onCancel={async () => {
                                        const paymentId = paymentIntentId || "";
                                        const customerEmail = loginEmail || "";
                                        const amount = String(
                                          orderGrandTotal || grandTotal || "0"
                                        );
                                        const status = "cancelled";

                                        const redirectUrl = buildRedirectUrl(
                                          redirectFailureUrl ||
                                            `${window.location.origin}/merchant/checkout/failure`,
                                          paymentId,
                                          status,
                                          customerEmail,
                                          amount,
                                          detectedCurrency.code || "USD",
                                          redirectAdvanceOptions
                                        );

                                        if (paymentId) {
                                          try {
                                            const updateRes = await fetch(
                                              `${WEBSERVICE}/payment/updatePaymentStatus`,
                                              {
                                                method: "PUT",
                                                headers: {
                                                  "Content-Type":
                                                    "application/json",
                                                },
                                                body: JSON.stringify({
                                                  paymentIntentId: paymentId,
                                                }),
                                              }
                                            );
                                            const updateData =
                                              await updateRes.json();
                                            if (updateData.status) {
                                              window.location.href =
                                                redirectUrl;
                                            }
                                          } catch {
                                            // API call failed, do not redirect
                                          }
                                        }
                                      }}
                                    />
                                  </PayPalScriptProvider>
                                )
                              ) : (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                    fontSize: 13,
                                    color: "var(--co-gray-500)",
                                  }}
                                >
                                  PayBito Titan is not available. Please try another
                                  payment method.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Stripe ── */}
                      {showStripeOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "stripe" ? "selected" : ""
                          } ${
                            (paymentIntentLoading || stripeProcessing) &&
                            selectedOpt === "stripe"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.stripe }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "stripe";
                              setSelectedOpt((p) =>
                                p === "stripe" ? null : "stripe"
                              );
                              if (opening) {
                                paymentIntentCache.current[1] = null;
                                createPaymentIntent(1);
                              }
                            }}
                          >
                            <div className="co-option-icon stripe">
                              <i className="fa fa-credit-card-alt" />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">
                                Pay with Card
                              </div>
                              <div className="co-option-desc">
                                {isOneTimePayment
                                  ? "Credit or debit card via PayBito Apex"
                                  : "Card subscription via PayBito Apex"}
                              </div>
                            </div>
                            <span className="co-badge stripe">Apex</span>
                          </div>

                          {selectedOpt === "stripe" && (
                            <div
                              style={{
                                padding: "0 18px 18px",
                                borderTop: "1px dashed var(--co-gray-200)",
                                paddingTop: 14,
                              }}
                            >
                              {paymentIntentLoading ? (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                  }}
                                >
                                  <span
                                    className="co-spinner"
                                    style={{
                                      borderTopColor: "#635bff",
                                      borderColor: "rgba(99,91,255,0.3)",
                                    }}
                                  />
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 13,
                                      color: "var(--co-gray-500)",
                                    }}
                                  >
                                    Initializing PayBito Apex...
                                  </span>
                                </div>
                              ) : isOneTimePayment ? (
                                // One-time payment - use Payment Element
                                <StripePaymentWrapper
                                  stripePublishableKey={stripePublishableKey}
                                  stripePaymentIntentSecret={
                                    stripePaymentIntentSecret
                                  }
                                  paymentIntentId={paymentIntentId}
                                  loginEmail={loginEmail}
                                  orderGrandTotal={orderGrandTotal}
                                  grandTotal={grandTotal}
                                  detectedCurrency={detectedCurrency}
                                  redirectSuccessUrl={redirectSuccessUrl}
                                  redirectFailureUrl={redirectFailureUrl}
                                  buildRedirectUrl={buildRedirectUrl}
                                  onError={(errorMessage) => {
                                    showToast("error", errorMessage);
                                  }}
                                />
                              ) : (
                                // Subscription - redirect to Stripe Checkout
                                <>
                                  <div className="co-stripe-sub-info">
                                    <p>
                                      <i
                                        className="fa fa-info-circle"
                                        style={{
                                          color: "#635bff",
                                          marginRight: 6,
                                        }}
                                      />
                                      You'll be redirected to PayBito Apex's secure
                                      hosted checkout page to set up your
                                      subscription.
                                    </p>
                                    <p>
                                      Your payment details are handled directly
                                      by PayBito Apex — we never store them.
                                    </p>
                                  </div>
                                  <button
                                    className="co-btn co-btn-stripe"
                                    onClick={handleStripeSubscription}
                                    disabled={
                                      stripeSubRedirecting || !paymentIntentId
                                    }
                                    style={{ width: "100%" }}
                                  >
                                    {stripeSubRedirecting ? (
                                      <>
                                        <span className="co-spinner" />
                                        &nbsp;Redirecting to PayBito Apex...
                                      </>
                                    ) : (
                                      <>
                                        <i className="fa fa-lock" />
                                        &nbsp;Subscribe with PayBito Apex
                                      </>
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── KurvPay ── */}
                      {showKurvOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "kurv" ? "selected" : ""
                          } ${
                            (paymentIntentLoading || kurvProcessing) &&
                            selectedOpt === "kurv"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.kurv }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "kurv";
                              setSelectedOpt((p) =>
                                p === "kurv" ? null : "kurv"
                              );
                              if (opening) {
                                paymentIntentCache.current[7] = null;
                                createPaymentIntent(7);
                              }
                            }}
                          >
                            <div
                              className="co-option-icon"
                              style={{
                                background: "rgba(0,0,0,0.08)",
                                width: 46,
                                height: 46,
                                borderRadius: 11,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <i
                                className="fa fa-credit-card"
                                style={{ fontSize: 20, color: "#6b7280" }}
                              />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">
                                Pay with PayBito Nexus
                              </div>
                              <div className="co-option-desc">
                                Credit or debit card via PayBito Nexus
                              </div>
                            </div>
                            <span
                              className="co-badge"
                              style={{ background: "#6b7280", color: "#fff" }}
                            >
                              Nexus
                            </span>
                          </div>

                          {selectedOpt === "kurv" && (
                            <div
                              style={{
                                padding: "0 18px 18px",
                                borderTop: "1px dashed var(--co-gray-200)",
                                paddingTop: 14,
                              }}
                            >
                              {paymentIntentLoading && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                  }}
                                >
                                  <span
                                    className="co-spinner"
                                    style={{
                                      borderTopColor: "#111",
                                      borderColor: "rgba(0,0,0,0.15)",
                                    }}
                                  />
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 13,
                                      color: "var(--co-gray-500)",
                                    }}
                                  >
                                    Initializing PayBito Nexus...
                                  </span>
                                </div>
                              )}
                              <div style={{ display: paymentIntentLoading ? "none" : undefined }}>
                                <KurvPaymentForm
                                  tokenizationKey={kurvTokenizationKey}
                                  paymentIntentId={paymentIntentId}
                                  loginEmail={loginEmail}
                                  orderGrandTotal={orderGrandTotal}
                                  grandTotal={grandTotal}
                                  detectedCurrency={detectedCurrency}
                                  redirectSuccessUrl={redirectSuccessUrl}
                                  redirectFailureUrl={redirectFailureUrl}
                                  buildRedirectUrl={buildRedirectUrl}
                                  onError={(msg) =>
                                    showToast("error", msg)
                                  }
                                  shippingProfile={shippingProfile}
                                  orderProducts={orderProducts}
                                  discountAmount={discountAmount}
                                  orderFeeAmount={orderFeeAmount}
                                  gatewayPlanId={kurvGatewayPlanId}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── HMS ── */}
                      {showHmsOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "hms" ? "selected" : ""
                          } ${
                            (paymentIntentLoading || hmsProcessing) &&
                            selectedOpt === "hms"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.hms }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "hms";
                              setSelectedOpt((p) =>
                                p === "hms" ? null : "hms"
                              );
                              if (opening) {
                                paymentIntentCache.current[9] = null;
                                createPaymentIntent(9);
                              }
                            }}
                          >
                            <div
                              className="co-option-icon"
                              style={{
                                background: "rgba(13,148,136,0.12)",
                                width: 46,
                                height: 46,
                                borderRadius: 11,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <i
                                className="fa fa-credit-card"
                                style={{ fontSize: 20, color: "#0d9488" }}
                              />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">
                                Pay with PayBito Nova
                              </div>
                              <div className="co-option-desc">
                                Credit or debit card via PayBito Nova
                              </div>
                            </div>
                            <span
                              className="co-badge"
                              style={{ background: "#0d9488", color: "#fff" }}
                            >
                              Nova
                            </span>
                          </div>

                          {selectedOpt === "hms" && (
                            <div
                              style={{
                                padding: "0 18px 18px",
                                borderTop: "1px dashed var(--co-gray-200)",
                                paddingTop: 14,
                              }}
                            >
                              {paymentIntentLoading && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                  }}
                                >
                                  <span
                                    className="co-spinner"
                                    style={{
                                      borderTopColor: "#0d9488",
                                      borderColor: "rgba(13,148,136,0.15)",
                                    }}
                                  />
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 13,
                                      color: "var(--co-gray-500)",
                                    }}
                                  >
                                    Initializing PayBito Nova...
                                  </span>
                                </div>
                              )}
                              <div style={{ display: paymentIntentLoading ? "none" : undefined }}>
                                <HMSPaymentForm
                                  tokenizationKey={hmsTokenizationKey}
                                  paymentIntentId={paymentIntentId}
                                  loginEmail={loginEmail}
                                  orderGrandTotal={orderGrandTotal}
                                  grandTotal={grandTotal}
                                  detectedCurrency={detectedCurrency}
                                  redirectSuccessUrl={redirectSuccessUrl}
                                  redirectFailureUrl={redirectFailureUrl}
                                  buildRedirectUrl={buildRedirectUrl}
                                  onError={(msg) =>
                                    showToast("error", msg)
                                  }
                                  shippingProfile={shippingProfile}
                                  orderProducts={orderProducts}
                                  discountAmount={discountAmount}
                                  orderFeeAmount={orderFeeAmount}
                                  gatewayPlanId={hmsGatewayPlanId}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── NMI ── */}
                      {showNmiOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "nmi" ? "selected" : ""
                          } ${
                            (paymentIntentLoading || nmiProcessing) &&
                            selectedOpt === "nmi"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.nmi }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "nmi";
                              setSelectedOpt((p) =>
                                p === "nmi" ? null : "nmi"
                              );
                              if (opening) {
                                paymentIntentCache.current[11] = null;
                                setNmiFormKey((k) => k + 1);
                                createPaymentIntent(11);
                              }
                            }}
                          >
                            <div
                              className="co-option-icon"
                              style={{
                                background: "rgba(124,58,237,0.12)",
                                width: 46,
                                height: 46,
                                borderRadius: 11,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <i
                                className="fa fa-credit-card"
                                style={{ fontSize: 20, color: "#7c3aed" }}
                              />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">
                                Pay with PayBito Zenith
                              </div>
                              <div className="co-option-desc">
                                Credit or debit card via PayBito Zenith
                              </div>
                            </div>
                            <span
                              className="co-badge"
                              style={{ background: "#7c3aed", color: "#fff" }}
                            >
                              Zenith
                            </span>
                          </div>

                          {selectedOpt === "nmi" && (
                            <div
                              style={{
                                padding: "0 18px 18px",
                                borderTop: "1px dashed var(--co-gray-200)",
                                paddingTop: 14,
                              }}
                            >
                              {paymentIntentLoading && (
                                <div
                                  style={{
                                    textAlign: "center",
                                    padding: "16px 0",
                                  }}
                                >
                                  <span
                                    className="co-spinner"
                                    style={{
                                      borderTopColor: "#7c3aed",
                                      borderColor: "rgba(124,58,237,0.15)",
                                    }}
                                  />
                                  <span
                                    style={{
                                      marginLeft: 8,
                                      fontSize: 13,
                                      color: "var(--co-gray-500)",
                                    }}
                                  >
                                    Initializing PayBito Zenith...
                                  </span>
                                </div>
                              )}
                              <div style={{ display: paymentIntentLoading ? "none" : undefined }}>
                                <NMIPaymentForm
                                  key={nmiFormKey}
                                  tokenizationKey={paymentIntentLoading ? "" : nmiTokenizationKey}
                                  paymentIntentId={paymentIntentId}
                                  loginEmail={loginEmail}
                                  orderGrandTotal={orderGrandTotal}
                                  grandTotal={grandTotal}
                                  detectedCurrency={detectedCurrency}
                                  redirectSuccessUrl={redirectSuccessUrl}
                                  redirectFailureUrl={redirectFailureUrl}
                                  buildRedirectUrl={buildRedirectUrl}
                                  onError={(msg) =>
                                    showToast("error", msg)
                                  }
                                  shippingProfile={shippingProfile}
                                  orderProducts={orderProducts}
                                  discountAmount={discountAmount}
                                  orderFeeAmount={orderFeeAmount}
                                  gatewayPlanId={nmiGatewayPlanId}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── NetBilling ── */}
                      {showNetBillingOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "netbilling" ? "selected" : ""
                          } ${
                            (paymentIntentLoading || netbillingProcessing) &&
                            selectedOpt === "netbilling"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.netbilling }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "netbilling";
                              setSelectedOpt((p) =>
                                p === "netbilling" ? null : "netbilling"
                              );
                              if (opening) {
                                paymentIntentCache.current[8] = null;
                                createPaymentIntent(8);
                              }
                            }}
                          >
                            <div className="co-option-icon netbilling">
                              <NetBillingIcon />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">Credit Card</div>
                              <div className="co-option-desc">
                                {isOneTimePayment
                                  ? "Card payment via PayBito Vertex"
                                  : "Card subscription via PayBito Vertex"}
                              </div>
                            </div>
                            <span className="co-badge netbilling">Secure</span>
                          </div>
                          {selectedOpt === "netbilling" && (
                            <div
                              className="co-option-content"
                              style={{ padding: "20px" }}
                            >
                              <NetBillingCardForm
                                orderId={invoiceData?.invoice_id}
                                amount={orderGrandTotal || grandTotal || "0"}
                                currency={detectedCurrency.code || "USD"}
                                currencySymbol={detectedCurrency.symbol || ""}
                                isSubscription={isSubscription}
                                description="Invoice Payment"
                                loading={netbillingProcessing}
                                countries={countries}
                                onSubmit={handleNetBillingSubmit}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Card flo ── */}
                      {showCardFloOption && (
                        <div
                          className={`co-option ${
                            selectedOpt === "cardflo" ? "selected" : ""
                          } ${
                            (paymentIntentLoading || cardfloProcessing) &&
                            selectedOpt === "cardflo"
                              ? "is-loading"
                              : ""
                          }`}
                          style={{ order: sectionCssOrder.cardflo }}
                        >
                          <div
                            className="co-option-header"
                            onClick={() => {
                              const opening = selectedOpt !== "cardflo";
                              setSelectedOpt((p) =>
                                p === "cardflo" ? null : "cardflo"
                              );
                              if (opening) {
                                paymentIntentCache.current[10] = null;
                                createPaymentIntent(10);
                              }
                            }}
                          >
                            <div className="co-option-icon cardflo">
                              <CardFloIcon />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">PayBito Sovereign</div>
                              <div className="co-option-desc">
                                {isOneTimePayment
                                  ? "Card payment via PayBito Sovereign"
                                  : "Card subscription via PayBito Sovereign"}
                              </div>
                            </div>
                            <span className="co-badge cardflo">Secure</span>
                          </div>
                          {selectedOpt === "cardflo" && (
                            <div
                              className="co-option-content"
                              style={{ padding: "20px" }}
                            >
                              <CardFloCardForm
                                orderId={invoiceData?.invoice_id}
                                amount={orderGrandTotal || grandTotal || "0"}
                                currency={detectedCurrency.code || "USD"}
                                currencySymbol={detectedCurrency.symbol || ""}
                                isSubscription={isSubscription}
                                description="Invoice Payment"
                                loading={cardfloProcessing}
                                countries={countries}
                                onSubmit={handleCardFloSubmit}
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Pay with Crypto — collapsible ── */}
                      {showCryptoSection && (
                        <div
                          style={{
                            border: cryptoExpanded
                              ? "1.5px solid rgba(37,99,235,0.4)"
                              : "1.5px solid var(--co-gray-200,#e2e6ef)",
                            borderRadius: 12,
                            background: cryptoExpanded
                              ? "rgba(37,99,235,0.03)"
                              : "#fff",
                            overflow: "hidden",
                            transition: "all .2s",
                            order: sectionCssOrder.crypto,
                          }}
                        >
                          <div
                            className="co-option-header"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              const isExpanding = !cryptoExpanded;
                              setCryptoExpanded((p) => !p);
                              // When only one currency is available, auto-select it on expand
                              if (isExpanding && availableCryptos.length === 1) {
                                const singleCrypto = availableCryptos[0];
                                const code = singleCrypto.currency_code;
                                const networks = singleCrypto.network || [];
                                setSelectedCrypto(code);
                                // Auto-select network:
                                // - Single non-NATIVE network (e.g. USDC → ERC)
                                // - Multiple networks (e.g. USDT → first = ERC)
                                if (networks.length === 1 && networks[0] !== "NATIVE") {
                                  setSelectedNetwork(networks[0]);
                                } else if (networks.length > 1) {
                                  setSelectedNetwork(networks[0]);
                                }
                                // NATIVE-only coins (BTC, ETH, etc.) need no network selection
                              }
                              if (isExpanding) {
                                // Scroll down to show Crypto options
                                setTimeout(() => {
                                  window.scrollBy({
                                    top: 300,
                                    behavior: "smooth",
                                  });
                                }, 100);
                              }
                            }}
                          >
                            <div
                              style={{
                                width: 46,
                                height: 46,
                                borderRadius: 11,
                                flexShrink: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background: "rgba(37,99,235,0.12)",
                              }}
                            >
                              <i
                                className="fa fa-coins"
                                style={{
                                  fontSize: 22,
                                  color: "#2563EB",
                                }}
                              />
                            </div>
                            <div className="co-option-text">
                              <div className="co-option-title">
                                {showBrandWallet && !showExternalWallet && !showGuestCheckout
                                  ? `Pay with ${brandName} wallet`
                                  : "Pay with Crypto"}
                              </div>
                              {(showExternalWallet || showGuestCheckout) && (
                                <div className="co-option-desc">
                                  {[
                                    showBrandWallet && `${brandName} wallet`,
                                    showExternalWallet && "External wallet",
                                    showGuestCheckout && "Guest checkout",
                                  ]
                                    .filter(Boolean)
                                    .join(", ")}
                                </div>
                              )}
                            </div>
                            <i
                              className={`fa ${
                                cryptoExpanded
                                  ? "fa-chevron-up"
                                  : "fa-chevron-down"
                              }`}
                              style={{
                                fontSize: 13,
                                color: "var(--co-gray-500,#6b7280)",
                                marginLeft: 8,
                              }}
                            />
                          </div>
                          {cryptoExpanded && (
                            <div
                              style={{
                                borderTop: "1px dashed rgba(37,99,235,0.25)",
                                padding: "16px 18px",
                                display: "flex",
                                flexDirection: "column",
                                gap: 14,
                              }}
                            >
                              {/* Crypto selector */}
                              <div>
                                {availableCryptos.length > 1 && (<>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "#111827",
                                    }}
                                  >
                                    Choose coin
                                  </span>
                                  {selectedCrypto && (
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: "#2563EB",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 4,
                                      }}
                                    >
                                      <i
                                        className="fa fa-check-circle"
                                        style={{ color: "#2563EB" }}
                                      />
                                      {selectedCrypto} selected
                                    </span>
                                  )}
                                </div>
                                <div
                                  style={{
                                    border:
                                      "1.5px solid var(--co-gray-200,#e2e6ef)",
                                    borderRadius: 10,
                                    overflow: "hidden",
                                    maxHeight: 172,
                                    overflowY: "auto",
                                  }}
                                >
                                  {availableCryptos.map((cr, idx) => {
                                    const code = cr.currency_code;
                                    const name = cr.currency_name;
                                    const logo = cr.logo;
                                    const isSelected = selectedCrypto === code;
                                    const isLast =
                                      idx === availableCryptos.length - 1;
                                    return (
                                      <div
                                        key={code}
                                        onClick={() => {
                                          silentDisconnectWallet();
                                          setSelectedCrypto(
                                            isSelected ? "" : code
                                          );
                                          setSelectedNetwork("");
                                        }}
                                        style={{
                                          width: "100%",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 10,
                                          padding: "9px 12px",
                                          cursor: "pointer",
                                          borderBottom: isLast
                                            ? "none"
                                            : "1px solid var(--co-gray-100,#f3f4f6)",
                                          boxSizing: "border-box",
                                        }}
                                      >
                                        <img
                                          src={logo}
                                          alt={code}
                                          style={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            flexShrink: 0,
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: isSelected
                                              ? "#d97706"
                                              : "var(--co-gray-800,#1f2937)",
                                            minWidth: 44,
                                          }}
                                        >
                                          {code}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: 11,
                                            color: "var(--co-gray-400,#9ca3af)",
                                            flex: 1,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {name}
                                        </span>
                                        <span
                                          style={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            border: isSelected
                                              ? "none"
                                              : "1.5px solid var(--co-gray-200,#e2e6ef)",
                                            background: isSelected
                                              ? "#2563EB"
                                              : "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                            transition: "all .15s",
                                          }}
                                        >
                                          {isSelected && (
                                            <i
                                              className="fa fa-check"
                                              style={{
                                                fontSize: 7,
                                                color: "#fff",
                                              }}
                                            />
                                          )}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                                </>)}
                                {/* Network selector */}
                                {selectedCrypto &&
                                  (() => {
                                    const selectedCoin = availableCryptos.find(
                                      (c) => c.currency_code === selectedCrypto
                                    );
                                    const networks =
                                      selectedCoin?.network || [];
                                    const selectableNetworks = networks.filter(
                                      (n) => n !== "NATIVE"
                                    );
                                    if (selectableNetworks.length <= 1)
                                      return null;

                                    // Get network label for display
                                    const getNetworkLabel = (net) => {
                                      return net === "ERC"
                                        ? "ERC-20 (Ethereum)"
                                        : net === "TRC"
                                        ? "TRC-20 (Tron)"
                                        : net;
                                    };

                                    const getNetworkColor = (net) => {
                                      return net === "ERC"
                                        ? "#627EEA"
                                        : net === "TRC"
                                        ? "#ef0027"
                                        : "#2563EB";
                                    };

                                    return (
                                      <div
                                        style={{
                                          marginTop: 12,
                                          padding: "14px 16px",
                                          borderRadius: 10,
                                          backgroundColor: "#f9fafb",
                                          border: "1.5px solid #e2e6ef",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: 11,
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            letterSpacing: ".4px",
                                            color: "#2563EB",
                                            marginBottom: 10,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 6,
                                          }}
                                        >
                                          <i
                                            className="fa fa-sitemap"
                                            style={{ color: "#2563EB" }}
                                          />{" "}
                                          Select Network for {selectedCrypto}
                                        </div>

                                        {/* DROPDOWN SELECTOR with visible arrow */}
                                        <div style={{ position: "relative" }}>
                                          <select
                                            className="custom-select-white"
                                            value={selectedNetwork}
                                            onChange={(e) => {
                                              const newNetwork = e.target.value;
                                              if (
                                                isTestMode &&
                                                newNetwork === "ERC" &&
                                                selectedCrypto === "USDT"
                                              ) {
                                                showToast(
                                                  "error",
                                                  "ERC-20 network is not available in test mode for USDT"
                                                );
                                                return;
                                              }
                                              silentDisconnectWallet();
                                              setSelectedNetwork(newNetwork);

                                              setTimeout(() => {
                                                const brandWalletSection =
                                                  document.querySelector(
                                                    ".co-option.selected"
                                                  );
                                                if (brandWalletSection) {
                                                  brandWalletSection.scrollIntoView(
                                                    {
                                                      behavior: "smooth",
                                                      block: "center",
                                                    }
                                                  );
                                                }
                                              }, 150);
                                            }}
                                            disabled={networks.length === 1}
                                            style={{
                                              width: "100%",
                                              padding: "10px 14px",
                                              paddingRight: "32px",
                                              borderRadius: 8,
                                              fontSize: 13,
                                              fontWeight: 500,
                                              border:
                                                "1.5px solid #e2e6ef !important",
                                              background:
                                                networks.length === 1
                                                  ? "#f9fafb !important"
                                                  : "#fff !important",
                                              color:
                                                networks.length === 1
                                                  ? "#6b7280 !important"
                                                  : "#000000 !important",
                                              cursor:
                                                networks.length === 1
                                                  ? "not-allowed"
                                                  : "pointer",
                                              outline: "none",
                                              transition: "all .15s",
                                              appearance: "none",
                                              WebkitAppearance: "none",
                                              MozAppearance: "none",
                                              opacity:
                                                networks.length === 1 ? 0.7 : 1,
                                            }}
                                          >
                                            {networks.length === 1 ? (
                                              <option
                                                value={networks[0]}
                                                style={{
                                                  color: "#000000 !important",
                                                  backgroundColor:
                                                    "#ffffff !important",
                                                }}
                                              >
                                                {getNetworkLabel(networks[0])}
                                              </option>
                                            ) : (
                                              <>
                                                <option
                                                  value=""
                                                  style={{
                                                    color: "#000000 !important",
                                                    backgroundColor:
                                                      "#ffffff !important",
                                                  }}
                                                >
                                                  -- Select a network --
                                                </option>
                                                {networks.map((net) => (
                                                  <option
                                                    key={net}
                                                    value={net}
                                                    style={{
                                                      color:
                                                        "#000000 !important",
                                                      backgroundColor:
                                                        "#ffffff !important",
                                                    }}
                                                  >
                                                    {getNetworkLabel(net)}
                                                  </option>
                                                ))}
                                              </>
                                            )}
                                          </select>

                                          {/* Custom dropdown arrow icon */}
                                          <div
                                            style={{
                                              position: "absolute",
                                              right: "12px",
                                              top: "50%",
                                              transform: "translateY(-50%)",
                                              pointerEvents: "none",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              opacity:
                                                networks.length === 1 ? 0.4 : 1,
                                            }}
                                          >
                                            <svg
                                              width="14"
                                              height="14"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke={
                                                selectedNetwork
                                                  ? getNetworkColor(
                                                      selectedNetwork
                                                    )
                                                  : "#6b7280"
                                              }
                                              strokeWidth="2"
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                            >
                                              <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                          </div>
                                        </div>

                                        {selectedNetwork && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              fontSize: 11,
                                              color: "#10b981",
                                              fontWeight: 600,
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 5,
                                            }}
                                          >
                                            <i
                                              className="fa fa-check-circle"
                                              style={{ color: "#10b981" }}
                                            />{" "}
                                            {selectedNetwork === "ERC"
                                              ? "ERC-20"
                                              : selectedNetwork === "TRC"
                                              ? "TRC-20"
                                              : selectedNetwork}{" "}
                                            network selected
                                          </div>
                                        )}
                                        {!selectedNetwork && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              fontSize: 11,
                                              color: "#ef4444",
                                              fontWeight: 600,
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 5,
                                            }}
                                          >
                                            <i
                                              className="fa fa-exclamation-circle"
                                              style={{ color: "#ef4444" }}
                                            />{" "}
                                            Please select a network to continue
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                              </div>
                              <div
                                style={{
                                  height: 1,
                                  background: "rgba(37,99,235,0.15)",
                                }}
                              />
                              {/* Brand Wallet */}
                              {/* showBrandWallet &&
                                (brandWalletAvaliability === "1" ||
                                  localStorage.getItem("access_token")) */}
                              {showBrandWallet && (
                                <div
                                  className={`co-option ${
                                    selectedOpt === "wallet" ? "selected" : ""
                                  } ${walletLoading ? "is-loading" : ""}`}
                                  style={{
                                    border:
                                      "1.5px solid var(--co-gray-200,#e2e6ef)",
                                    order: cryptoSubCssOrder.brandWallet,
                                    position: "relative",
                                    ...(paymentMethodsLocked
                                      ? { opacity: 0.4, userSelect: "none" }
                                      : {}),
                                  }}
                                >
                                  {paymentMethodsLocked && (
                                    <div
                                      title="Choose your preferred cryptocurrency above."
                                      onClick={() => showToast("info", "Choose your preferred cryptocurrency above.")}
                                      style={{ position: "absolute", inset: 0, zIndex: 10, cursor: "not-allowed" }}
                                    />
                                  )}
                                  <div
                                    className="co-option-header"
                                    onClick={() => {
                                      const opening = selectedOpt !== "wallet";
                                      setSelectedOpt((p) =>
                                        p === "wallet" ? null : "wallet"
                                      );
                                      if (opening) {
                                        createPaymentIntent(3);
                                        initWalletInline();
                                        // Scroll down to show Brand Wallet options
                                        setTimeout(() => {
                                          window.scrollBy({
                                            top: 300,
                                            behavior: "smooth",
                                          });
                                        }, 100);
                                      } else {
                                        handleCloseReceiveQR();
                                      }
                                    }}
                                  >
                                    <div className="co-option-icon blue">
                                      <i className="fa fa-credit-card" />
                                    </div>
                                    <div className="co-option-text">
                                      <div className="co-option-title">
                                        Pay with {brandName} Wallet
                                      </div>
                                      <div className="co-option-desc">
                                        Pay instantly using your credit or
                                        debit card, or use your existing{" "}
                                        {brandName} wallet balance.
                                      </div>
                                    </div>
                                    <span className="co-badge fast">
                                      Fastest
                                    </span>
                                  </div>
                                  {selectedOpt === "wallet" && (
                                    <div
                                      style={{
                                        padding: "0 18px 18px",
                                        borderTop:
                                          "1px dashed var(--co-gray-200,#e2e6ef)",
                                        paddingTop: 14,
                                      }}
                                    >
                                      {/* Loading state */}
                                      {walletLoading && (
                                        <div
                                          style={{
                                            textAlign: "center",
                                            padding: "16px 0",
                                          }}
                                        >
                                          <span
                                            className="co-spinner"
                                            style={{ width: 24, height: 24 }}
                                          />
                                          <p
                                            style={{
                                              fontSize: 13,
                                              color: "var(--co-gray-500)",
                                              marginTop: 8,
                                            }}
                                          >
                                            Connecting to {brandName} Wallet…
                                          </p>
                                        </div>
                                      )}
                                      {/* Case 2: Not logged in + brandWalletAvaliability === "1" → Sign In */}
                                      {!walletLoading &&
                                        !isWalletLoggedIn &&
                                        brandWalletAvaliability === "1" && (
                                          <div>
                                            <p
                                              style={{
                                                fontSize: "12.5px",
                                                color:
                                                  "var(--co-gray-500,#6b7280)",
                                                marginBottom: 14,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              Sign in to your {brandName}{" "}
                                              account to pay with your wallet
                                              balance.
                                            </p>
                                            <button
                                              className="co-btn co-btn-blue"
                                              onClick={initWalletInline}
                                              disabled={!selectedCrypto}
                                            >
                                              <i className="fa fa-sign-in" />
                                              &nbsp;Sign in to {brandName}
                                            </button>
                                          </div>
                                        )}

                                      {/* Case 3: Not logged in + brandWalletAvaliability === "0" → Sign Up */}
                                      {!walletLoading &&
                                        !isWalletLoggedIn &&
                                        brandWalletAvaliability === "0" && (
                                          <div>
                                            <p
                                              style={{
                                                fontSize: "12.5px",
                                                color:
                                                  "var(--co-gray-500,#6b7280)",
                                                marginBottom: 14,
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              Create a {brandName} account to
                                              pay with your wallet balance.
                                            </p>
                                            <button
                                              className="co-btn co-btn-blue"
                                              onClick={initWalletInline}
                                              disabled={!selectedCrypto}
                                            >
                                              <i className="fa fa-user-plus" />
                                              &nbsp;Sign up to {brandName}
                                            </button>
                                          </div>
                                        )}

                                      {/* Case 1: Logged in, no crypto selected yet */}
                                      {!walletLoading &&
                                        isWalletLoggedIn &&
                                        !selectedCrypto && (
                                          <p
                                            style={{
                                              fontSize: "12.5px",
                                              color:
                                                "var(--co-gray-500,#6b7280)",
                                              marginBottom: 0,
                                              lineHeight: 1.6,
                                            }}
                                          >
                                            Please select a cryptocurrency above
                                            to view your wallet balance and
                                            proceed with payment.
                                          </p>
                                        )}
                                      {!walletLoading &&
                                        isWalletLoggedIn &&
                                        selectedCrypto && (
                                          <div>
                                            {/* Balance & Amount */}
                                            <div
                                              style={{
                                                background:
                                                  "var(--co-gray-100,#f3f4f6)",
                                                borderRadius: 10,
                                                padding: "12px 16px",
                                                marginBottom: 14,
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                  marginBottom: 6,
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: "var(--co-gray-500)",
                                                    textTransform: "uppercase",
                                                    letterSpacing: ".4px",
                                                  }}
                                                >
                                                  Wallet Balance
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: 15,
                                                    fontWeight: 700,
                                                    color: hasSufficientBalance
                                                      ? "#10b981"
                                                      : "#ef4444",
                                                  }}
                                                >
                                                  {walletBalance !== null
                                                    ? walletBalance
                                                    : "…"}{" "}
                                                  {selectedCrypto ||
                                                    currency ||
                                                    "BTC"}
                                                </span>
                                              </div>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <span
                                                  style={{
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    color: "var(--co-gray-500)",
                                                    textTransform: "uppercase",
                                                    letterSpacing: ".4px",
                                                  }}
                                                >
                                                  Amount to Pay
                                                </span>
                                                <span
                                                  style={{
                                                    fontSize: 15,
                                                    fontWeight: 700,
                                                    color: "var(--co-gray-900)",
                                                  }}
                                                >
                                                  {safeDisplayCryptoAmt}{" "}
                                                  {selectedCrypto ||
                                                    currency ||
                                                    "BTC"}
                                                </span>
                                              </div>
                                            </div>

                                            {/* Sufficient balance — Pay button hasSufficientBalance*/}
                                            {hasSufficientBalance && (
                                              <>
                                                <button
                                                  className="co-btn co-btn-blue"
                                                  onClick={handleWalletPayment}
                                                  disabled={
                                                    payLoading ||
                                                    walletBalance === null ||
                                                    isNetworkMissing
                                                  }
                                                  style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                  }}
                                                >
                                                  {payLoading ? (
                                                    <span
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                      }}
                                                    >
                                                      <span className="co-spinner" />
                                                      Processing…
                                                    </span>
                                                  ) : (
                                                    <span
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      <i className="fa fa-credit-card" />
                                                      {isSubscription ? (
                                                        trailAvaliability ===
                                                          "0" ||
                                                        trailAvaliability ===
                                                          0 ? (
                                                          <>
                                                            Subscribe &amp; Pay{" "}
                                                            {
                                                              safeDisplayCryptoAmt
                                                            }{" "}
                                                            {selectedCrypto ||
                                                              currency ||
                                                              "BTC"}
                                                          </>
                                                        ) : (
                                                          <>Subscribe</>
                                                        )
                                                      ) : (
                                                        <>
                                                          Proceed to Pay{" "}
                                                          {safeDisplayCryptoAmt}{" "}
                                                          {selectedCrypto ||
                                                            currency ||
                                                            "BTC"}
                                                        </>
                                                      )}
                                                    </span>
                                                  )}
                                                </button>
                                                {/* isSubscription */}
                                                {isSubscription && (
                                                  <div
                                                    style={{
                                                      width: "100%",
                                                      marginTop: 10,
                                                    }}
                                                  >
                                                    <button
                                                      className="co-btn co-btn-green"
                                                      onClick={() =>
                                                        autoLoginRedirectToPage(
                                                          "my-wallet"
                                                        )
                                                      }
                                                      style={{
                                                        width: "100%",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        alignItems: "center",
                                                        gap: 2,
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          display: "flex",
                                                          alignItems: "center",
                                                          gap: 6,
                                                          whiteSpace: "nowrap",
                                                        }}
                                                      >
                                                        <i className="fa fa-wallet" />
                                                        Load balance in your{" "}
                                                        {brandName} Wallet
                                                      </span>
                                                    </button>
                                                    <span
                                                      style={{
                                                        fontSize: 12,
                                                        fontWeight: 500,
                                                        color: "#2563EB",
                                                        marginTop: 8,
                                                        display: "block",
                                                        textAlign: "center",
                                                        letterSpacing: "0.3px",
                                                      }}
                                                    >
                                                      Required for future
                                                      subscription payments
                                                    </span>
                                                  </div>
                                                )}
                                              </>
                                            )}

                                            {/* Insufficient balance — show options */}
                                            {!hasSufficientBalance && (
                                              <div>
                                                <p
                                                  style={{
                                                    fontSize: 13,
                                                    color: "#ef4444",
                                                    fontWeight: 600,
                                                    marginBottom: 12,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 6,
                                                  }}
                                                >
                                                  <i className="fa fa-exclamation-circle" />{" "}
                                                  The balance in your wallet is
                                                  not sufficient to complete
                                                  this purchase.
                                                </p>
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 10,
                                                  }}
                                                >
                                                  {billingProfile?.isBuyCryptoEnabled ===
                                                      1 && (
                                                      <button
                                                        className="co-btn co-btn-blue"
                                                        onClick={handleBuyCrypto}
                                                        disabled={pabtekDirectLoading}
                                                        style={{ flex: 1 }}
                                                      >
                                                        {pabtekDirectLoading ? (
                                                          <>
                                                            <i className="fa fa-spinner fa-spin" />
                                                            &nbsp;Processing…
                                                          </>
                                                        ) : (
                                                          <>
                                                            <i className="fa fa-credit-card" />
                                                            &nbsp;Pay with Card
                                                          </>
                                                        )}
                                                      </button>
                                                    )}
                                                  <button
                                                    onClick={
                                                      handleShowReceiveQR
                                                    }
                                                    style={{ flex: 1 }}
                                                    className="co-btn co-btn-grey"
                                                  >
                                                    <i className="fa fa-download" />
                                                    &nbsp;Receive{" "}
                                                    {selectedCrypto ||
                                                      currency ||
                                                      "BTC"}
                                                  </button>
                                                </div>

                                                <p
                                                  style={{
                                                    fontSize: 15,
                                                    color: "#1a1a1a",
                                                    fontWeight: 600,
                                                    marginBottom: 12,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: 6,
                                                    cursor: "pointer",
                                                    letterSpacing: "0.3px",
                                                    paddingTop: 14,
                                                  }}
                                                  onClick={() =>
                                                    autoLoginRedirectToPage(
                                                      "my-wallet"
                                                    )
                                                  }
                                                >
                                                  Go to{" "}
                                                  <span
                                                    style={{
                                                      textDecoration:
                                                        "underline",
                                                      fontWeight: 700,
                                                      color: "#1a1a1a",
                                                    }}
                                                  >
                                                    {brandName} Wallet
                                                  </span>
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {/* External Wallet */}
                              {showExternalWallet && (
                                <div
                                  style={{
                                    order: cryptoSubCssOrder.externalWallet,
                                    position: "relative",
                                    ...(paymentMethodsLocked
                                      ? { opacity: 0.4, userSelect: "none" }
                                      : {}),
                                  }}
                                >
                                  {paymentMethodsLocked && (
                                    <div
                                      title="Choose your preferred cryptocurrency above."
                                      onClick={() => showToast("info", "Choose your preferred cryptocurrency above.")}
                                      style={{ position: "absolute", inset: 0, zIndex: 10, cursor: "not-allowed" }}
                                    />
                                  )}
                                  <ExternalWalletOption
                                    selectedOpt={selectedOpt}
                                    connectExternalWallet={
                                      connectExternalWallet
                                    }
                                    externalConnecting={externalConnecting}
                                    isTron={isTron}
                                    connectedWallet={connectedWallet}
                                    walletLabel={walletLabel}
                                    walletDesc={walletDesc}
                                    proceedBtnClass={proceedBtnClass}
                                    proceedWithExternalWallet={
                                      proceedWithExternalWallet
                                    }
                                    txConfirmed={txConfirmed}
                                    txStatus={txStatus}
                                    txHash={txHash}
                                    disconnectWallet={disconnectWallet}
                                  />
                                </div>
                              )}
                              {/* Guest Checkout */}
                              {showGuestCheckout && (
                                <div
                                  style={{
                                    order: cryptoSubCssOrder.guestCheckout,
                                    position: "relative",
                                    ...(paymentMethodsLocked
                                      ? { opacity: 0.4, userSelect: "none" }
                                      : {}),
                                  }}
                                >
                                  {paymentMethodsLocked && (
                                    <div
                                      title="Choose your preferred cryptocurrency above."
                                      onClick={() => showToast("info", "Choose your preferred cryptocurrency above.")}
                                      style={{ position: "absolute", inset: 0, zIndex: 10, cursor: "not-allowed" }}
                                    />
                                  )}
                                  <GuestCheckoutOption
                                    selectedOpt={selectedOpt}
                                    rawDataParam={rawDataParam}
                                    quantity={quantity}
                                    hostedCatalogId={hostedCatalogId}
                                    emailBillId={emailBillId}
                                    createPaymentIntent={createPaymentIntent}
                                    appliedDiscount={appliedDiscount}
                                    selectedCrypto={selectedCrypto}
                                    selectedNetwork={selectedNetwork}
                                    availableCryptos={availableCryptos}
                                    grandTotal={orderGrandTotal || grandTotal}
                                    cryptoConvertedAmt={cryptoConvertedAmt}
                                    cryptoMarketPrice={cryptoMarketPrice}
                                    setGuestCheckoutData={(data) => {
                                      // Optional: Store in state if needed
                                      console.log("Guest checkout data:", data);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ════ RIGHT ════ */}
                <div className="co-right">
                  <div
                    className="co-section-label"
                    style={{ fontSize: "22px", fontWeight: 700 }}
                  >
                    Order Summary
                  </div>
                  {orderLoading ? (
                    <>
                      <div
                        className="co-skel"
                        style={{ height: 22, width: "55%" }}
                      />
                      <div
                        className="co-skel"
                        style={{ height: 14, width: "40%", marginBottom: 16 }}
                      />
                    </>
                  ) : (
                    billingProfile && (
                      <>
                        <div
                          style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: "var(--co-gray-900)",
                            marginBottom: 4,
                          }}
                        >
                          {"Order Summary"}
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "var(--co-gray-700)",
                            marginBottom: 4,
                          }}
                        >
                          {isOneTimePayment
                            ? "One-time Payment"
                            : `Reccuring Payment ${
                                recurringInterval ? `${recurringInterval}` : ""
                              }`}
                        </div>
                      </>
                    )
                  )}
                  <div className="co-divider" />
                  {orderLoading ? (
                    <>
                      <div className="co-skel" style={{ height: 36 }} />
                      <div className="co-skel" style={{ height: 36 }} />
                      <div className="co-skel" style={{ height: 36 }} />
                    </>
                  ) : (
                    <div className="co-tbl-wrap">
                      <table className="co-tbl">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th style={{ textAlign: "center" }}>Quantity</th>
                            <th style={{ textAlign: "right" }}></th>
                            <th style={{ textAlign: "right" }}>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderProducts.map((p, i) => {
                            const base = getOrderBase(p);
                            return (
                              <tr key={p.productId || p.id || i}>
                                <td>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      color: "var(--co-gray-900)",
                                    }}
                                  >
                                    {p.name}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    textAlign: "center !important",
                                    color: "#374151",
                                  }}
                                >
                                  {p.itemQuantity ?? 1}
                                </td>
                                <td style={{ textAlign: "right" }}></td>
                                <td
                                  style={{ textAlign: "right", color: "#000" }}
                                >
                                  <span
                                    style={{
                                      whiteSpace: "nowrap",
                                      color: "#000",
                                    }}
                                  >
                                    {cur.symbol} {base} {cur.code}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {hPct > 0 && (
                            <tr>
                              <td colSpan={2} className="co-fee-label">
                                Handling Fee ({hPct}%)
                              </td>
                              <td
                                style={{ textAlign: "right" }}
                                className="co-fee-label"
                              >
                                —
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color: "#000",
                                }}
                              >
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    color: "#000",
                                  }}
                                >
                                  {cur.symbol} {parseFloat(totalHandling).toFixed(2)} {cur.code}
                                </span>
                              </td>
                            </tr>
                          )}
                          {tPct > 0 && (
                            <tr>
                              <td colSpan={2} className="co-fee-label">
                                Tax ({tPct}%)
                              </td>
                              <td
                                style={{ textAlign: "right" }}
                                className="co-fee-label"
                              >
                                —
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color: "#000",
                                }}
                              >
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    color: "#000",
                                  }}
                                >
                                  {cur.symbol} {parseFloat(totalTax).toFixed(2)} {cur.code}
                                </span>
                              </td>
                            </tr>
                          )}
                          {/* Discount row */}
                          {appliedDiscount && discountAmount > 0 && (
                            <tr>
                              <td
                                colSpan={2}
                                className="co-fee-label co-discount-text"
                              >
                                Discount (
                                {appliedDiscount.discountPercentage ||
                                  appliedDiscount.discountPercent}
                                %) (Min. {cur.symbol}{" "}
                                {appliedDiscount.minimumCartValue})
                                <span
                                  style={{
                                    fontSize: 10,
                                    marginLeft: 6,
                                    opacity: 0.8,
                                  }}
                                >
                                  {/* <span style={{ whiteSpace: "nowrap" }}> */}
                                  {/* (Min. {cur.symbol}{" "}
                                    {appliedDiscount.minimumCartValue}) */}
                                  {/* </span> */}
                                </span>
                              </td>
                              <td
                                style={{ textAlign: "right" }}
                                className="co-fee-label"
                              >
                                —
                              </td>
                              <td
                                style={{
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color: "#10b981",
                                }}
                              >
                                {/* <span style={{ whiteSpace: "nowrap" }}> */}-
                                {cur.symbol} {parseFloat(discountAmount).toFixed(2)} {cur.code}
                                {/* </span> */}
                              </td>
                            </tr>
                          )}
                          {orderFeeApplied && (
                            <tr>
                              <td colSpan={2} className="co-fee-label">
                                {orderFeeLoading ? (
                                  <span
                                    style={{
                                      fontStyle: "italic",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    Calculating fee…
                                  </span>
                                ) : (
                                  `Processing Fee (${orderFeePercent}%)`
                                )}
                              </td>
                              <td
                                style={{ textAlign: "right" }}
                                className="co-fee-label"
                              ></td>
                              <td
                                style={{ textAlign: "right", color: "#111827" }}
                              >
                                {orderFeeLoading ? (
                                  "—"
                                ) : (
                                  <span
                                    style={{
                                      whiteSpace: "nowrap",
                                      color: "#111827",
                                    }}
                                  >
                                    {cur.symbol} {orderFeeAmount.toFixed(2)}{" "}
                                    {cur.code}
                                  </span>
                                )}
                              </td>
                            </tr>
                          )}
                          {/* Wallet Balance Deduction */}
                          {redeemAmount > 0 && (
                            <tr>
                              <td
                                colSpan={2}
                                style={{ padding: "10px 16px", fontSize: 12 }}
                              >
                                <span className="co-wallet-label">
                                  Wallet Balance Applied
                                </span>
                              </td>
                              <td style={{ textAlign: "right", fontSize: 12 }}>
                                <span className="co-wallet-label">—</span>
                              </td>
                              <td
                                style={{ textAlign: "right", fontWeight: 600 }}
                              >
                                <span
                                  className="co-wallet-label"
                                  style={{ whiteSpace: "nowrap" }}
                                >
                                  -{cur.symbol}
                                  {parseFloat(redeemAmount).toFixed(2)} {cur.code}
                                </span>
                              </td>
                            </tr>
                          )}
                        </tbody>
                        <tfoot>
                          <th colSpan={3} style={{ textAlign: "left" }}>
                            Total
                          </th>
                          <th style={{ textAlign: "right" }}>
                            {orderFeeLoading ? (
                              "…"
                            ) : (
                              <span style={{ whiteSpace: "nowrap" }}>
                                {cur.symbol} {orderGrandTotal.toFixed(2)}{" "}
                                {cur.code}
                              </span>
                            )}
                          </th>
                        </tfoot>
                      </table>
                    </div>
                  )}

                  {/* Wallet Redeem Notification Banner */}
                  {redeemAmount > 0 && (
                    <div className="co-wallet-banner">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#0369a1"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>
                        <strong>
                          {cur.symbol}
                          {parseFloat(redeemAmount).toFixed(2)} {cur.code}
                        </strong>{" "}
                        has been redeemed from your wallet balance. Your final
                        payable amount is{" "}
                        <strong>
                          {cur.symbol}
                          {orderGrandTotal.toFixed(2)} {cur.code}
                        </strong>
                        .
                      </span>
                    </div>
                  )}

                  {/* Discount Notification Banner */}
                  {appliedDiscount && discountAmount > 0 && (
                    <div className="co-discount-banner">
                      <span style={{ fontWeight: 600 }}></span>{" "}
                      <span style={{ whiteSpace: "nowrap" }}></span>
                      {/* <span
                        style={{
                          fontSize: 10,
                          display: "block",
                          marginTop: 2,
                          color: "#2e7d32",
                        }}
                      > */}
                      🎉 Discount Applied! You saved {cur.symbol}{" "}
                      {discountAmount.toFixed(3)} {cur.code}{" "}
                      {getDiscountMessage(appliedDiscount)}
                      {/* </span> */}
                    </div>
                  )}

                  <div className="co-total-card">
                    <h4>Order Total</h4>
                    {orderLoading ? (
                      <div
                        className="co-skel"
                        style={{ height: 32, width: "50%" }}
                      />
                    ) : (
                      <>
                        {appliedDiscount && discountAmount > 0 && (
                          <div
                            className="co-original-price"
                            style={{ marginBottom: 4 }}
                          >
                            Original:{" "}
                            {/* <span style={{ whiteSpace: "nowrap" }}> */}
                            {cur.symbol} {orderSubtotal.toFixed(2)} {cur.code}
                            {/* </span> */}
                          </div>
                        )}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                            marginBottom: 10,
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                            <span
                              className="co-total-val"
                              style={{ marginBottom: 0 }}
                            >
                              {orderFeeLoading
                                ? "…"
                                : (trailAvaliability == 1 || trailAvaliability === "1")
                                  ? `${cur.symbol} 0.00`
                                  : `${cur.symbol} ${orderGrandTotal.toFixed(2)}`}
                            </span>
                            {(trailAvaliability == 1 || trailAvaliability === "1") && (
                              <>
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                  Due today (free trial)
                                </span>
                                <span style={{ fontSize: 11, color: "#374151" }}>
                                  Then {cur.symbol} {orderGrandTotal.toFixed(2)} {cur.code} after trial ends
                                </span>
                              </>
                            )}
                          </div>
                          {selectedCrypto && (
                            <>
                              <span
                                style={{
                                  fontSize: 28,
                                  color: "var(--co-gray-500)",
                                  opacity: 0.5,
                                  fontWeight: 300,
                                }}
                              >
                                ≈
                              </span>
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <div
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "8px 14px",
                                    borderRadius: 10,
                                    background: cryptoAmtInputError
                                      ? "rgba(239,68,68,0.06)"
                                      : "rgba(37,99,235,0.10)",
                                    border: cryptoAmtInputError
                                      ? "1.5px solid rgba(239,68,68,0.45)"
                                      : "1.5px solid rgba(37,99,235,0.30)",
                                    minHeight: 44,
                                    overflow: "visible",
                                  }}
                                >
                                  {cryptoPriceLoading ? (
                                    <span
                                      style={{
                                        fontSize: 13,
                                        color: "#2563EB",
                                        fontWeight: 600,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                      }}
                                    >
                                      <span
                                        style={{
                                          width: 12,
                                          height: 12,
                                          borderRadius: "50%",
                                          border: "2px solid rgba(37,99,235,0.3)",
                                          borderTopColor: "#2563EB",
                                          display: "inline-block",
                                          animation:
                                            "co-spin .75s linear infinite",
                                        }}
                                      />
                                      Loading…
                                    </span>
                                  ) : cryptoConvertedAmt !== null ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 6,
                                        flexWrap: "nowrap",
                                      }}
                                    >
                                      <input
                                        type="number"
                                        className={`co-crypto-amt-input${cryptoAmtInputError ? " co-crypto-amt-input--error" : ""}`}
                                        value={cryptoConvertedAmt}
                                        min={minCryptoAmt || 0}
                                        step="any"
                                        onChange={handleCryptoAmtInputChange}
                                        style={{
                                          fontSize: 16,
                                          fontWeight: 700,
                                          width: 110,
                                          minWidth: 60,
                                          padding: 0,
                                          fontFamily: "inherit",
                                        }}
                                      />
                                      <span
                                        style={{
                                          fontSize: 15,
                                          fontWeight: 700,
                                          color: cryptoAmtInputError
                                            ? "#ef4444"
                                            : "#2563EB",
                                          whiteSpace: "nowrap",
                                          flexShrink: 0,
                                        }}
                                      >
                                        {selectedCrypto}
                                      </span>
                                      {(() => {
                                        const coin = availableCryptos.find(
                                          (c) =>
                                            c.currency_code === selectedCrypto
                                        );
                                        const nets = coin?.network || [];
                                        const isNativeOnly =
                                          nets.length === 1 &&
                                          nets[0] === "NATIVE";
                                        if (!isNativeOnly && selectedNetwork) {
                                          const label =
                                            selectedNetwork === "ERC"
                                              ? "ERC-20"
                                              : selectedNetwork === "TRC"
                                              ? "TRC-20"
                                              : selectedNetwork;
                                          return (
                                            <span
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                background: "rgba(37,99,235,0.12)",
                                                border: "1px solid rgba(37,99,235,0.35)",
                                                color: "#2563EB",
                                                borderRadius: 5,
                                                padding: "1px 6px",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                              }}
                                            >
                                              {label}
                                            </span>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  ) : (
                                    <span
                                      style={{
                                        fontSize: 12,
                                        color: "#2563EB",
                                        opacity: 0.5,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {(() => {
                                        const coin = availableCryptos.find(
                                          (c) =>
                                            c.currency_code === selectedCrypto
                                        );
                                        const nets = coin?.network || [];
                                        return nets.length === 1 &&
                                          nets[0] === "NATIVE"
                                          ? "—"
                                          : "Select network";
                                      })()}
                                    </span>
                                  )}
                                </div>
                                {cryptoAmtInputError ? (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: "#ef4444",
                                      fontWeight: 500,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                    }}
                                  >
                                    <i
                                      className="fa fa-exclamation-circle"
                                      style={{ fontSize: 11 }}
                                    />
                                    {cryptoAmtInputError}
                                  </span>
                                ) : (
                                  minCryptoAmt &&
                                  cryptoConvertedAmt !== null &&
                                  parseFloat(cryptoConvertedAmt) >
                                    parseFloat(minCryptoAmt) && (
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: "#6b7280",
                                      }}
                                    >
                                      Required: {minCryptoAmt} {selectedCrypto}
                                    </span>
                                  )
                                )}
                              </div>
                            </>
                          )}
                        </div>
                        <p className="co-total-note">
                          {cur.code} — amounts shown in your local currency
                          based on IP detection.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showLoginIframe && (
        <div className="co-modal-overlay" onClick={closeLoginIframe}>
          <div
            style={{
              position: "relative",
              width: "90vw",
              maxWidth: 1100,
              height: "85vh",
              background: "#fff",
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 20px",
                borderBottom: "1px solid #e5e7eb",
                background: "#f9fafb",
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 15, color: "#1f2937" }}>
                <i className="fa fa-lock" style={{ marginRight: 8 }} />
                Sign in to {brandName}
              </span>
              <button
                onClick={closeLoginIframe}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 22,
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "4px 8px",
                  borderRadius: 6,
                  lineHeight: 1,
                }}
                title="Close"
              >
                &times;
              </button>
            </div>
            <iframe
              src={loginIframeUrl}
              title={`Sign in to ${brandName}`}
              style={{
                width: "100%",
                height: "calc(100% - 49px)",
                border: "none",
              }}
            />
          </div>
        </div>
      )}

      {/* ── Beautiful Sign In Modal (brandWalletAvaliability === "1") ── */}
      {showLoginModal && !showOtpModal && (
        <div
          className="co-modal-overlay"
          onClick={closeLoginModal}
          style={{
            backdropFilter: "blur(4px)",
            background: "rgba(15,23,42,0.55)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#fff",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
              animation: "fadeInUp .22s ease",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",
                padding: "28px 28px 20px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 14,
                }}
              >
                <i
                  className="fa fa-lock"
                  style={{ fontSize: 20, color: "#fff" }}
                />
              </div>
              <h2
                style={{
                  margin: 0,
                  color: "#fff",
                  fontSize: 20,
                  fontWeight: 700,
                  letterSpacing: "-0.3px",
                }}
              >
                Welcome back
              </h2>
              <p
                style={{
                  margin: "4px 0 0",
                  color: "rgba(255,255,255,0.8)",
                  fontSize: 13,
                }}
              >
                Sign in to your {brandName} account
              </p>
              <button
                onClick={closeLoginModal}
                style={{
                  position: "absolute",
                  top: 16,
                  right: 16,
                  background: "rgba(255,255,255,0.18)",
                  border: "none",
                  borderRadius: 8,
                  width: 30,
                  height: 30,
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: 18,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                &times;
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "24px 28px 28px" }}>
              <form onSubmit={handleLoginSubmit}>
                {/* Email */}
                <div style={{ marginBottom: 18 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: 6,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Email Address
                  </label>
                  <div style={{ position: "relative" }}>
                    <i
                      className="fa fa-envelope"
                      style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9ca3af",
                        fontSize: 14,
                      }}
                    />
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      style={{
                        width: "100%",
                        padding: "11px 14px 11px 36px",
                        border: "1.5px solid #e5e7eb",
                        borderRadius: 10,
                        fontSize: 14,
                        outline: "none",
                        boxSizing: "border-box",
                        transition: "border-color .15s",
                        background: "#f9fafb",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                  </div>
                </div>

                {/* Password */}
                <div style={{ marginBottom: 22, position: "relative" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#374151",
                      marginBottom: 6,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Password
                  </label>
                  <div style={{ position: "relative" }}>
                    <i
                      className="fa fa-key"
                      style={{
                        position: "absolute",
                        left: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color: "#9ca3af",
                        fontSize: 14,
                      }}
                    />
                    <input
                      type={loginPasswordVisible ? "text" : "password"}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      style={{
                        width: "100%",
                        padding: "11px 42px 11px 36px",
                        border: "1.5px solid #e5e7eb",
                        borderRadius: 10,
                        fontSize: 14,
                        outline: "none",
                        boxSizing: "border-box",
                        background: "#f9fafb",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                      onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                    />
                    <span
                      onClick={() =>
                        setLoginPasswordVisible(!loginPasswordVisible)
                      }
                      style={{
                        position: "absolute",
                        right: 12,
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#9ca3af",
                        fontSize: 15,
                      }}
                    >
                      <i
                        className={`fa ${
                          loginPasswordVisible ? "fa-eye" : "fa-eye-slash"
                        }`}
                      />
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loginLoading}
                  style={{
                    width: "100%",
                    padding: "13px 0",
                    background: loginLoading
                      ? "#93c5fd"
                      : "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: loginLoading ? "not-allowed" : "pointer",
                    letterSpacing: "0.2px",
                    boxShadow: loginLoading
                      ? "none"
                      : "0 4px 14px rgba(59,130,246,0.4)",
                  }}
                >
                  {loginLoading ? (
                    <>
                      <span
                        className="co-spinner"
                        style={{
                          width: 14,
                          height: 14,
                          marginRight: 8,
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      />
                      Signing in…
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Beautiful Sign Up Modal (brandWalletAvaliability === "0") ── */}
      {showSignupModal && !showRegOtpModal && (
        <div
          className="co-modal-overlay"
          onClick={closeSignupModal}
          style={{
            backdropFilter: "blur(4px)",
            background: "rgba(15,23,42,0.55)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 580,
              background: "#fff",
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                background:
                  "linear-gradient(135deg,#0f4c81 0%,#1d6fa4 50%,#38b2d4 100%)",
                padding: "24px 28px 20px",
                position: "relative",
                flexShrink: 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(255,255,255,0.18)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="fa fa-user-plus"
                    style={{ fontSize: 18, color: "#fff" }}
                  />
                </div>
                <div>
                  <h2
                    style={{
                      margin: 0,
                      color: "#fff",
                      fontSize: 19,
                      fontWeight: 700,
                      letterSpacing: "-0.3px",
                    }}
                  >
                    Create your {brandName} account
                  </h2>
                  <p
                    style={{
                      margin: "3px 0 0",
                      color: "rgba(255,255,255,0.78)",
                      fontSize: 12.5,
                    }}
                  >
                    Fill in your details to get started
                  </p>
                </div>
              </div>
              <button
                onClick={closeSignupModal}
                style={{
                  position: "absolute",
                  top: 14,
                  right: 16,
                  background: "rgba(255,255,255,0.18)",
                  border: "none",
                  borderRadius: 8,
                  width: 30,
                  height: 30,
                  cursor: "pointer",
                  color: "#fff",
                  fontSize: 18,
                  lineHeight: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                &times;
              </button>
            </div>

            {/* Body — scrollable */}
            <div
              style={{
                padding: "22px 28px 28px",
                overflowY: "auto",
                flex: 1,
              }}
            >
              <form onSubmit={handleSignupFormSubmit} noValidate>
                {/* Gender + Name row */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "80px 1fr 1fr 1fr",
                    gap: 8,
                    marginBottom: 14,
                  }}
                >
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      Title
                    </label>
                    <select
                      value={signupGender}
                      onChange={(e) => setSignupGender(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "9px 6px",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        fontSize: 13,
                        background: "#fff",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    >
                      <option value="Male">Mr.</option>
                      <option value="Female">Mrs.</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={signupFirstName}
                      onChange={(e) =>
                        setSignupFirstName(
                          e.target.value.replace(/[^A-Za-z ]/g, "")
                        )
                      }
                      placeholder="First"
                      style={{
                        width: "100%",
                        padding: "9px 10px",
                        border: `1px solid ${
                          signupErrors.firstName ? "#ef4444" : "#d1d5db"
                        }`,
                        borderRadius: 8,
                        fontSize: 13,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {signupErrors.firstName && (
                      <p
                        style={{
                          fontSize: 11,
                          color: "#ef4444",
                          margin: "3px 0 0",
                        }}
                      >
                        {signupErrors.firstName}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      Middle
                    </label>
                    <input
                      type="text"
                      value={signupMiddleName}
                      onChange={(e) =>
                        setSignupMiddleName(
                          e.target.value.replace(/[^A-Za-z ]/g, "")
                        )
                      }
                      placeholder="Middle"
                      style={{
                        width: "100%",
                        padding: "9px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        fontSize: 13,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#374151",
                        marginBottom: 4,
                      }}
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={signupLastName}
                      onChange={(e) =>
                        setSignupLastName(
                          e.target.value.replace(/[^A-Za-z ]/g, "")
                        )
                      }
                      placeholder="Last"
                      style={{
                        width: "100%",
                        padding: "9px 10px",
                        border: `1px solid ${
                          signupErrors.lastName ? "#ef4444" : "#d1d5db"
                        }`,
                        borderRadius: 8,
                        fontSize: 13,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                    {signupErrors.lastName && (
                      <p
                        style={{
                          fontSize: 11,
                          color: "#ef4444",
                          margin: "3px 0 0",
                        }}
                      >
                        {signupErrors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 4,
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    onBlur={(e) => checkEmail(e.target.value)}
                    placeholder="Enter your email"
                    autoComplete="off"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: `1px solid ${
                        signupErrors.email ? "#ef4444" : "#d1d5db"
                      }`,
                      borderRadius: 8,
                      fontSize: 14,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {signupErrors.email && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "#ef4444",
                        margin: "3px 0 0",
                      }}
                    >
                      {signupErrors.email}
                    </p>
                  )}
                </div>

                {/* Phone with country picker */}
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 4,
                    }}
                  >
                    Phone *
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    {/* Country flag button */}
                    <div style={{ position: "relative" }}>
                      <button
                        type="button"
                        onClick={() => setSignupCountryOpen(!signupCountryOpen)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "10px 10px",
                          border: `1px solid ${
                            signupErrors.countryCode ? "#ef4444" : "#d1d5db"
                          }`,
                          borderRadius: 8,
                          background: "#fff",
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          fontSize: 13,
                        }}
                      >
                        {signupSelectedCountry ? (
                          <>
                            <img
                              src={signupSelectedCountry.flag}
                              alt=""
                              style={{ width: 20, height: 14 }}
                            />
                            <span>+{signupSelectedCountry.value}</span>
                          </>
                        ) : (
                          <span style={{ color: "#9ca3af" }}>Code</span>
                        )}
                        <i
                          className="fa fa-chevron-down"
                          style={{ fontSize: 10, color: "#6b7280" }}
                        />
                      </button>
                      {signupCountryOpen && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            width: 280,
                            border: "1px solid #d1d5db",
                            borderRadius: 8,
                            background: "#fff",
                            zIndex: 2000,
                            maxHeight: 200,
                            overflowY: "auto",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Search country…"
                            value={signupCountrySearch}
                            onChange={(e) =>
                              handleSignupCountrySearch(e.target.value)
                            }
                            style={{
                              width: "100%",
                              padding: "8px 12px",
                              border: "none",
                              borderBottom: "1px solid #e5e7eb",
                              outline: "none",
                              fontSize: 13,
                              boxSizing: "border-box",
                            }}
                          />
                          {signupFilteredCountries.map((c, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                setSignupSelectedCountry(c);
                                setSignupCountryOpen(false);
                                setSignupCountrySearch("");
                                setSignupFilteredCountries(signupCountryList);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "8px 12px",
                                cursor: "pointer",
                                fontSize: 13,
                                background:
                                  signupSelectedCountry?.value === c.value
                                    ? "#f0f4ff"
                                    : "#fff",
                              }}
                            >
                              <img
                                src={c.flag}
                                alt=""
                                style={{ width: 20, height: 14 }}
                              />
                              <span style={{ color: "#555" }}>+{c.value}</span>
                              <span style={{ color: "#333" }}>{c.country}</span>
                            </div>
                          ))}
                          {signupFilteredCountries.length === 0 && (
                            <div
                              style={{
                                padding: "10px 12px",
                                fontSize: 13,
                                color: "#9ca3af",
                              }}
                            >
                              No results
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={signupPhone}
                      onChange={(e) =>
                        setSignupPhone(
                          e.target.value.replace(/\D/g, "").slice(0, 15)
                        )
                      }
                      onBlur={(e) => validatePhoneNumber(e.target.value)}
                      placeholder="Phone number"
                      autoComplete="off"
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        border: `1px solid ${
                          signupErrors.phone ? "#ef4444" : "#d1d5db"
                        }`,
                        borderRadius: 8,
                        fontSize: 14,
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  {(signupErrors.phone || signupErrors.countryCode) && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "#ef4444",
                        margin: "3px 0 0",
                      }}
                    >
                      {signupErrors.phone || signupErrors.countryCode}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div
                  style={{
                    marginBottom: 14,
                    position: "relative",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 4,
                    }}
                  >
                    Password *
                  </label>
                  <input
                    type={signupPasswordVisible ? "text" : "password"}
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    style={{
                      width: "100%",
                      padding: "10px 40px 10px 14px",
                      border: `1px solid ${
                        signupErrors.password ? "#ef4444" : "#d1d5db"
                      }`,
                      borderRadius: 8,
                      fontSize: 14,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <span
                    onClick={() =>
                      setSignupPasswordVisible(!signupPasswordVisible)
                    }
                    style={{
                      position: "absolute",
                      right: 12,
                      top: 34,
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: 16,
                    }}
                  >
                    <i
                      className={`fa ${
                        signupPasswordVisible ? "fa-eye" : "fa-eye-slash"
                      }`}
                    />
                  </span>
                  {signupErrors.password && (
                    <p
                      style={{
                        fontSize: 11,
                        color: "#ef4444",
                        margin: "3px 0 0",
                      }}
                    >
                      {signupErrors.password}
                    </p>
                  )}
                </div>

                {/* Retype Password */}
                <div style={{ marginBottom: 18, position: "relative" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 4,
                    }}
                  >
                    Retype Password *
                  </label>
                  <input
                    type={signupRetypePasswordVisible ? "text" : "password"}
                    value={signupRetypePassword}
                    onChange={(e) => setSignupRetypePassword(e.target.value)}
                    placeholder="Retype your password"
                    autoComplete="new-password"
                    style={{
                      width: "100%",
                      padding: "10px 40px 10px 14px",
                      border: `1px solid ${
                        signupErrors.retypePassword
                          ? "#ef4444"
                          : signupRetypePassword &&
                            signupPassword === signupRetypePassword
                          ? "#10b981"
                          : "#d1d5db"
                      }`,
                      borderRadius: 8,
                      fontSize: 14,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <span
                    onClick={() =>
                      setSignupRetypePasswordVisible(
                        !signupRetypePasswordVisible
                      )
                    }
                    style={{
                      position: "absolute",
                      right: 12,
                      top: 34,
                      cursor: "pointer",
                      color: "#6b7280",
                      fontSize: 16,
                    }}
                  >
                    <i
                      className={`fa ${
                        signupRetypePasswordVisible ? "fa-eye" : "fa-eye-slash"
                      }`}
                    />
                  </span>
                  {signupErrors.retypePassword ? (
                    <p
                      style={{
                        fontSize: 11,
                        color: "#ef4444",
                        margin: "3px 0 0",
                      }}
                    >
                      {signupErrors.retypePassword}
                    </p>
                  ) : (
                    signupRetypePassword &&
                    signupPassword === signupRetypePassword && (
                      <p
                        style={{
                          fontSize: 11,
                          color: "#10b981",
                          margin: "3px 0 0",
                        }}
                      >
                        Passwords match
                      </p>
                    )
                  )}
                </div>

                <button
                  type="submit"
                  disabled={signupLoading}
                  style={{
                    width: "100%",
                    padding: "13px 0",
                    background: signupLoading
                      ? "#7dd3fc"
                      : "linear-gradient(135deg,#0f4c81 0%,#1d6fa4 50%,#38b2d4 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 10,
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: signupLoading ? "not-allowed" : "pointer",
                    letterSpacing: "0.2px",
                    boxShadow: signupLoading
                      ? "none"
                      : "0 4px 14px rgba(29,111,164,0.4)",
                    marginTop: 4,
                  }}
                >
                  {signupLoading ? "Creating Account…" : "Create Account"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <PaybitoCaptchaExchange ref={captchaRef} />
      <PaybitoCaptchaExchange ref={signupCaptchaRef} />

      {/* ── PabtekPaymentPopup – Buy with Card ── */}
      <PabtekPaymentPopup
        open={showPabtekPopup}
        onClose={handleMoonPayPopupClose}
        currencyCode={pabtekCurrencyCode}
        currencyId={pabtekCurrencyId}
        userUuid={paybitoUuid}
        selectedNetwork={pabtekSelectedNetwork}
        requiredAmount={pabtekRequiredAmount}
        paymentIntentId={paymentIntentId}
        onPaymentLinkOpened={handlePabtekPaymentLinkOpened}
        pollingError={pabtekPollingError}
        directPollingMode={pabtekDirectPolling}
      />

      {/* Registration OTP modal (after signup) */}
      {showRegOtpModal && (
        <div className="co-modal-overlay" onClick={closeRegOtpModal}>
          <div
            className="co-modal-box"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="co-modal-header">
              <h3>Verify Your Account</h3>
              <button
                className="co-modal-close"
                onClick={closeRegOtpModal}
                title="Close"
              >
                &times;
              </button>
            </div>
            <div className="co-modal-body">
              {regShowPhoneOtp && (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 6,
                    }}
                  >
                    Phone OTP
                  </label>
                  <p
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    Enter the code sent to your registered phone number.
                  </p>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={regPhoneOtp}
                    onChange={(e) =>
                      setRegPhoneOtp(
                        e.target.value.replace(/\D/g, "").slice(0, 8)
                      )
                    }
                    placeholder="8-digit code"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 14,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#374151",
                    marginBottom: 6,
                  }}
                >
                  Email OTP
                </label>
                <p
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 8,
                  }}
                >
                  Enter the code sent to {maskEmail(signupEmail)}.
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={regEmailOtp}
                  onChange={(e) =>
                    setRegEmailOtp(
                      e.target.value.replace(/\D/g, "").slice(0, 8)
                    )
                  }
                  placeholder="8-digit code"
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    fontSize: 14,
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div
                style={{
                  textAlign: "center",
                  marginBottom: 16,
                  fontSize: 13,
                }}
              >
                {regOtpCountdown > 0 ? (
                  <span style={{ color: "#6b7280" }}>
                    Resend in {regOtpCountdown}s
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={resendRegOtp}
                    style={{
                      background: "none",
                      border: "none",
                      color: "var(--co-primary,#2563EB)",
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: 13,
                    }}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleRegOtpSubmit}
                disabled={regOtpLoading || regEmailOtp.length !== 8}
                style={{
                  width: "100%",
                  padding: "12px 0",
                  background: "var(--co-primary,#2563EB)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor:
                    regOtpLoading || regEmailOtp.length !== 8
                      ? "not-allowed"
                      : "pointer",
                  opacity: regOtpLoading || regEmailOtp.length !== 8 ? 0.6 : 1,
                }}
              >
                {regOtpLoading ? "Verifying…" : "Verify & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div className="co-modal-overlay" onClick={closeOtpModal}>
          <div
            className="co-modal-box"
            style={{ maxWidth: 420 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="co-modal-header">
              <h3>Security Authentication</h3>
              <button
                className="co-modal-close"
                onClick={closeOtpModal}
                title="Close"
              >
                &times;
              </button>
            </div>
            <div className="co-modal-body">
              <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                Please enter the code sent to {maskEmail(loginEmail)}
              </p>
              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#374151",
                    marginBottom: 6,
                  }}
                >
                  E-mail verification code
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={emailOtp}
                    onChange={(e) =>
                      setEmailOtp(e.target.value.replace(/\D/g, "").slice(0, 8))
                    }
                    placeholder="8-digit code"
                    style={{
                      flex: 1,
                      padding: "10px 14px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 14,
                      outline: "none",
                    }}
                  />
                  {otpCountdown === 0 && (
                    <button
                      type="button"
                      onClick={sendLoginEmailOtp}
                      style={{
                        padding: "10px 16px",
                        background: "var(--co-primary,#2563EB)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        fontSize: 13,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Get code
                    </button>
                  )}
                </div>
                {otpCountdown > 0 && (
                  <p style={{ fontSize: 12, color: "#10b981", marginTop: 4 }}>
                    Resend in {otpCountdown}s
                  </p>
                )}
              </div>
              {isPhoneVerificationEnable && (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 6,
                    }}
                  >
                    Phone OTP
                  </label>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={phoneOtp}
                      onChange={(e) =>
                        setPhoneOtp(
                          e.target.value.replace(/\D/g, "").slice(0, 8)
                        )
                      }
                      placeholder="8-digit code"
                      style={{
                        flex: 1,
                        padding: "10px 14px",
                        border: "1px solid #d1d5db",
                        borderRadius: 8,
                        fontSize: 14,
                        outline: "none",
                      }}
                    />
                    {smsOtpCountdown === 0 && (
                      <button
                        type="button"
                        onClick={() =>
                          sendLoginSmsOtp(loginUserPhone, loginUserCountryCode)
                        }
                        style={{
                          padding: "10px 16px",
                          background: "var(--co-primary,#2563EB)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 8,
                          fontSize: 13,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Get code
                      </button>
                    )}
                  </div>
                  {smsOtpCountdown > 0 && (
                    <p style={{ fontSize: 12, color: "#10b981", marginTop: 4 }}>
                      Resend in {smsOtpCountdown}s
                    </p>
                  )}
                </div>
              )}
              {isGoogleAuthEnable && (
                <div style={{ marginBottom: 14 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#374151",
                      marginBottom: 6,
                    }}
                  >
                    Google Authenticator code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={gaOtp}
                    onChange={(e) =>
                      setGaOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="6-digit code"
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      fontSize: 14,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              )}
              <button
                type="button"
                onClick={() => handleOtpLogin()}
                disabled={loginLoading || !emailOtp || emailOtp.length < 8}
                style={{
                  width: "100%",
                  padding: "12px 0",
                  marginTop: 8,
                  background: "var(--co-primary,#2563EB)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 600,
                  cursor:
                    loginLoading || !emailOtp || emailOtp.length < 8
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    loginLoading || !emailOtp || emailOtp.length < 8 ? 0.6 : 1,
                }}
              >
                {loginLoading ? "Verifying…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receive QR Modal  showReceiveQR*/}
      {showReceiveQR && (
        <div className="co-modal-overlay" onClick={handleCloseReceiveQR}>
          <div
            className="co-modal-box"
            style={{ maxWidth: 480 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="co-modal-header">
              <h3>
                <i className="fa fa-qrcode" style={{ marginRight: 8 }} />
                Receive code for {selectedCrypto || currency || "BTC"}
                {selectedNetwork && selectedNetwork !== "NATIVE"
                  ? ` ${selectedNetwork.toUpperCase()}`
                  : ""}
              </h3>
              <button
                className="co-modal-close"
                onClick={handleCloseReceiveQR}
                title="Close"
              >
                &times;
              </button>
            </div>
            <div
              className="co-modal-body"
              style={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              {depositLoading ? (
                <div style={{ padding: "30px 0" }}>
                  <span
                    className="co-spinner"
                    style={{ width: 28, height: 28 }}
                  />
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--co-gray-500)",
                      marginTop: 10,
                    }}
                  >
                    Generating deposit address…
                  </p>
                </div>
              ) : depositAddress ? (
                <>
                  {/* Tag/Memo if present */}
                  {depositTag && (
                    <div style={{ marginBottom: 16 }}>
                      <p
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "var(--co-gray-500)",
                          textTransform: "uppercase",
                          letterSpacing: ".4px",
                          marginBottom: 6,
                        }}
                      >
                        {(selectedCrypto || "").toUpperCase()} Tag / Memo
                      </p>
                      <div
                        style={{
                          background: "var(--co-gray-100,#f3f4f6)",
                          borderRadius: 8,
                          padding: "10px 14px",
                          fontSize: 13,
                          wordBreak: "break-all",
                          color: "var(--co-gray-700)",
                          fontFamily: "monospace",
                          marginBottom: 8,
                        }}
                      >
                        {depositTag}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(depositTag);
                          showToast("success", "Tag copied!");
                        }}
                        style={{
                          background: "none",
                          border: "1px solid var(--co-gray-200)",
                          borderRadius: 6,
                          padding: "5px 12px",
                          fontSize: 11,
                          cursor: "pointer",
                          color: "var(--co-gray-600)",
                        }}
                      >
                        <i className="fa fa-copy" />
                        &nbsp;Copy Tag
                      </button>
                    </div>
                  )}

                  {/* Deposit address */}
                  <p
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--co-gray-500)",
                      textTransform: "uppercase",
                      letterSpacing: ".4px",
                      marginBottom: 6,
                    }}
                  >
                    Deposit Address
                  </p>
                  <div
                    style={{
                      background: "var(--co-gray-100,#f3f4f6)",
                      borderRadius: 8,
                      padding: "10px 14px",
                      fontSize: 12,
                      wordBreak: "break-all",
                      color: "var(--co-gray-700)",
                      fontFamily: "monospace",
                      marginBottom: 8,
                    }}
                  >
                    {depositAddress}
                  </div>

                  {/* QR Code */}
                  <div
                    style={{
                      background: "#fff",
                      display: "inline-block",
                      padding: 16,
                      borderRadius: 12,
                      border: "1px solid var(--co-gray-200,#e2e6ef)",
                      marginBottom: 16,
                    }}
                  >
                    <QRCodeSVG value={depositAddress} size={200} />
                  </div>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(depositAddress);
                      showToast("success", "Address copied!");
                    }}
                    style={{
                      background: "none",
                      border: "1px solid var(--co-gray-200)",
                      borderRadius: 6,
                      padding: "6px 14px",
                      fontSize: 12,
                      cursor: "pointer",
                      color: "var(--co-gray-600)",
                      marginBottom: 14,
                    }}
                  >
                    <i className="fa fa-copy" />
                    &nbsp;Copy Address
                  </button>
                </>
              ) : (
                <p
                  style={{ fontSize: 13, color: "#ef4444", padding: "20px 0" }}
                >
                  Unable to fetch deposit address. Please try again.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EVM Wallet Picker — shown when multiple EIP-6963 wallets are detected */}
      {showEVMWalletPicker && (
        <div
          className="co-modal-overlay"
          onClick={() => setShowEVMWalletPicker(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: 20,
              width: "90vw",
              maxWidth: 380,
              overflow: "hidden",
              boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
              border: "1px solid rgba(0,0,0,0.07)",
              fontFamily: "Roboto, sans-serif",
            }}
          >
            {/* Header */}
            <div style={{ padding: "22px 22px 0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 11,
                      background: "rgba(37,99,235,0.10)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <i
                      className="fa fa-credit-card"
                      style={{ color: "#2563EB", fontSize: 17 }}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#111827 !important",
                        lineHeight: 1.2,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      Connect wallet
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        marginTop: 2,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      {NETWORK_MODE === "MAINNET"
                        ? "Ethereum Mainnet"
                        : "Sepolia Testnet"}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowEVMWalletPicker(false)}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    border: "1px solid #e5e7eb",
                    background: "#2563EB",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: "#fff",
                    lineHeight: 1,
                  }}
                >
                  &times;
                </button>
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "#9ca3af",
                  margin: "12px 0 16px",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                Choose a wallet to complete this payment
              </p>
              <div style={{ height: 1, background: "#f3f4f6" }} />
            </div>

            {/* Wallet list */}
            <div
              style={{
                padding: "14px 22px 22px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {detectedEVMWallets
                .filter((wallet) =>
                  paymentType === "trc20" || selectedNetwork === "TRC"
                    ? true
                    : !wallet.info.name.toLowerCase().includes("tron")
                )
                .map((wallet) => (
                <div
                  key={wallet.info.uuid}
                  onClick={() =>
                    connectSelectedEVMWallet(wallet.provider, wallet.info.name)
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    padding: "13px 16px",
                    borderRadius: 14,
                    border: "1.5px solid #2563EB",
                    background: "#ffffff",
                    cursor: "pointer",
                    width: "100%",
                    textAlign: "left",
                    transition: "all .18s",
                    boxSizing: "border-box",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(37,99,235,0.05)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "#ffffff";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {wallet.info.icon ? (
                    <img
                      src={wallet.info.icon}
                      alt={wallet.info.name}
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 12,
                        flexShrink: 0,
                        border: "1px solid #f3f4f6",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: 12,
                        background: "rgba(37,99,235,0.10)",
                        border: "1px solid rgba(37,99,235,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className="fa fa-credit-card"
                        style={{ color: "#2563EB", fontSize: 18 }}
                      />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#111827",
                        fontFamily: "Roboto, sans-serif",
                        lineHeight: 1.3,
                      }}
                    >
                      {wallet.info.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        marginTop: 3,
                        fontFamily: "Roboto, sans-serif",
                      }}
                    >
                      Browser extension detected
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        background: "#22c55e",
                      }}
                    />
                    <i
                      className="fa fa-chevron-right"
                      style={{ fontSize: 11, color: "#9ca3af" }}
                    />
                  </div>
                </div>
              ))}

              {/* Info note */}
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 8,
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: "rgba(37,99,235,0.06)",
                  border: "1px solid rgba(37,99,235,0.12)",
                  marginTop: 4,
                }}
              >
                <i
                  className="fa fa-info-circle"
                  style={{
                    color: "#2563EB",
                    fontSize: 13,
                    marginTop: 1,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: "#374151",
                    lineHeight: 1.5,
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  Only wallets on{" "}
                  {NETWORK_MODE === "MAINNET"
                    ? "Ethereum Mainnet"
                    : "Sepolia Testnet"}{" "}
                  are supported for this payment.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {stripeResultModal && (
        <div
          className="co-modal-overlay"
          onClick={() => setStripeResultModal(null)}
        >
          <div
            className="co-modal-box"
            style={{ maxWidth: 440 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="co-modal-header">
              <h3>
                {stripeResultModal.type === "success"
                  ? "Payment Confirmation"
                  : "Payment Error"}
              </h3>
              <button
                className="co-modal-close"
                onClick={() => setStripeResultModal(null)}
                title="Close"
              >
                &times;
              </button>
            </div>
            <div className="co-modal-body">
              <div className={`co-result-icon ${stripeResultModal.type}`}>
                <i
                  className={`fa ${
                    stripeResultModal.type === "success"
                      ? "fa-check"
                      : "fa-times"
                  }`}
                />
              </div>
              <h3 className="co-paypal-result-title">
                {stripeResultModal.title}
              </h3>
              <p className="co-paypal-result-msg">
                {stripeResultModal.message}
              </p>
              {stripeResultModal.details && (
                <div style={{ marginBottom: 20 }}>
                  {stripeResultModal.details.paymentId && (
                    <div className="co-paypal-detail-row">
                      <span className="co-paypal-detail-label">Payment ID</span>
                      <span className="co-paypal-detail-value">
                        {stripeResultModal.details.paymentId}
                      </span>
                    </div>
                  )}
                  {stripeResultModal.details.amount && (
                    <div className="co-paypal-detail-row">
                      <span className="co-paypal-detail-label">Amount</span>
                      <span className="co-paypal-detail-value">
                        {stripeResultModal.details.amount}{" "}
                        {stripeResultModal.details.currency}
                      </span>
                    </div>
                  )}
                </div>
              )}
              <button
                className={`co-btn ${
                  stripeResultModal.type === "success"
                    ? "co-btn-green"
                    : "co-btn-stripe"
                }`}
                onClick={() => setStripeResultModal(null)}
              >
                {stripeResultModal.type === "success" ? "Done" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const ExternalWalletOption = ({
  selectedOpt,
  connectExternalWallet,
  externalConnecting,
  isTron,
  connectedWallet,
  walletLabel,
  walletDesc,
  proceedBtnClass,
  proceedWithExternalWallet,
  txConfirmed,
  txStatus,
  txHash,
  disconnectWallet,
}) => (
  <div
    className={`co-option ${selectedOpt === "external" ? "selected" : ""} ${
      externalConnecting ? "is-loading" : ""
    }`}
  >
    <div className="co-option-header" onClick={connectExternalWallet}>
      <div className="co-option-icon green">
        {externalConnecting ? (
          <span
            className="co-spinner"
            style={{
              borderTopColor: "var(--co-success)",
              borderColor: "rgba(16,185,129,0.3)",
            }}
          />
        ) : (
          <i className={isTron ? "fa fa-bolt" : "fa fa-qrcode"} />
        )}
      </div>
      <div className="co-option-text">
        <div className="co-option-title">{walletLabel}</div>
        <div className="co-option-desc">
          {connectedWallet
            ? `Connected: ${connectedWallet.slice(
                0,
                6
              )}…${connectedWallet.slice(-4)}`
            : walletDesc}
        </div>
      </div>
      {connectedWallet ? (
        <span className="co-badge fast">Connected</span>
      ) : (
        <span className="co-badge secure">Secure</span>
      )}
    </div>
    {connectedWallet && (
      <div
        style={{
          padding: "0 18px 18px",
          borderTop: "1px dashed var(--co-gray-200)",
          paddingTop: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "rgba(16,185,129,0.06)",
            border: "1px solid rgba(16,185,129,0.2)",
            borderRadius: 10,
            padding: "10px 14px",
            marginBottom: 14,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: "var(--co-gray-500)",
              }}
            >
              Connected
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--co-gray-700)",
                wordBreak: "break-all",
              }}
            >
              {connectedWallet.slice(0, 6)}...{connectedWallet.slice(-4)}
            </div>
          </div>
          <button
            onClick={disconnectWallet}
            style={{
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.3)",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 600,
              color: "#ef4444",
              cursor: "pointer",
            }}
          >
            Disconnect
          </button>
        </div>

        <button
          className={proceedBtnClass}
          onClick={proceedWithExternalWallet}
          disabled={externalConnecting}
          style={{
            opacity: externalConnecting ? 0.7 : 1,
            cursor: externalConnecting ? "not-allowed" : "pointer",
          }}
        >
          {externalConnecting ? (
            <>
              <span className="co-spinner" />
              &nbsp;Processing…
            </>
          ) : (
            "Proceed to Payment"
          )}
        </button>
      </div>
    )}
  </div>
);

const GuestCheckoutOption = ({
  selectedOpt,
  rawDataParam,
  quantity,
  hostedCatalogId,
  emailBillId,
  createPaymentIntent,
  appliedDiscount,
  selectedCrypto,
  selectedNetwork,
  availableCryptos,
  grandTotal,
  cryptoConvertedAmt,
  cryptoMarketPrice,
}) => (
  <div className={`co-option ${selectedOpt === "guest" ? "selected" : ""}`}>
    <div
      className="co-option-header"
      onClick={async () => {
        if (!rawDataParam) return;

        // Validate crypto selection
        if (!selectedCrypto) {
          showToast(
            "error",
            "Please select a cryptocurrency before proceeding."
          );
          return;
        }

        // Validate network if needed
        const selectedCoin = availableCryptos.find(
          (c) => c.currency_code === selectedCrypto
        );
        const needsNetwork =
          selectedCoin?.network?.length > 1 ||
          (selectedCoin?.network?.length === 1 &&
            selectedCoin.network[0] !== "NATIVE");
        if (needsNetwork && !selectedNetwork) {
          showToast(
            "error",
            "Please select a network for the chosen cryptocurrency."
          );
          return;
        }

        // Show loading state on the button
        const guestButton = document.activeElement;
        if (guestButton) guestButton.style.opacity = "0.7";

        try {
          // Prepare crypto details for createPaymentIntent
          const cryptoDetails = {
            amount: cryptoConvertedAmt || grandTotal,
            currency: selectedCrypto,
            marketPrice: cryptoMarketPrice,
          };

          // Call createPaymentIntent for guest checkout (pmId = 5)
          const result = await createPaymentIntent(5, cryptoDetails);

          console.log("RESULT => ", result);

          // Check for chargeId in the merged result
          if (!result || !result.chargeId) {
            showToast(
              "error",
              "Failed to initialize payment. Please try again."
            );
            if (guestButton) guestButton.style.opacity = "1";
            return;
          }

          // Encode the data for the invoice page
          let enrichedDataParam = rawDataParam;
          try {
            const decoded = JSON.parse(atob(rawDataParam.replace(/ /g, "+")));
            const obj = Array.isArray(decoded) ? decoded[0] : decoded;

            // Add crypto and network info
            if (selectedCrypto) obj.crypto = selectedCrypto;
            if (selectedCrypto && selectedNetwork)
              obj.network = selectedNetwork;
            if (grandTotal) obj.amount = String(grandTotal);

            // IMPORTANT: Add the createCharge response data to the URL params
            obj.chargeId = result.chargeId;
            obj.receivingAddress = result.receivingAddress;
            obj.maxRetry = result.maxRetry;
            obj.retryCount = result.retryCount;
            obj.paymentIntentId = result.paymentIntentId;

            enrichedDataParam = btoa(
              JSON.stringify(Array.isArray(decoded) ? [obj] : obj)
            );
          } catch (error) {
            console.error("Failed to encode data:", error);
          }

          // Redirect to invoice page with the enriched data
          const params = new URLSearchParams({ data: enrichedDataParam });
          window.location.href = `${
            window.location.origin
          }/merchant/guest-checkout/invoice?${params.toString()}`;
        } catch (error) {
          console.error("Guest checkout initialization failed:", error);
          showToast(
            "error",
            "Payment initialization failed. Please try again."
          );
          if (guestButton) guestButton.style.opacity = "1";
        }
      }}
    >
      
      <div className="co-option-icon purple">
        <i className="fa fa-user" />
      </div>
      <div className="co-option-text">
        <div className="co-option-title">Guest Checkout</div>
        <div className="co-option-desc">
          Pay as a guest by scanning the QR code with any crypto wallet.
        </div>
      </div>
      <span className="co-badge no-login">No Login</span>
    </div>
  </div>
);
export default CheckoutPaymentOptions;
